import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import './CollectionModal.css';
import customFetch from './apicall/api';
import { generateId } from './Utility';
import moment from 'moment';
import toast, { Toaster } from 'react-hot-toast';
import { LocalisedAlert } from './LocalisedAlert.jsx';
import { PEDiv } from './permissionComponents';

/**
 * CollectionModal Component
 * 
 * Enhanced with comprehensive collection amount handling:
 * 
 * 1. Priority-based allocation: OD → CD → Charges → Next EMI → Prepayment
 * 2. Dynamic validation to prevent manual edits that break balance
 * 3. Smart reallocation when amounts are reduced/removed
 * 4. Pre-demand payment support (when CD = 0)
 * 5. Multi-mode payment support with payment history tracking
 * 6. Comprehensive logging for payment modes and UPI link generation
 * 
 * Key Features:
 * - Automatic allocation following strict priority
 * - Manual edit validation with helpful error messages
 * - Smart reallocation to next priority field when amounts are reduced
 * - Support for payments before demand generation
 * - Multi-mode payment tracking and remaining demand calculation
 * - Payment mode click logging and UPI link generation logging
 */


export default function CollectionModal({ items, onClose, api_token, onDataRefresh, isOpen }) {
  
  const history = useHistory();
  console.log("items in modal:", items);
  console.log("modal isOpen:", isOpen);

  // Get API token from props or items or fallback
  const apiToken = api_token || (items && items.api_token) || localStorage.getItem('api_token');

  // Extract values from items
  const overdueDemand = Math.max(0, parseFloat(items && items.tot_overdue || 0)); // Ensure OD is never negative
  const currentDemand = Math.max(0, parseFloat((items && items.Balance || 0) - (items && items.tot_overdue || 0))); // Ensure CD is never negative
  const chargeDue = Math.max(0, parseFloat(items && items.Charges_os || 0)); // Ensure charges are never negative
  const collectedAmount = parseFloat(items && items.Collected || 0);
  const standardEmi = parseFloat(items && items.emi || 0);
  const loanOutstanding = parseFloat(items && items.Loan_os || 0);
  const totalDemandValue = parseFloat(items && items.Demand || 0);
  
  // advancePaid represents total advance payments already made (includes Next EMI + Prepayment)
  // This is calculated as: Collected - Demand
  const advancePaid = Math.max(0, collectedAmount - totalDemandValue);
  
  // Calculate Next EMI: Show the minimum of three scenarios
  // Scenario 1: Loan Outstanding - Demand (remaining loan after current demand)
  const scenario1 =  standardEmi;

  // Scenario 2: Loan Outstanding - Demand (remaining loan after current demand)
  const scenario2 = Math.max(0, loanOutstanding - (totalDemandValue-collectedAmount));
  
  // Scenario 3: items.emi - (Collected - Demand) (remaining amount in next EMI after advance payment)
  const scenario3 = Math.max(0, standardEmi - advancePaid);
  
  // Show the MINIMUM value among all three scenarios (including 0)
  // We want the smallest value, even if it's 0
  // Round to 2 decimal places to avoid floating-point precision issues (e.g. 3299.9999... showing as 3299.99)
  const emiAmount = Math.max(0, parseFloat(Math.min(scenario1, scenario2, scenario3).toFixed(2))); // Ensure Next EMI is never negative
  
  console.log('💰 Next EMI Calculation:', {
    scenario1_LoanOS_minus_Demand: scenario1,
    scenario2_Collected_minus_Demand: scenario2,
    scenario3_EMI_minus_AdvancePaid: scenario3,
    standardEmi: standardEmi,
    finalEmiAmount: emiAmount,
    loanOutstanding,
    totalDemandValue,
    collectedAmount,
    advancePaid
  });
  
  console.log('💰 Demand Values Extracted:', {
    overdueDemand,
    currentDemand,
    chargeDue,
    emiAmount,
    rawEmi: items && items.emi,
    collected: items && items.Collected,
    demand: items && items.Demand,
    loanOS: items && items.Loan_os
  });
  
  // Calculate total demand as items.Demand + items.Charges_os (negative values treated as 0)
  const totalDemand = Math.max(0, parseFloat(items && items.Balance || 0)) + Math.max(0, parseFloat(items && items.Charges_os || 0));
  
  // System-configurable parameters
  const maxAdvanceEmis = 3; // Configurable - not currently used in new logic
  const foreclosureChargePercentage = 4; // NEW: Configurable - Foreclosure charges percentage (4%)
  
  // State for dynamic prepayment configuration from API (BRE)
  const [maxPrepaymentEmis, setMaxPrepaymentEmis] = useState(2); // Default to 2, will be fetched from API (BRE)
  
  // State for NACH bounce charges from BRE API
  const [nachBounceCharge, setNachBounceCharge] = useState(0); // Default to 0, will be fetched from API (BRE)
  
  // Calculate maximum prepayment allowed: Show the minimum of three scenarios
  // Scenario 1: standardEmi * BRE → Normal case (base capacity)
  const prepaymentScenario1 = standardEmi * maxPrepaymentEmis;
  
  // Scenario 2: Loan Outstanding - Total Demand - standardEmi → For handling cases which are at verge of closing
  const prepaymentScenario2 = Math.max(0, loanOutstanding - currentDemand - standardEmi);
  
  // Scenario 3: standardEmi * (BRE + 1) - advancePaid → This is the KEY scenario!
  // This accounts for ALL advance payments (Next EMI + Prepayment combined)
  // Example: standardEmi=10000, BRE=2, advancePaid=11000 (10000 Next EMI + 1000 Prepayment)
  // Result: 10000 * 3 - 11000 = 30000 - 11000 = 19000 ✓
  const prepaymentScenario3 = Math.max(0, standardEmi * (maxPrepaymentEmis + 1) - advancePaid);
  
  // Show the MINIMUM value among all three scenarios
  // Scenario 3 automatically gives us the correct remaining capacity!
  const maxPrepaymentAmount = Math.max(0, Math.min(prepaymentScenario1, prepaymentScenario2, prepaymentScenario3));
  
  console.log('💰 Prepayment Calculation:', {
    scenario1_standardEmi_times_BRE: prepaymentScenario1,
    scenario2_LoanOS_minus_Demand_minus_EMI: prepaymentScenario2,
    scenario3_EMI_times_BREplus1_minus_AdvancePaid: prepaymentScenario3,
    maxPrepaymentEmis_BRE: maxPrepaymentEmis,
    standardEmi,
    loanOutstanding,
    totalDemandValue,
    advancePaid_includesAllAdvancePayments: advancePaid,
    finalMaxPrepaymentAmount: maxPrepaymentAmount,
    explanation: 'Scenario3 handles everything: if you paid 11000 total advance, it shows (10000*3)-11000=19000'
  });

  // Try to find the correct loan outstanding field
  const possibleLoanFields = [
    items && items.Loan_os,
    items && items.loan_outstanding,
    items && items.principal_outstanding,
    items && items.outstanding_amount,
    items && items.loan_balance,
    items && items.Principal_os,
    items && items.total_outstanding,
    items && items.remaining_principal,
    items && items.loan_amount,
    items && items.principal_amount
  ];
  
  // Find the first non-zero value from possible fields
  const foreclosureAmount = possibleLoanFields.find(field => parseFloat(field || 0) > 0) || 0;

  // State management
  const [collectionMode, setCollectionMode] = useState('');
  const [totalCollection, setTotalCollection] = useState(0);
  const [odCollection, setOdCollection] = useState(0);
  const [currentCollection, setCurrentCollection] = useState(0);
  const [chargeCollection, setChargeCollection] = useState(0);
  const [advanceEmiCollection, setAdvanceEmiCollection] = useState(0);
  const [prepaymentCollection, setPrepaymentCollection] = useState(0);
  const [isForeclosure, setIsForeclosure] = useState(false);
  const [paymentToggle, setPaymentToggle] = useState('advance'); // 'advance' or 'foreclosure'
  const [foreclosureCollection, setForeclosureCollection] = useState(0);
  const [foreclosureCharges, setForeclosureCharges] = useState(0); // NEW: 4% of foreclosure amount
  const [netForeclosureAmount, setNetForeclosureAmount] = useState(0); // NEW: 96% of foreclosure amount (applied to POS)
  const [showForeclosureCharges, setShowForeclosureCharges] = useState(true); // NEW: Control visibility
  const [remarks, setRemarks] = useState('');
  const [showAdvanceEmiTooltip, setShowAdvanceEmiTooltip] = useState(false);
  const [showFcmTooltip, setShowFcmTooltip] = useState(false);
  const [showFcmTooltipForeclosure, setShowFcmTooltipForeclosure] = useState(false);
  const [showPrepaymentTooltip, setShowPrepaymentTooltip] = useState(false);
  const [showForeclosureTooltip, setShowForeclosureTooltip] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [chequeFile, setChequeFile] = useState(null);
  const [chequePreview, setChequePreview] = useState(null);
  const [issuerName, setIssuerName] = useState('');
  const [chequeDate, setChequeDate] = useState('');
  const [chequeNumber, setChequeNumber] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [ocrError, setOcrError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [transactionId, setTransactionId] = useState('');
  
  // Payment Link Waiting Modal states
  const [showPaymentWaitingModal, setShowPaymentWaitingModal] = useState(false);
  const [waitingCountdown, setWaitingCountdown] = useState(300); // 5 minutes in seconds
  const [paymentLinkData, setPaymentLinkData] = useState(null); // Store payment link result
  const countdownIntervalRef = useRef(null);
  
  const [isUpdatingFromManualFields, setIsUpdatingFromManualFields] = useState(false); // Flag to prevent loops
  const [manuallyEditedFields, setManuallyEditedFields] = useState(new Set()); // Track which fields were manually edited
  const [autoAllocatedFields, setAutoAllocatedFields] = useState(new Set()); // Track fields auto-allocated from charge reductions
  const isChargeTransferRef = useRef(false); // Flag to prevent total recalculation during charge transfers
  const isManualUpdateRef = useRef(false); // Ref to track manual updates without causing re-renders
  
  // Toast debouncing - prevent duplicate toast messages
  const lastToastMessageRef = useRef('');
  const lastToastTimeRef = useRef(0);
  const TOAST_DEBOUNCE_MS = 2000; // 2 seconds debounce
  
  // Store original total collection and allocation when first entered
  const [originalTotalCollection, setOriginalTotalCollection] = useState(0);
  const [originalAllocation, setOriginalAllocation] = useState({
    od: 0,
    current: 0,
    charges: 0,
    nextEmi: 0,
    prepayment: 0
  });
  const [hasUserEnteredTotal, setHasUserEnteredTotal] = useState(false); // Track if user has manually entered total
  
  // Store original decimal value before rounding for Cash mode (to restore when switching modes)
  const [originalDecimalTotal, setOriginalDecimalTotal] = useState(0);
  
  // Add refs to track previous values for delta calculation
  const previousChargeAmountRef = useRef(0);
  const previousNextEmiAmountRef = useRef(0);
  
  // Flag to prevent Next EMI blur from capping when set by transfer
  const isNextEmiFromTransferRef = useRef(false);
  
  // Ref to track if remainingDemand has been initialized
  const remainingDemandInitializedRef = useRef(false);
  
  // Speech recognition states
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);

  // Display states for formatted inputs
  const [totalCollectionDisplay, setTotalCollectionDisplay] = useState('');
  const [advanceEmiDisplay, setAdvanceEmiDisplay] = useState('');
  const [prepaymentDisplay, setPrepaymentDisplay] = useState('');
  const [foreclosureDisplay, setForeclosureDisplay] = useState('');
  const [chargeCollectionDisplay, setChargeCollectionDisplay] = useState('');

  // Multi-mode payment and logging states
  const [paymentHistory, setPaymentHistory] = useState([]); // Track all payments made
  const [remainingDemand, setRemainingDemand] = useState({
    od: 0,
    current: 0,
    charges: 0,
    nextEmi: 0,
    total: 0
  }); // Track remaining demand after payments
  const [paymentModeLog, setPaymentModeLog] = useState([]); // Log payment mode clicks
  const [upiLinkLog, setUpiLinkLog] = useState([]); // Log UPI link generations

  // Flag to prevent duplicate overflow messages
  const [hasShownOverflowMessage, setHasShownOverflowMessage] = useState(false);

  // Confirmation modal state
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationMode, setConfirmationMode] = useState('');
  const [isOkDisabled, setIsOkDisabled] = useState(false);
  const [timerCount, setTimerCount] = useState(0);

  // Helper to determine if incentive warning should show
  const getIncentiveWarningStatus = () => {
    const totalInst = (advanceEmiCollection || 0) + (prepaymentCollection || 0);
    const potInst = totalInst + (chargeCollection || 0);
    const thresholds = remainingDemand.nextEmi > 0 
      ? [remainingDemand.nextEmi, remainingDemand.nextEmi + standardEmi, remainingDemand.nextEmi + 2 * standardEmi]
      : [standardEmi, 2 * standardEmi, 3 * standardEmi];
    
    return chargeCollection > 0 && thresholds.some(t => potInst >= t - 0.1 && totalInst < t - 0.1);
  };

  // Timer effect for confirmation modal
  useEffect(() => {
    let interval;
    if (showConfirmationModal && getIncentiveWarningStatus()) {
      setIsOkDisabled(true);
      setTimerCount(3);
      
      interval = setInterval(() => {
        setTimerCount((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsOkDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setIsOkDisabled(false);
      setTimerCount(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showConfirmationModal]);

  const generate_Id1 = generateId(10);

  // Fetch prepayment configuration from API when modal is opened
  useEffect(() => {
    // Only fetch if modal is actually open and we have valid items
    if (!isOpen || !items) {
      console.log('⏭️ Skipping prepayment config fetch - modal not open or no items');
      return;
    }

    const fetchPrepaymentConfig = async () => {
      try {
        console.log('🔧 Fetching prepayment configuration...');
        
        const payload = {
          project_name: "prepayment",
          decision_name: "prepayment-count",
          context: {
            branch_id: items && items.branch_id || "",
            product_id: items && items.product_id || "",
            partner_id: items && items.partner_id || "",
            tenure: items && items.tenure || ""
          },
          trace: false,
          sample_trace: false
        };

        const generate_Id = generateId(10);
        let bre_url = process.env.REACT_APP_CLOUD;
        let env = process.env.REACT_APP_ENVIRONMENT;

        const response = await customFetch(`${bre_url}/${process.env.REACT_APP_ENVIRONMENT}_evaluate_business_rule`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'api-token': apiToken,
            component_id: generate_Id1,
            apiId: generate_Id,
            api_group_id: "",
            component: "CollectionModal",
            user_id: localStorage.getItem("in_userid"),
          },
          body: JSON.stringify(payload)
        });

        if (!response) {
          throw new Error('No response received from prepayment config API');
        }

        const data = await response.json();
        
        if (data.status === 'SUCCESS' && data.result && data.result.prepayment_count) {
          const prepaymentCount = parseInt(data.result.prepayment_count);
          console.log('✅ Prepayment config fetched:', prepaymentCount, 'EMIs');
          setMaxPrepaymentEmis(prepaymentCount);
        } else {
          console.warn('⚠️ Invalid API response, using default prepayment count (2)');
          setMaxPrepaymentEmis(2); // Fallback to default
        }
      } catch (error) {
        console.error('❌ Error fetching prepayment config:', error);
        setMaxPrepaymentEmis(2); // Fallback to default on error
      }
    };

    fetchPrepaymentConfig();
  }, [isOpen, items]); // Trigger when modal opens or items change

  // Fetch NACH bounce charges from API when modal is opened
  useEffect(() => {
    // Only fetch if modal is actually open and we have valid items
    if (!isOpen || !items) {
      console.log('⏭️ Skipping bounce charge fetch - modal not open or no items');
      return;
    }

    const fetchBounceCharge = async () => {
      try {
        console.log('🔧 Fetching NACH bounce charges...');
        
        const payload = {
          project_name: "Bounce_Charge",
          decision_name: "SBL_NACH_BOUNCE_CHARGES",
          context: {
            branch_id: items && items.branch_id || 5,
            product_id: items && items.product_id || 4,
            tenure: items && items.tenure || 0,
            txn_days: items && items.txn_days || 30,
            remaining_current_demand_emi: items && items.remaining_current_demand_emi || 1,
            nach_flag: items && items.nach_flag || "F",
            bounce_flag: items && items.bounce_flag || 1
          },
          trace: false,
          sample_trace: false
        };

        const generate_Id = generateId(10);
        let bre_url = process.env.REACT_APP_CLOUD;
        let env = process.env.REACT_APP_ENVIRONMENT;

        const response = await customFetch(`${bre_url}/${process.env.REACT_APP_ENVIRONMENT}_evaluate_business_rule`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'api-token': apiToken,
            component_id: generate_Id1,
            apiId: generate_Id,
            api_group_id: "",
            component: "CollectionModal",
            user_id: localStorage.getItem("in_userid"),
          },
          body: JSON.stringify(payload)
        });

        if (!response) {
          throw new Error('No response received from prepayment config API');
        }

        const data = await response.json();
        
        if (data.status === 'SUCCESS' && data.result && data.result.nach_bounce_charge !== undefined) {
          const bounceCharge = parseFloat(data.result.nach_bounce_charge);
          console.log('✅ NACH bounce charge fetched:', bounceCharge);
          setNachBounceCharge(bounceCharge);
        } else {
          console.warn('⚠️ Invalid API response, using default bounce charge (0)');
          setNachBounceCharge(0); // Fallback to default
        }
      } catch (error) {
        console.error('❌ Error fetching bounce charge:', error);
        setNachBounceCharge(0); // Fallback to default on error
      }
    };

    fetchBounceCharge();
  }, [isOpen, items]); // Trigger when modal opens or items change

  // Initialize remaining demand ONLY ONCE when component mounts with valid demand values
  // Do NOT reinitialize when demands change (after payment/refresh) to preserve remaining demand tracking
  useEffect(() => {
    // Only initialize if:
    // 1. Not already initialized AND
    // 2. No payment history exists (fresh start) AND
    // 3. We have valid demand values
    if (!remainingDemandInitializedRef.current && 
        paymentHistory.length === 0 && 
        (overdueDemand > 0 || currentDemand > 0 || chargeDue > 0 || emiAmount > 0 || totalDemand > 0)) {
      const initialDemand = {
        od: Math.abs(overdueDemand),
        current: currentDemand,
        charges: chargeDue,
        nextEmi: emiAmount,
        total: totalDemand
      };
      console.log('🎯 Initial remainingDemand (useEffect):', initialDemand);
      setRemainingDemand(initialDemand);
      remainingDemandInitializedRef.current = true; // Mark as initialized
    } else if (paymentHistory.length > 0) {
      console.log('⚠️ useEffect skipped - payment history exists, preserving remainingDemand');
    }
  }, [overdueDemand, currentDemand, chargeDue, emiAmount, totalDemand, paymentHistory.length]);

  // Reset all state when component mounts or items change (fresh start)
  // IMPORTANT: Do NOT reset remainingDemand or payment history when items change due to data refresh
  // Only reset when the household changes (HH_ID changes)
  useEffect(() => {
    // Reset all form state to initial values
    setCollectionMode('');
    setTotalCollection(0);
    setOdCollection(0);
    setCurrentCollection(0);
    setChargeCollection(0);
    setAdvanceEmiCollection(0);
    setPrepaymentCollection(0);
    setIsForeclosure(false);
    setPaymentToggle('advance');
    setForeclosureCollection(0);
    setRemarks('');
    setShowAdvanceEmiTooltip(false);
    setShowFcmTooltip(false);
    setShowFcmTooltipForeclosure(false);
    setShowPrepaymentTooltip(false);
    setShowForeclosureTooltip(false);
    setMobileNumber(items && items.Household_mobile_no ? items.Household_mobile_no : '');
    setChequeFile(null);
    setChequePreview(null);
    setIssuerName('');
    setChequeDate('');
    setChequeNumber('');
    setShowDeleteConfirm(false);
    setIsProcessingOCR(false);
    setOcrError('');
    setShowSuccessModal(false);
    setSuccessMessage('');
    setTransactionId('');
    setIsSubmitting(false);
    setIsUpdatingFromManualFields(false); // Reset the flag
    setManuallyEditedFields(new Set()); // Clear manually edited fields tracking
    setAutoAllocatedFields(new Set()); // Clear auto-allocated fields tracking
    isNextEmiFromTransferRef.current = false;
    setHasShownOverflowMessage(false); // Reset overflow message flag
    
    // Reset original total collection tracking
    setOriginalTotalCollection(0);
    setOriginalAllocation({
      od: 0,
      current: 0,
      charges: 0,
      nextEmi: 0,
      prepayment: 0
    });
    setHasUserEnteredTotal(false);
    
    // Reset display values
    setTotalCollectionDisplay('');
    setAdvanceEmiDisplay('');
    setPrepaymentDisplay('');
    setForeclosureDisplay('');
    setChargeCollectionDisplay('');
    
    // Reset foreclosure charges
    setForeclosureCharges(0);
    setNetForeclosureAmount(0);
    setShowForeclosureCharges(false);
    
    // Reset remainingDemand initialization flag when household changes
    // This ensures a fresh start for a new household
    remainingDemandInitializedRef.current = false;
    setPaymentHistory([]); // Clear payment history for new household
  }, [items && items.household_id || items && items.HH_ID]); // Only reset when household ID changes, not on every items update

  // Countdown timer effect for payment waiting modal
  // COMMENTED OUT - Countdown timer disabled
  // useEffect(() => {
  //   if (showPaymentWaitingModal && waitingCountdown > 0) {
  //     countdownIntervalRef.current = setInterval(() => {
  //       setWaitingCountdown(prev => {
  //         if (prev <= 1) {
  //           clearInterval(countdownIntervalRef.current);
  //           // Auto-fail when countdown reaches 0
  //           setTimeout(() => {
  //             console.log('⏰ Payment confirmation timeout - auto-cancelling');
  //             handlePaymentLinkFail();
  //             toast.warning('Payment confirmation timeout. Please retry if needed.', { duration: 5000 });
  //           }, 100); // Small delay to ensure UI updates
  //           return 0;
  //         }
  //         return prev - 1;
  //       });
  //     }, 1000);
  //   }

  //   return () => {
  //     if (countdownIntervalRef.current) {
  //       clearInterval(countdownIntervalRef.current);
  //     }
  //   };
  // }, [showPaymentWaitingModal]);

  // Format countdown to MM:SS
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to format foreclosure display value
  const formatForeclosureDisplay = (value) => {
    if (!value || value === 0) return '';
    
    // For Cash Collection mode, always show integers for amounts >= 1
    // For amounts < 1, show exact decimal value even in Cash mode
    if (collectionMode === 'Cash Collection') {
      if (value < 1) {
        // Small amount - show exact decimal value
        return value.toFixed(2).replace(/\.?0+$/, '');
      } else {
        // Large amount - round to integer
        return Math.round(value).toString();
      }
    }
    
    // For non-Cash modes, show decimals
    return value.toFixed(2).replace(/\.00$/, '');
  };

  // Calculate automatic allocation with dynamic priority: OD -> CD -> Charges -> Next EMI -> Prepayment
  // Enhanced to handle no-OD scenarios and consider payment history for multi-mode scenarios
  const calculateAllocation = (totalAmount) => {
    console.log('💰 calculateAllocation called with totalAmount:', totalAmount);
    console.log('💰 Using remainingDemand:', remainingDemand);
    
    // Check if this is a pre-demand payment scenario
    if (isPreDemandPayment()) {
      return calculatePreDemandAllocation(totalAmount);
    }

    let remaining = totalAmount;
    let od = 0, current = 0, charge = 0, nextEmi = 0, prepayment = 0;

    // Use remainingDemand state which is already updated after each payment
    // This ensures the allocation uses the correct remaining amounts
    const remainingOdDemand = remainingDemand.od;
    const remainingCurrentDemand = remainingDemand.current;
    // Total allowed charges = Charges_os (remainingDemand.charges) + Next EMI
    const remainingChargeDemand = remainingDemand.charges;
    const totalAllowedCharges = remainingChargeDemand + remainingDemand.nextEmi;
    // Round to 2 decimal places to prevent floating-point precision issues from creating tiny overflow amounts
    const remainingNextEmiCapacity = parseFloat(remainingDemand.nextEmi.toFixed(2));
    
    console.log('💰 Remaining demands:', {
      od: remainingOdDemand,
      current: remainingCurrentDemand,
      charges: remainingChargeDemand,
      nextEmi: remainingNextEmiCapacity,
      totalAllowedCharges: totalAllowedCharges
    });

    // Check if no OD is present
    const hasOdDemand = remainingOdDemand > 0;

    // First, account for manually controlled fields by subtracting their current values from remaining
    if (manuallyEditedFields.has('charges')) {
      remaining -= chargeCollection;
      charge = chargeCollection;
    }
    if (manuallyEditedFields.has('nextEmi')) {
      // Cap manually entered Next EMI to the remaining Next EMI capacity to enforce business rules
      const cappedNextEmi = Math.min(advanceEmiCollection, remainingNextEmiCapacity);
      remaining -= cappedNextEmi;
      nextEmi = cappedNextEmi;
    }
    if (manuallyEditedFields.has('prepayment')) {
      remaining -= prepaymentCollection;
      prepayment = prepaymentCollection;
    }

    // Priority 1: OD allocation (highest priority, only if OD exists)
    if (remaining > 0 && hasOdDemand) {
      if (remaining <= remainingOdDemand) {
        od = parseFloat(remaining.toFixed(2));
        remaining = 0;
      } else {
        od = parseFloat(remainingOdDemand.toFixed(2));
        remaining = parseFloat((remaining - remainingOdDemand).toFixed(2));
      }
    }

    // Priority 2: Current demand allocation (first priority if no OD)
    if (remaining > 0 && remainingCurrentDemand > 0) {
      if (remaining <= remainingCurrentDemand) {
        current = parseFloat(remaining.toFixed(2));
        remaining = 0;
      } else {
        current = parseFloat(remainingCurrentDemand.toFixed(2));
        remaining = parseFloat((remaining - remainingCurrentDemand).toFixed(2));
      }
    }

    // Priority 3: Charge allocation (only if not manually controlled)
    // Special handling for no-OD scenario: if CD is fulfilled and amount exceeds total demand, apply to charges
    // Note: Auto-allocation only allocates Charges_os, but user can manually add bounce charges on top
    if (remaining > 0 && remainingChargeDemand > 0 && !manuallyEditedFields.has('charges')) {
      if (remaining <= remainingChargeDemand) {
        charge = parseFloat(remaining.toFixed(2));
        remaining = parseFloat((remaining - charge).toFixed(2));
      } else {
        charge = parseFloat(remainingChargeDemand.toFixed(2));
        remaining = parseFloat((remaining - remainingChargeDemand).toFixed(2));
      }
    }

    // Priority 4: Next EMI allocation (gets priority over prepayment for waterfall approach)
    // Only allocate to Next EMI if not manually edited and there's remaining Next EMI capacity
    // Check how much Next EMI has already been paid across all payments
    
    if (remaining > 0 && remainingNextEmiCapacity > 0 && !manuallyEditedFields.has('nextEmi')) {
      if (remaining <= remainingNextEmiCapacity) {
        nextEmi = parseFloat(remaining.toFixed(2));
        remaining = 0;
      } else {
        nextEmi = parseFloat(remainingNextEmiCapacity.toFixed(2));
        remaining = parseFloat((remaining - remainingNextEmiCapacity).toFixed(2));
      }
    }

    // Priority 5: Prepayment/Advance Collection (limited to 2 EMIs)
    // Any overflow beyond 2 EMIs is treated as foreclosure
    let foreclosure = 0;
    let localForeclosureCharges = 0; // Track foreclosure charges locally to include in Cash roundedSum
    if (remaining > 0 && !manuallyEditedFields.has('prepayment')) {
      if (remaining <= maxPrepaymentAmount && maxPrepaymentAmount > 0) {
        // Amount fits within prepayment limit (2 EMIs)
        prepayment = parseFloat(remaining.toFixed(2));
        remaining = 0;
      } else if (maxPrepaymentAmount > 0) {
        // Amount exceeds prepayment limit, split into prepayment and foreclosure
        prepayment = parseFloat(maxPrepaymentAmount.toFixed(2));
        const overflowAmount = parseFloat((remaining - maxPrepaymentAmount).toFixed(2));
        remaining = 0;
        
        // For small amounts (< 1), show exact overflow without splitting into 96/4
        // For larger amounts, apply the 96/4 split
        let netForeclosureToAdd, foreclosureChargesToAdd;
        
        if (overflowAmount < 1) {
          // Small amount - show exact value, no charge split
          netForeclosureToAdd = Math.round(overflowAmount * 100) / 100;
          foreclosureChargesToAdd = 0;
        } else {
          // Large amount - apply 96/4 split
          netForeclosureToAdd = Math.ceil((overflowAmount * 100) / 104);
          foreclosureChargesToAdd = parseFloat((overflowAmount - netForeclosureToAdd).toFixed(2));
        }
        
        console.log('💰 Foreclosure from Prepayment Overflow:', {
          overflowAmount,
          netForeclosureToAdd,
          foreclosureChargesToAdd,
          verification: netForeclosureToAdd + foreclosureChargesToAdd
        });
        
        // Update foreclosure-related states
        setForeclosureCharges(foreclosureChargesToAdd);
        setNetForeclosureAmount(netForeclosureToAdd);
        setShowForeclosureCharges(foreclosureChargesToAdd > 0);
        setForeclosureCollection(netForeclosureToAdd);
        setForeclosureDisplay(formatForeclosureDisplay(netForeclosureToAdd));
        foreclosure = netForeclosureToAdd;
        localForeclosureCharges = foreclosureChargesToAdd; // Track for Cash rounding
      } else {
        // No prepayment capacity (all demands are 0), entire amount goes to foreclosure
        // For small amounts (< 1), show exact amount without splitting into 96/4
        // For larger amounts, apply the 96/4 split
        let netForeclosureToAdd, foreclosureChargesToAdd;
        
        if (remaining < 1) {
          // Small amount - show exact value, no charge split
          netForeclosureToAdd = Math.round(remaining * 100) / 100;
          foreclosureChargesToAdd = 0;
        } else {
          // Large amount - apply 96/4 split
          netForeclosureToAdd = Math.ceil((remaining * 100) / 104);
          foreclosureChargesToAdd = parseFloat((remaining - netForeclosureToAdd).toFixed(2));
        }
        
        remaining = 0;
        
        console.log('💰 Direct Foreclosure Payment (no demands):', {
          totalAmount: remaining,
          netForeclosureToAdd,
          foreclosureChargesToAdd,
          verification: netForeclosureToAdd + foreclosureChargesToAdd
        });
        
        // Update foreclosure-related states
        setForeclosureCharges(foreclosureChargesToAdd);
        setNetForeclosureAmount(netForeclosureToAdd);
        setShowForeclosureCharges(foreclosureChargesToAdd > 0);
        setForeclosureCollection(netForeclosureToAdd);
        setForeclosureDisplay(formatForeclosureDisplay(netForeclosureToAdd));
        foreclosure = netForeclosureToAdd;
        localForeclosureCharges = foreclosureChargesToAdd; // Track for Cash rounding
      }
    }

    // For Cash Collection mode, round all amounts to integers to avoid decimal issues
    // EXCEPT foreclosure which needs to maintain 96/4 split precision
    if (collectionMode === 'Cash Collection') {
      od = Math.round(od);
      current = Math.round(current);
      charge = Math.round(charge);
      nextEmi = Math.round(nextEmi);
      prepayment = Math.round(prepayment);
      // foreclosure = Math.round(foreclosure); // DON'T round - needs to maintain 96/4 split
      remaining = Math.round(remaining);
      
      // After rounding, check if sum matches total and adjust.
      // IMPORTANT: Include localForeclosureCharges so the 4% foreclosure charge portion
      // is not mistakenly added to prepayment as a rounding difference.
      const roundedSum = od + current + charge + nextEmi + prepayment + foreclosure + localForeclosureCharges;
      const difference = totalAmount - roundedSum;
      
      if (difference !== 0) {
        // Adjust fields to balance the total, but respect demand caps.
        // First: if nextEmi was rounded UP and a small foreclosure exists due to the cap being
        // a floating-point value (e.g. 3299.99), reduce foreclosure to compensate instead of
        // creating a negative prepayment.
        if (foreclosure > 0 && difference < 0) {
          const newForeclosure = parseFloat((foreclosure + difference).toFixed(2));
          if (newForeclosure >= 0) {
            foreclosure = newForeclosure;
            if (foreclosure === 0) {
              // Clear all foreclosure state when it drops to zero
              setForeclosureCollection(0);
              setForeclosureDisplay('');
              setForeclosureCharges(0);
              setNetForeclosureAmount(0);
              setShowForeclosureCharges(false);
            } else {
              setForeclosureCollection(foreclosure);
              setForeclosureDisplay(formatForeclosureDisplay(foreclosure));
            }
          }
        } else if (prepayment > 0) {
          prepayment = prepayment + difference;
        } else if (nextEmi > 0 && nextEmi < remainingNextEmiCapacity) {
          // Only adjust nextEmi if it won't exceed the cap
          const adjustedNextEmi = nextEmi + difference;
          if (adjustedNextEmi <= remainingNextEmiCapacity) {
            nextEmi = adjustedNextEmi;
          } else {
            // If adjustment would exceed cap, put difference in prepayment instead
            nextEmi = remainingNextEmiCapacity;
            prepayment = difference - (remainingNextEmiCapacity - nextEmi);
          }
        } else if (current > 0) {
          current = current + difference;
        } else {
          // Last resort: add to prepayment even if it was 0
          prepayment = prepayment + difference;
        }
      }
    }

    return { od, current, charge, nextEmi, prepayment, remaining, foreclosure };
  };

  // Effect to handle automatic allocation when total collection changes
  useEffect(() => {
    console.log('🔄 Auto-allocation useEffect triggered', {
      totalCollection,
      isUpdatingFromManualFields,
      isManualUpdateRef: isManualUpdateRef.current,
      isChargeTransferRef: isChargeTransferRef.current,
      manuallyEditedFields: Array.from(manuallyEditedFields),
      advanceEmiCollection,
      prepaymentCollection,
      chargeCollection
    });

    // Skip if this update came from manual field changes or charge transfer to prevent loops
    if (isUpdatingFromManualFields || isManualUpdateRef.current || isChargeTransferRef.current) {
      console.log('⏭️ Skipping auto-allocation (flags are set)');
      // Reset the flag after a brief delay to allow the effect to complete
      const timeoutId = setTimeout(() => {
        setIsUpdatingFromManualFields(false);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    
    // Skip auto-allocation if charges are manually edited to prevent interference with charge transfer logic
    if (manuallyEditedFields.has('charges')) {
      console.log('⏭️ Skipping auto-allocation (charges manually edited)');
      return;
    }
    
    if (totalCollection > 0) {
      const allocation = calculateAllocation(totalCollection);
      
      // Check if Next EMI overflow occurred during automatic allocation
      const emiDemand = emiAmount;
      
      // Check if overflow occurred: Next EMI is at limit AND prepayment received allocation
      const overflowOccurred = allocation.nextEmi === emiDemand && allocation.prepayment > 0;
      
      if (overflowOccurred && !hasShownOverflowMessage) {
        // The overflow amount is exactly what's in prepayment since calculateAllocation puts overflow there
        const overflowAmount = allocation.prepayment;
        setHasShownOverflowMessage(true);
        // toast(`₹${formatCurrency(overflowAmount)} moved to Prepayment as Next EMI cannot exceed ₹${formatCurrency(emiDemand)}`, {
        //   icon: 'ℹ️',
        //   style: {
        //     background: '#d1ecf1',
        //     color: '#0c5460',
        //     border: '1px solid #bee5eb'
        //   }
        // });
      } else if (!overflowOccurred) {
        // Reset the flag when no overflow occurs
        setHasShownOverflowMessage(false);
      }
      
      setOdCollection(allocation.od);
      setCurrentCollection(allocation.current);
      
      // Only update charges if not manually edited
      if (!manuallyEditedFields.has('charges')) {
        setChargeCollection(Math.max(0, allocation.charge)); // Ensure charge collection is never negative
        
        // Update charge collection display as well
        if (allocation.charge > 0) {
          setChargeCollectionDisplay(allocation.charge.toString());
        } else {
          setChargeCollectionDisplay('');
        }
      }
      // If charges were manually cleared, keep them at 0 - don't allow automatic restoration

      // Only update Next EMI if not manually edited (respects charge transfer allocation)
      if (!manuallyEditedFields.has('nextEmi')) {
        if (allocation.nextEmi > 0) {
          setAdvanceEmiCollection(allocation.nextEmi);
          // Use formatCurrency for proper display formatting based on collection mode
          setAdvanceEmiDisplay(formatCurrency(allocation.nextEmi));
        } else {
          setAdvanceEmiCollection(0);
          setAdvanceEmiDisplay('');
        }
      }
      
      // Only update prepayment if not manually edited
      if (!manuallyEditedFields.has('prepayment')) {
        setPrepaymentCollection(allocation.prepayment);
        if (allocation.prepayment > 0) {
          // Use formatCurrency for proper display formatting based on collection mode
          setPrepaymentDisplay(formatCurrency(allocation.prepayment));
        } else {
          setPrepaymentDisplay('');
        }
      }
      
      // Handle foreclosure: Update or clear based on allocation
      if (allocation.foreclosure > 0) {
        // Foreclosure amount exists - already set in calculateAllocation
        // But ensure display is updated
        setForeclosureCollection(allocation.foreclosure);
        setForeclosureDisplay(allocation.foreclosure.toFixed(2).replace(/\.00$/, ''));
      } else {
        // No foreclosure needed - clear all foreclosure states
        setForeclosureCollection(0);
        setForeclosureDisplay('');
        setForeclosureCharges(0);
        setNetForeclosureAmount(0);
        setShowForeclosureCharges(false);
      }
      
      setIsForeclosure(false);

      // Capture original allocation AFTER automatic allocation is complete
      // This ensures we have the correct distributed amounts
      if (hasUserEnteredTotal && !isChargeTransferRef.current) {
        setOriginalAllocation({
          od: allocation.od,
          current: allocation.current,
          charges: manuallyEditedFields.has('charges') ? chargeCollection : allocation.charge,
          nextEmi: allocation.nextEmi,
          prepayment: manuallyEditedFields.has('prepayment') ? prepaymentCollection : allocation.prepayment
        });
      }
    } else {
      // Reset all collections when total is 0 or removed, but preserve manually edited fields
      setOdCollection(0);
      setCurrentCollection(0);
      
      if (!manuallyEditedFields.has('charges')) {
        setChargeCollection(0);
        setChargeCollectionDisplay(''); // Clear charge display as well
      }
      
      if (!manuallyEditedFields.has('nextEmi')) {
        setAdvanceEmiCollection(0);
        setAdvanceEmiDisplay('');
      }
      
      if (!manuallyEditedFields.has('prepayment')) {
        setPrepaymentCollection(0);
        setPrepaymentDisplay('');
      }
      
      setForeclosureCollection(0);
      setIsForeclosure(false);
      setHasShownOverflowMessage(false); // Reset overflow message flag
      
      // Reset foreclosure charges when no collection
      setForeclosureCharges(0);
      setNetForeclosureAmount(0);
      setShowForeclosureCharges(false);
    }
  }, [totalCollection, remainingDemand, isUpdatingFromManualFields, manuallyEditedFields, hasUserEnteredTotal]); // Updated to use remainingDemand instead of individual demand values

  // Effect to update total collection when individual fields are manually changed
  useEffect(() => {
    // Only calculate and update total if we're not currently in an automatic allocation update
    // and not in a charge transfer operation
    // and Next EMI is not manually controlled (to prevent automatic total changes when user enters Next EMI)
    // and we don't have an original total stored (to prevent disturbing user-entered totals)
    if (!isUpdatingFromManualFields && !isManualUpdateRef.current && !isChargeTransferRef.current && 
        !manuallyEditedFields.has('nextEmi') && !hasUserEnteredTotal) {
      // Always include all fields in total calculation, including foreclosure
      // For foreclosure: include BOTH net amount (96%) AND charges (4%) to get the full amount
      const totalForeclosureAmount = (foreclosureCollection || 0) + (foreclosureCharges || 0);
      const calculatedTotal = odCollection + currentCollection + chargeCollection + 
                             (advanceEmiCollection || 0) + (prepaymentCollection || 0) + totalForeclosureAmount;
      
      // Only update if the calculated total is different from current total (avoid infinite loops)
      if (Math.abs(calculatedTotal - totalCollection) > 0.01) {
        isManualUpdateRef.current = true; // Set ref to indicate manual update
        setIsUpdatingFromManualFields(true); // Set flag to prevent loop
        setTotalCollection(calculatedTotal);
        setTotalCollectionDisplay(calculatedTotal > 0 ? formatCurrency(calculatedTotal) : '');
        
        // Reset the manual update flag after state updates
        setTimeout(() => {
          isManualUpdateRef.current = false;
          setIsUpdatingFromManualFields(false);
        }, 200);
      }
    }
  }, [chargeCollection, prepaymentCollection, foreclosureCollection, paymentToggle, manuallyEditedFields, hasUserEnteredTotal]); // Removed isChargeTransfer since it's now a ref

  // Effect to handle clicking outside tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAdvanceEmiTooltip && !event.target.closest('.info-icon-wrapper')) {
        setShowAdvanceEmiTooltip(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showAdvanceEmiTooltip]);

  // Effect to handle clicking outside foreclosure tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showForeclosureTooltip && !event.target.closest('.foreclosure-info-icon-wrapper')) {
        setShowForeclosureTooltip(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showForeclosureTooltip]);

  // Speech recognition setup
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      setSpeechSupported(true);
    }
  }, []);

  const formatCurrency = (value, forceInteger = false) => {
    if (!value && value !== 0) return '';
    const strValue = String(value);
    const isNegative = strValue.includes('-');
    
    // Handle decimal values properly
    const numValue = parseFloat(strValue.replace(/[^0-9.-]/g, ''));
    if (isNaN(numValue)) return '';
    
    // In Cash Collection mode or when forceInteger is true, round to integer
    const shouldRoundToInteger = collectionMode === 'Cash Collection' || forceInteger;
    const valueToFormat = shouldRoundToInteger ? Math.round(numValue) : numValue;
    
    // Format with Indian locale
    const formatted = valueToFormat.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: shouldRoundToInteger ? 0 : 2
    });
    
    return isNegative ? `-${formatted.replace('-', '')}` : formatted;
  };

  // Debounced toast error function to prevent duplicate messages
  const showDebouncedToastError = (message) => {
    const now = Date.now();
    const timeSinceLastToast = now - lastToastTimeRef.current;
    const isSameMessage = lastToastMessageRef.current === message;
    
    // Only show toast if:
    // 1. It's a different message OR
    // 2. Enough time has passed since the same message was shown
    if (!isSameMessage || timeSinceLastToast > TOAST_DEBOUNCE_MS) {
      toast.error(message);
      lastToastMessageRef.current = message;
      lastToastTimeRef.current = now;
    }
  };

  // Enhanced validation function to check if manual edits maintain total balance
  const validateManualEdit = (fieldName, newValue, showError = true) => {
    // Enhanced charge handling validation scenario
    if (fieldName === 'charges' && newValue > 0) {
      const odCovered = odCollection >= remainingDemand.od;
      const cdCovered = currentCollection >= remainingDemand.current;
      const totalCoversOnlyOdAndCd = totalCollection <= (remainingDemand.od + remainingDemand.current);
      
      // Scenario: OD and CD are collected, charges outstanding, but total only covers OD+CD
      if (odCovered && cdCovered && remainingDemand.charges > 0 && totalCoversOnlyOdAndCd) {
        if (showError) {
          showDebouncedToastError("Amount doesn't match total collected. Please update total collected first.");
        }
        return false;
      }
      
      // Calculate the delta (how much user is trying to add)
      const currentChargeAmount = chargeCollection || 0;
      const chargeIncrease = newValue - currentChargeAmount;
      
      // Check if user is INCREASING charges (adding back)
      if (chargeIncrease > 0) {
        // When increasing charges, we'll take the amount from Next EMI or Prepayment
        // So we need to check if there's enough in Next EMI + Prepayment to take from
        const availableInNextEmi = advanceEmiCollection || 0;
        const availableInPrepayment = prepaymentCollection || 0;
        const totalAvailableToTakeFrom = availableInNextEmi + availableInPrepayment;
        
        // If we have enough in Next EMI + Prepayment, allow the increase
        if (totalAvailableToTakeFrom >= chargeIncrease) {
          return true; // Allow the increase, blur handler will take from Next EMI/Prepayment
        }
        
        // If not enough in Next EMI/Prepayment, check if it would exceed total collection
        const proposedAllocation = odCollection + currentCollection + newValue + availableInNextEmi + availableInPrepayment - chargeIncrease;
        if (proposedAllocation > totalCollection) {
          if (showError) {
            showDebouncedToastError("Amount doesn't match total collected. Please update total collected first.");
          }
          return false;
        }
      } else {
        // User is REDUCING or keeping the same - check normal validation
        const currentAllocation = odCollection + currentCollection + chargeCollection;
        const proposedAllocation = odCollection + currentCollection + newValue;
        
        if (proposedAllocation > totalCollection) {
          if (showError) {
            showDebouncedToastError("Amount doesn't match total collected. Please update total collected first.");
          }
          return false;
        }
      }
    }

    // Enhanced validation for Next EMI reduction
    if (fieldName === 'nextEmi' && newValue < getCurrentFieldValue(fieldName)) {
      // Check if this field was auto-allocated from charge reductions
      if (autoAllocatedFields.has('nextEmi')) {
        if (showError) {
          showDebouncedToastError("You can't edit this amount as it was auto-allocated from charge reductions. Please update the total collected or redistribute the amount to other fields.");
        }
        return false;
      }
      
      // Check if the reduction would break the balance and there's nowhere to move the amount
      const reductionAmount = getCurrentFieldValue(fieldName) - newValue;
      const canMoveToPrepayment = !manuallyEditedFields.has('prepayment');
      const availableInPrepayment = prepaymentCollection || 0;
      
      // If prepayment is manually controlled and can't accommodate the reduction
      if (!canMoveToPrepayment && availableInPrepayment < reductionAmount) {
        if (showError) {
          showDebouncedToastError("You can't edit this amount as total collected will not be equal to bifurcated amount. OR try updating the total collected.");
        }
        return false;
      }
    }

    // Enhanced validation for Prepayment/Advance Collection reduction
    if (fieldName === 'prepayment' && newValue < getCurrentFieldValue(fieldName)) {
      // Check if this field was auto-allocated from charge reductions
      if (autoAllocatedFields.has('prepayment')) {
        if (showError) {
          showDebouncedToastError("You can't edit this amount as it was auto-allocated from charge reductions. Please update the total collected or redistribute the amount to other fields.");
        }
        return false;
      }
      
      // Check if this is the final allocation and reduction would break balance
      const reductionAmount = getCurrentFieldValue(fieldName) - newValue;
      const canMoveToNextEmi = !manuallyEditedFields.has('nextEmi');
      const nextEmiRoom = Math.max(0, emiAmount - (advanceEmiCollection || 0));
      
      // If Next EMI is manually controlled or filled, and can't accommodate the reduction
      if (!canMoveToNextEmi || nextEmiRoom < reductionAmount) {
        if (showError) {
          showDebouncedToastError("You can't edit this amount as total collected will not be equal to bifurcated amount. OR try updating the total collected.");
        }
        return false;
      }
    }

    // For charges reduction, don't validate here - let the blur handler auto-move the amount
    if (fieldName === 'charges' && newValue < getCurrentFieldValue(fieldName)) {
      return true; // Always allow charge reduction, auto-move logic handles it
    }

    // Calculate what the total should be if this field is changed
    let proposedOd = odCollection;
    let proposedCurrent = currentCollection;
    let proposedCharge = chargeCollection;
    let proposedNextEmi = advanceEmiCollection;
    let proposedPrepayment = prepaymentCollection;
    let proposedForeclosure = foreclosureCollection;

    // Update the proposed value for the field being edited
    switch (fieldName) {
      case 'charges':
        proposedCharge = newValue;
        break;
      case 'nextEmi':
        proposedNextEmi = newValue;
        break;
      case 'prepayment':
        proposedPrepayment = newValue;
        break;
      case 'foreclosure':
        proposedForeclosure = newValue;
        break;
      default:
        return true; // Unknown field, allow edit
    }

    // Calculate proposed total - always include all fields
    // For foreclosure: include BOTH net amount (96%) AND charges (4%) to get the full amount
    const proposedTotalForeclosure = proposedForeclosure + (foreclosureCharges || 0);
    const proposedTotal = proposedOd + proposedCurrent + proposedCharge + 
                         proposedNextEmi + proposedPrepayment + proposedTotalForeclosure;

    // Check if proposed total matches current total collection
    const isValid = Math.abs(proposedTotal - totalCollection) <= 0.01;

    if (!isValid && showError) {
      // Provide specific error messages based on field and scenario
      let errorMessage = "";
      
      if (newValue < getCurrentFieldValue(fieldName)) {
        // User is trying to reduce amount
        if (fieldName === 'nextEmi' || fieldName === 'prepayment') {
          errorMessage = "You can't edit this amount as total collected will not be equal to bifurcated amount. OR try updating the total collected.";
        } else {
          // This should rarely happen now since charges auto-move
          errorMessage = `You can't reduce this amount as total collected will not be equal to bifurcated amount. Please update the total collected or redistribute the amount to other fields.`;
        }
      } else {
        // User is trying to increase amount beyond available
        if (fieldName === 'charges') {
          errorMessage = `Amount doesn't match total collected. Please update total collected first.`;
        } else {
          errorMessage = `You cannot enter this amount as it does not match the total collected. Please update the total collected to reflect the ${fieldName} due.`;
        }
      }
      
      if (typeof toast !== 'undefined') {
        showDebouncedToastError(errorMessage);
      } else {
        alert(errorMessage); // Fallback if toast is not available
      }
    }

    return isValid;
  };

  // Helper function to check if payment is being made before demand generation
  const isPreDemandPayment = () => {
    // If Current Demand (CD) is 0 or very low, it indicates payment before demand generation
    return remainingDemand.current <= 0;
  };

  // Enhanced allocation for pre-demand payments
  const calculatePreDemandAllocation = (totalAmount) => {
    let remaining = totalAmount;
    let od = 0, current = 0, charge = 0, nextEmi = 0, prepayment = 0;

    // Priority 1: OD allocation (if any overdue exists)
    const odDemand = remainingDemand.od;
    if (remaining > 0 && odDemand > 0) {
      if (remaining <= odDemand) {
        od = remaining;
        remaining = 0;
      } else {
        od = odDemand;
        remaining = remaining - odDemand;
      }
    }

    // Priority 2: Skip Current Demand (CD = 0 in pre-demand scenario)
    current = 0;

    // First, account for manually controlled fields by subtracting their current values from remaining
    if (manuallyEditedFields.has('charges')) {
      remaining -= chargeCollection;
      charge = chargeCollection;
    }
    if (manuallyEditedFields.has('nextEmi')) {
      // Cap manually entered Next EMI to the EMI demand to enforce business rules
      const cappedNextEmi = Math.min(advanceEmiCollection, emiAmount);
      remaining -= cappedNextEmi;
      nextEmi = cappedNextEmi;
    }
    if (manuallyEditedFields.has('prepayment')) {
      remaining -= prepaymentCollection;
      prepayment = prepaymentCollection;
    }

    // Priority 3: Charges (if any exist)
    // Auto-allocation only allocates Charges_os, user can manually add bounce charges later
    const chargeDemand = remainingDemand.charges;
    if (remaining > 0 && chargeDemand > 0 && !manuallyEditedFields.has('charges')) {
      if (remaining <= chargeDemand) {
        charge = remaining;
        remaining = remaining - charge;
      } else {
        charge = chargeDemand;
        remaining = remaining - chargeDemand;
      }
    }

    // Priority 4: Next EMI (gets priority in pre-demand payments)
    // Only allocate to Next EMI if not manually edited and there's remaining capacity
    // Round to 2 decimal places to prevent floating-point precision issues from creating tiny overflow amounts
    const remainingNextEmiCapacity = parseFloat(remainingDemand.nextEmi.toFixed(2));
    if (remaining > 0 && remainingNextEmiCapacity > 0 && !manuallyEditedFields.has('nextEmi')) {
      if (remaining <= remainingNextEmiCapacity) {
        nextEmi = remaining;
        remaining = 0;
      } else {
        nextEmi = remainingNextEmiCapacity;
        remaining = remaining - remainingNextEmiCapacity;
      }
    }

    // Priority 5: Prepayment/Advance Collection (limited to 2 EMIs)
    // Any overflow beyond 2 EMIs is treated as foreclosure
    let foreclosure = 0;
    if (remaining > 0 && !manuallyEditedFields.has('prepayment')) {
      if (remaining <= maxPrepaymentAmount && maxPrepaymentAmount > 0) {
        // Amount fits within prepayment limit (2 EMIs)
        prepayment = remaining;
        remaining = 0;
      } else if (maxPrepaymentAmount > 0) {
        // Amount exceeds prepayment limit, split into prepayment and foreclosure
        prepayment = maxPrepaymentAmount;
        const overflowAmount = remaining - maxPrepaymentAmount;
        remaining = 0;
        
        // For small amounts (< 1), show exact overflow without splitting into 96/4
        // For larger amounts, apply the 96/4 split
        let netForeclosureToAdd, foreclosureChargesToAdd;
        
        if (overflowAmount < 1) {
          // Small amount - show exact value, no charge split
          netForeclosureToAdd = Math.round(overflowAmount * 100) / 100;
          foreclosureChargesToAdd = 0;
        } else {
          // Large amount - apply 96/4 split
          netForeclosureToAdd = Math.ceil((overflowAmount * 100) / 104);
          foreclosureChargesToAdd = overflowAmount - netForeclosureToAdd;
        }
        
        console.log('💰 Pre-Demand Foreclosure from Prepayment Overflow:', {
          overflowAmount,
          netForeclosureToAdd,
          foreclosureChargesToAdd,
          verification: (netForeclosureToAdd + foreclosureChargesToAdd)
        });
        
        // Update foreclosure-related states
        setForeclosureCharges(foreclosureChargesToAdd);
        setNetForeclosureAmount(netForeclosureToAdd);
        setShowForeclosureCharges(foreclosureChargesToAdd > 0);
        setForeclosureCollection(netForeclosureToAdd);
        setForeclosureDisplay(formatForeclosureDisplay(netForeclosureToAdd));
        foreclosure = netForeclosureToAdd;
      } else {
        // No prepayment capacity (all demands are 0), entire amount goes to foreclosure
        // For small amounts (< 1), show exact amount without splitting into 96/4
        // For larger amounts, apply the 96/4 split
        let netForeclosureToAdd, foreclosureChargesToAdd;
        const amountToForeclosure = remaining;
        
        if (amountToForeclosure < 1) {
          // Small amount - show exact value, no charge split
          netForeclosureToAdd = Math.round(amountToForeclosure * 100) / 100;
          foreclosureChargesToAdd = 0;
        } else {
          // Large amount - apply 96/4 split
          netForeclosureToAdd = Math.ceil((amountToForeclosure * 100) / 104);
          foreclosureChargesToAdd = amountToForeclosure - netForeclosureToAdd;
        }
        
        remaining = 0;
        
        console.log('💰 Pre-Demand Direct Foreclosure Payment (no demands):', {
          totalAmount: amountToForeclosure,
          netForeclosureToAdd,
          foreclosureChargesToAdd,
          verification: (netForeclosureToAdd + foreclosureChargesToAdd)
        });
        
        // Update foreclosure-related states
        setForeclosureCharges(foreclosureChargesToAdd);
        setNetForeclosureAmount(netForeclosureToAdd);
        setShowForeclosureCharges(foreclosureChargesToAdd > 0);
        setForeclosureCollection(netForeclosureToAdd);
        setForeclosureDisplay(formatForeclosureDisplay(netForeclosureToAdd));
        foreclosure = netForeclosureToAdd;
      }
    }

    // For Cash Collection mode, round all amounts to integers to avoid decimal issues
    // EXCEPT foreclosure which needs to maintain 96/4 split precision
    if (collectionMode === 'Cash Collection') {
      od = Math.round(od);
      current = Math.round(current);
      charge = Math.round(charge);
      nextEmi = Math.round(nextEmi);
      prepayment = Math.round(prepayment);
      // foreclosure = Math.round(foreclosure); // DON'T round - needs to maintain 96/4 split
      remaining = Math.round(remaining);

      // After rounding, check if sum matches total and adjust.
      // If nextEmi rounded up and a tiny foreclosure exists, reduce foreclosure to compensate.
      const roundedSumPD = od + current + charge + nextEmi + prepayment + foreclosure;
      const differencePD = totalAmount - roundedSumPD;
      if (differencePD !== 0 && foreclosure > 0 && differencePD < 0) {
        const newForeclosure = parseFloat((foreclosure + differencePD).toFixed(2));
        if (newForeclosure >= 0) {
          foreclosure = newForeclosure;
          if (foreclosure === 0) {
            setForeclosureCollection(0);
            setForeclosureDisplay('');
            setForeclosureCharges(0);
            setNetForeclosureAmount(0);
            setShowForeclosureCharges(false);
          } else {
            setForeclosureCollection(foreclosure);
            setForeclosureDisplay(formatForeclosureDisplay(foreclosure));
          }
        }
      }
    }

    return { od, current, charge, nextEmi, prepayment, remaining, foreclosure };
  };

  // Payment mode logging functions
  const logPaymentModeClick = (paymentMode) => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      paymentMode: paymentMode,
      householdId: items.household_id || items.HH_ID,
      userId: localStorage.getItem("in_userid"),
      action: 'payment_mode_selected'
    };
    
    setPaymentModeLog(prev => [...prev, logEntry]);
    
    // Send to backend if needed
    // logToBackend('payment_mode_click', logEntry);
  };

  const logUpiLinkGeneration = (linkDetails, status = 'generated') => {
    const logEntry = {
      timestamp: new Date().toISOString(),
      householdId: items.household_id || items.HH_ID,
      userId: localStorage.getItem("in_userid"),
      action: 'upi_link_generated',
      status: status, // 'generated', 'sent', 'failed', 'completed'
      amount: totalCollection,
      mobileNumber: mobileNumber,
      linkDetails: linkDetails
    };
    
    setUpiLinkLog(prev => [...prev, logEntry]);
    
    // Send to backend if needed
    // logToBackend('upi_link_action', logEntry);
  };







  // Enhanced demand display for post-demand generation scenarios
  const getPostDemandGenerationDisplay = () => {
    return {
      odDisplay: Math.abs(overdueDemand) || 0,
      cdDisplay: currentDemand, // This should be adjusted by backend after demand generation
      chargesDisplay: chargeDue || 0,
      
      // Show information about advance payments impact
      advancePaymentInfo: paymentHistory.length > 0 ? {
        totalAdvancePaid: paymentHistory.reduce((sum, p) => sum + (p.allocation.nextEmi || 0), 0),
        impactOnCurrentDemand: true
      } : null
    };
  };


  const addPaymentToHistory = (paymentData) => {
    console.log('📥 Adding payment to history:', paymentData); // Debug log
    const historyEntry = {
      id: generateId(8),
      timestamp: new Date().toISOString(),
      mode: paymentData.mode,
      amount: paymentData.amount,
      allocation: paymentData.allocation,
      detailedAllocation: {
        od: { amount: paymentData.allocation.od || 0, mode: paymentData.mode },
        current: { amount: paymentData.allocation.current || 0, mode: paymentData.mode },
        charges: { amount: paymentData.allocation.charges || 0, mode: paymentData.mode },
        nextEmi: { amount: paymentData.allocation.nextEmi || 0, mode: paymentData.mode },
        prepayment: { amount: paymentData.allocation.prepayment || 0, mode: paymentData.mode }
      },
      transactionId: paymentData.transactionId,
      status: paymentData.status || 'completed'
    };
    
    console.log('📦 History entry created:', historyEntry); // Debug log
    setPaymentHistory(prev => {
      const newHistory = [...prev, historyEntry];
      console.log('📚 Updated payment history (total payments: ' + newHistory.length + '):', newHistory); // Debug log
      return newHistory;
    });
    
    // Only update remaining demand if this is NOT the final payment
    // For the final payment, we want to show the allocation summary without recalculating
    // Remaining demand should only be updated when preparing for the next payment
    // This is handled by resetForNextPayment() when user clicks "Next Payment" button
    // DO NOT call updateRemainingDemand here as it causes auto-allocation to re-run
    // updateRemainingDemand(paymentData.allocation);
  };

  const updateRemainingDemand = (paidAllocation) => {
    console.log('📊 updateRemainingDemand called with:', paidAllocation);
    setRemainingDemand(prev => {
      const newOd = Math.max(0, prev.od - (paidAllocation.od || 0));
      const newCurrent = Math.max(0, prev.current - (paidAllocation.current || 0));
      const newCharges = Math.max(0, prev.charges - (paidAllocation.charges || 0));
      const newNextEmi = Math.max(0, prev.nextEmi - (paidAllocation.nextEmi || 0));
      
      const updated = {
        od: newOd,
        current: newCurrent,
        charges: newCharges,
        nextEmi: newNextEmi,
        // Total should be sum of core demands (od + current + charges), not including Next EMI/Prepayment
        total: newOd + newCurrent + newCharges
      };
      console.log('📊 Previous remainingDemand:', prev);
      console.log('📊 Paid allocation:', paidAllocation);
      console.log('📊 Updated remainingDemand:', updated);
      return updated;
    });
  };

  // Function to reset collection form for next payment in multi-mode scenario
  const resetForNextPayment = () => {
    console.log('🔄 resetForNextPayment called');
    console.log('🔄 Current remainingDemand BEFORE update:', remainingDemand);
    console.log('🔄 Payment history:', paymentHistory);
    
    // Update remaining demand based on the last completed payment
    // This ensures the next payment uses the correct remaining amounts
    if (paymentHistory.length > 0) {
      const lastPayment = paymentHistory[paymentHistory.length - 1];
      console.log('🔄 Last payment:', lastPayment);
      console.log('💳 Payment mode:', lastPayment.mode);
      
      // Check if last payment was Cheque
      if (lastPayment.mode === 'Cheque') {
        // DON'T update demand for Cheque payments as they need FinOps approval
        console.log('✋ Cheque payment - Skipping demand update (requires FinOps approval)');
        toast('After FinOps approval the demand will change', { 
          duration: 5000,
          position: 'top-center',
          icon: '📋',
          style: {
            background: '#3b82f6',
            color: '#fff',
          },
        });
      } else {
        // Cash or UPI - update demand immediately
        console.log('💰 Cash/UPI payment - Updating demand with allocation:', lastPayment.allocation);
        updateRemainingDemand(lastPayment.allocation);
        console.log('🔄 updateRemainingDemand called - state will update asynchronously');
        
        // Call parent component's data refresh function for Cash/UPI
        // NOTE: This will cause items prop to update with backend data, but we preserve our remainingDemand state
        if (onDataRefresh && typeof onDataRefresh === 'function') {
          console.log('🔄 Calling onDataRefresh to refresh parent data');
          onDataRefresh();
        }
      }
    }
    
    setTotalCollection(0);
    setTotalCollectionDisplay('');
    setOdCollection(0);
    setCurrentCollection(0);
    setChargeCollection(0);
    setChargeCollectionDisplay('');
    setAdvanceEmiCollection(0);
    setAdvanceEmiDisplay('');
    setPrepaymentCollection(0);
    setPrepaymentDisplay('');
    setForeclosureCollection(0);
    setForeclosureDisplay('');
    setRemarks('');
    
    // Reset foreclosure charges
    setForeclosureCharges(0);
    setNetForeclosureAmount(0);
    setShowForeclosureCharges(false);
    
    // Clear manual field tracking for fresh auto-allocation
    setManuallyEditedFields(new Set());
    setIsUpdatingFromManualFields(false);
    isManualUpdateRef.current = false;
    isNextEmiFromTransferRef.current = false;
    setHasShownOverflowMessage(false); // Reset overflow message flag
    
    // Reset original total collection tracking
    setOriginalTotalCollection(0);
    setOriginalAllocation({
      od: 0,
      current: 0,
      charges: 0,
      nextEmi: 0,
      prepayment: 0
    });
    setHasUserEnteredTotal(false);
    
    console.log('🔄 resetForNextPayment completed');
  };

  // Function to calculate adjusted demands based on payment history
  const getAdjustedDemands = () => {
    return {
      overdueDemand: Math.max(0, Math.abs(overdueDemand) - (remainingDemand.od || 0)),
      currentDemand: Math.max(0, currentDemand - (remainingDemand.current || 0)),
      chargeDue: Math.max(0, chargeDue - (remainingDemand.charges || 0)),
      totalDemand: Math.max(0, totalDemand - (remainingDemand.total || 0))
    };
  };

  // Function to get cumulative allocation across all payment modes
  const getCumulativeAllocation = () => {
    const cumulative = {
      od: { amount: 0, modes: {} },
      current: { amount: 0, modes: {} },
      charges: { amount: 0, modes: {} },
      nextEmi: { amount: 0, modes: {} },
      prepayment: { amount: 0, modes: {} }
    };

    // Add from payment history
    paymentHistory.forEach(payment => {
      const allocation = payment.allocation || {};
      Object.keys(cumulative).forEach(bucket => {
        const amount = allocation[bucket] || 0;
        if (amount > 0) {
          cumulative[bucket].amount += amount;
          cumulative[bucket].modes[payment.mode] = (cumulative[bucket].modes[payment.mode] || 0) + amount;
        }
      });
    });

    // Add current payment if any
    const currentAllocation = {
      od: odCollection,
      current: currentCollection,
      charges: chargeCollection,
      nextEmi: advanceEmiCollection,
      prepayment: prepaymentCollection
    };

    Object.keys(cumulative).forEach(bucket => {
      const amount = currentAllocation[bucket] || 0;
      if (amount > 0) {
        cumulative[bucket].amount += amount;
        cumulative[bucket].modes[collectionMode] = (cumulative[bucket].modes[collectionMode] || 0) + amount;
      }
    });

    return cumulative;
  };

  // Function to get final result breakdown for multi-mode scenarios
  const getFinalResultBreakdown = () => {
    const cumulative = getCumulativeAllocation();
    const totalPrepaymentAdvance = cumulative.prepayment.amount + cumulative.nextEmi.amount;
    
    return {
      od: {
        total: cumulative.od.amount,
        cash: cumulative.od.modes['Cash Collection'] || 0,
        upi: cumulative.od.modes['Payment Link'] || 0,
        cheque: cumulative.od.modes['Cheque'] || 0
      },
      current: {
        total: cumulative.current.amount,
        cash: cumulative.current.modes['Cash Collection'] || 0,
        upi: cumulative.current.modes['Payment Link'] || 0,
        cheque: cumulative.current.modes['Cheque'] || 0
      },
      charges: {
        total: cumulative.charges.amount,
        cash: cumulative.charges.modes['Cash Collection'] || 0,
        upi: cumulative.charges.modes['Payment Link'] || 0,
        cheque: cumulative.charges.modes['Cheque'] || 0
      },
      nextEmi: {
        total: cumulative.nextEmi.amount,
        cash: cumulative.nextEmi.modes['Cash Collection'] || 0,
        upi: cumulative.nextEmi.modes['Payment Link'] || 0,
        cheque: cumulative.nextEmi.modes['Cheque'] || 0
      },
      prepayment: {
        total: cumulative.prepayment.amount,
        cash: cumulative.prepayment.modes['Cash Collection'] || 0,
        upi: cumulative.prepayment.modes['Payment Link'] || 0,
        cheque: cumulative.prepayment.modes['Cheque'] || 0
      },
      payloadPrepaymentAdvance: totalPrepaymentAdvance,
      totalAmount: Object.values(cumulative).reduce((sum, bucket) => sum + bucket.amount, 0)
    };
  };

  // Check if this is a subsequent payment (after first payment in multi-mode)
  const hasExistingPayments = () => {
    return paymentHistory.length > 0;
  };

  // Get display message for remaining amounts
  const getRemainingAmountDisplay = () => {
    if (!hasExistingPayments()) return null;
    
    const adjusted = getAdjustedDemands();
    return {
      message: `Previous payments made. Remaining: OD: ₹${formatCurrency(adjusted.overdueDemand)}, CD: ₹${formatCurrency(adjusted.currentDemand)}, Charges: ₹${formatCurrency(adjusted.chargeDue)}`,
      totalRemaining: adjusted.totalDemand
    };
  };

  // Helper function to get current value of a field
  const getCurrentFieldValue = (fieldName) => {
    switch (fieldName) {
      case 'charges':
        return chargeCollection;
      case 'nextEmi':
        return advanceEmiCollection;
      case 'prepayment':
        return prepaymentCollection;
      case 'foreclosure':
        return foreclosureCollection;
      default:
        return 0;
    }
  };

  const handleModeChange = (e) => {
    const selectedMode = e.target.value;
    const previousMode = collectionMode;
    setCollectionMode(selectedMode);
    
    // Log the payment mode selection
    logPaymentModeClick(selectedMode);
    
    // If switching FROM Cash mode to another mode, restore the original decimal value
    if (previousMode === 'Cash Collection' && selectedMode !== 'Cash Collection' && originalDecimalTotal > 0) {
      setTotalCollection(originalDecimalTotal);
      setTotalCollectionDisplay(originalDecimalTotal.toString());
      setOriginalDecimalTotal(0);
    }
    
    // If switching TO Cash mode from another mode, round the totalCollection
    if (previousMode !== 'Cash Collection' && selectedMode === 'Cash Collection' && totalCollection > 0) {
      const roundedTotal = Math.round(totalCollection);
      if (totalCollection !== roundedTotal) {
        setOriginalDecimalTotal(totalCollection);
      }
      setTotalCollection(roundedTotal);
      setTotalCollectionDisplay(formatCurrency(roundedTotal));
    }
    
    // Dismiss all existing toasts before showing new one
    toast.dismiss();
    
    // Show toast notification to acknowledge mode selection
    const modeDisplayName = selectedMode === 'Cash Collection' ? 'Cash' : 
                           selectedMode === 'Payment Link' ? 'UPI/Payment Link' : 
                           'Cheque/Demand Draft';
    toast.success(`Collection mode switched to: ${modeDisplayName}`, {
      duration: 2000,
      icon: selectedMode === 'Cash Collection' ? '💵' : 
            selectedMode === 'Payment Link' ? '📱' : '📄'
    });
  };

  const handleTotalCollectionChange = (e) => {
    let inputValue = e.target.value;
    let value = 0;
    
    if (collectionMode === 'Cash Collection') {
      // For Cash mode: Remove all non-digits (including commas and decimals)
      // First remove commas, then split by decimal to get only integer part
      const cleanValue = inputValue.replace(/,/g, ''); // Remove commas
      const integerPart = cleanValue.split('.')[0]; // Get only integer part before decimal
      inputValue = integerPart.replace(/[^0-9]/g, ''); // Keep only digits
      value = parseInt(inputValue) || 0;
    } else {
      // For non-cash modes: Allow decimals up to 2 places
      inputValue = inputValue.replace(/[^0-9.]/g, '');
      
      // Limit to 2 decimal places
      if (inputValue.includes('.')) {
        const parts = inputValue.split('.');
        if (parts[1] && parts[1].length > 2) {
          inputValue = parts[0] + '.' + parts[1].substring(0, 2);
        }
      }
      value = parseFloat(inputValue) || 0;
    }
    
    // If total is cleared (set to 0), reset all collection fields
    if (value === 0 || inputValue === '') {
      console.log('🧹 Total cleared, resetting all collection fields');
      setTotalCollection(0);
      setTotalCollectionDisplay('');
      setOdCollection(0);
      setCurrentCollection(0);
      setChargeCollection(0);
      setChargeCollectionDisplay('');
      setAdvanceEmiCollection(0);
      setAdvanceEmiDisplay('');
      setPrepaymentCollection(0);
      setPrepaymentDisplay('');
      setForeclosureCollection(0);
      setForeclosureDisplay('');
      
      // Reset foreclosure charges
      setForeclosureCharges(0);
      setNetForeclosureAmount(0);
      setShowForeclosureCharges(false);
      
      // Clear manual field tracking
      setManuallyEditedFields(new Set());
      setAutoAllocatedFields(new Set());
      setHasUserEnteredTotal(false);
      setOriginalTotalCollection(0);
      return;
    }
    
    // Store/update original total collection whenever user enters/changes a value
    if (value > 0) {
      setOriginalTotalCollection(value);
      setHasUserEnteredTotal(true);
    }
    
    // Clear manual field tracking when total changes to allow fresh auto-allocation
    if (value > 0 && value !== totalCollection) {
      console.log('🔄 Total changed, clearing manual field tracking for fresh allocation');
      setManuallyEditedFields(new Set());
      setAutoAllocatedFields(new Set());
    }
    
    // For Cash Collection mode, round to integer before storing
    // This ensures allocation is based on rounded value
    const valueToStore = collectionMode === 'Cash Collection' ? Math.round(value) : value;
    
    // Store the original decimal value before rounding (for mode switching)
    if (collectionMode === 'Cash Collection' && value !== Math.round(value)) {
      setOriginalDecimalTotal(value);
    }
    
    // Allow users to enter any amount they want without restrictions
    // Always allow typing any amount - keep raw input without transformation
    setTotalCollection(valueToStore);
    setTotalCollectionDisplay(inputValue); // Keep raw typed value during editing
  };

  const handleTotalCollectionFocus = (e) => {
    if (totalCollection === 0) {
      setTotalCollectionDisplay(''); // Show empty field when amount is 0
      e.target.select(); // Select all text so typing replaces it
    } else {
      // For non-cash modes, show value with up to 2 decimal places
      if (collectionMode !== 'Cash Collection') {
        const formattedValue = Number(totalCollection).toFixed(2).replace(/\.?0+$/, '');
        setTotalCollectionDisplay(formattedValue);
      } else {
        // For cash mode, round to integer to avoid floating point precision issues
        const roundedValue = Math.round(totalCollection);
        setTotalCollectionDisplay(roundedValue.toString());
      }
    }
  };

  const handleTotalCollectionBlur = (e) => {
    // Allow users to enter any amount they want - just format the display
    if (totalCollection > 0) {
      // For non-cash modes, show value with up to 2 decimals but remove trailing zeros
      if (collectionMode !== 'Cash Collection') {
        const formattedValue = Number(totalCollection).toFixed(2).replace(/\.?0+$/, '');
        setTotalCollectionDisplay(formattedValue);
      } else {
        // For cash mode, round to integer and format with commas
        const roundedValue = Math.round(totalCollection);
        setTotalCollectionDisplay(formatCurrency(roundedValue));
      }
    } else {
      setTotalCollectionDisplay('');
    }
  };

  const handleChargeCollectionChange = (e) => {
    let inputValue = e.target.value;
    let value = 0;
    
    if (collectionMode === 'Cash Collection') {
      // For Cash mode: Remove all non-digits (including commas and decimals)
      const cleanValue = inputValue.replace(/,/g, ''); // Remove commas
      const integerPart = cleanValue.split('.')[0]; // Get only integer part before decimal
      inputValue = integerPart.replace(/[^0-9]/g, ''); // Keep only digits
      value = parseInt(inputValue) || 0;
    } else {
      // For non-cash modes: Allow decimals up to 2 places
      inputValue = inputValue.replace(/[^0-9.]/g, '');
      
      // Limit to 2 decimal places
      if (inputValue.includes('.')) {
        const parts = inputValue.split('.');
        if (parts[1] && parts[1].length > 2) {
          inputValue = parts[0] + '.' + parts[1].substring(0, 2);
        }
      }
      value = parseFloat(inputValue) || 0;
    }
    
    // Get previous charge amount for delta calculation
    const previousChargeAmount = chargeCollection || 0;
    
    // Get max charge limit from remaining demand + Next EMI
    // Total allowed charges = Charges_os (from remainingDemand) + Next EMI
    const maxChargeLimit = remainingDemand.charges + remainingDemand.nextEmi;
    
    console.log('💰 Charge Validation:', {
      chargeDue: chargeDue,
      remainingCharges: remainingDemand.charges,
      nextEmi: remainingDemand.nextEmi,
      maxChargeLimit: maxChargeLimit,
      enteredValue: value
    });
    
    // VALIDATION 1 (Priority): Check if the new charge value would exceed the total collection
    // For charges, we only need to check against core demands (OD + CD) because:
    // - When charges increase, they TAKE BACK from Next EMI/Prepayment automatically
    // - When charges decrease, they TRANSFER TO Next EMI/Prepayment automatically
    // So we should allow charges up to: Total Collection - (OD + CD)
    // This validation runs FIRST because it's more restrictive than charge demand
    const coreAllocation = odCollection + currentCollection;
    const maxAllowableChargeFromTotal = totalCollection - coreAllocation;
    
    if (value > maxAllowableChargeFromTotal) {
      showDebouncedToastError("Amount doesn't match total collected. Please update total collected first");
      return;
    }
    
    // VALIDATION 2: Don't allow charges to exceed the total allowed charges (Charges_os + Next EMI)
    // This runs second as a fallback check
    if (value > maxChargeLimit) {
      showDebouncedToastError(`Charges cannot exceed ₹${formatCurrency(maxChargeLimit)} (Charge Due: ₹${formatCurrency(remainingDemand.charges)} + EMI: ₹${formatCurrency(remainingDemand.nextEmi)})`);
      // Keep the current value, don't update
      return;
    }
    
    // VALIDATION 3: If user is INCREASING charges, check if there's enough in Foreclosure + Prepayment + Next EMI to take back
    if (value > previousChargeAmount) {
      const deltaToTakeBack = value - previousChargeAmount;
      const currentForeclosureAmount = foreclosureCollection || 0;
      const currentPrepaymentAmount = prepaymentCollection || 0;
      const currentNextEmiAmount = advanceEmiCollection || 0;
      const totalAvailableToTakeBack = currentForeclosureAmount + currentPrepaymentAmount + currentNextEmiAmount;
      
      // If trying to increase more than what's available in Foreclosure + Prepayment + Next EMI
      if (deltaToTakeBack > totalAvailableToTakeBack) {
        // Check if both Next EMI and Prepayment have insufficient funds
        const availableInAdvanceFields = currentPrepaymentAmount + currentNextEmiAmount;
        if (availableInAdvanceFields === 0 || deltaToTakeBack > availableInAdvanceFields) {
          showDebouncedToastError("Insufficient amount. Please increase the collected amount to apply charge.");
          return;
        }
        
        const maxAllowableCharge = previousChargeAmount + totalAvailableToTakeBack;
        showDebouncedToastError(`You are trying to add ₹${formatCurrency(value)} in charges. Only ₹${formatCurrency(totalAvailableToTakeBack)} available in Foreclosure/Prepayment/Next EMI to allocate. Maximum charge you can enter is ₹${formatCurrency(maxAllowableCharge)}`);
        return;
      }
    }
    
    // Set flags to prevent automatic allocation during typing
    isChargeTransferRef.current = true;
    setIsUpdatingFromManualFields(true);
    isManualUpdateRef.current = true;
    
    // Mark this field as manually edited
    setManuallyEditedFields(prev => new Set(prev).add('charges'));
    
    // Always allow typing, keep raw input
    setChargeCollection(value);
    setChargeCollectionDisplay(inputValue);
    
    // If user clears the field (empty input), immediately set to 0 and transfer to Next EMI → Prepayment → Foreclosure
    if (inputValue === '' || inputValue === '0') {
      setChargeCollection(0);
      
      // If there was a previous charge amount, transfer it when clearing
      if (previousChargeAmount > 0) {
        const deltaToTransfer = previousChargeAmount;
        
        // Check remaining Next EMI capacity
        const remainingNextEmiCapacity = remainingDemand.nextEmi;
        const currentNextEmiAmount = advanceEmiCollection || 0;
        const nextEmiRoom = Math.max(0, remainingNextEmiCapacity - currentNextEmiAmount);
        
        // Transfer to Next EMI first, then check prepayment capacity, overflow to Foreclosure
        if (nextEmiRoom > 0) {
          const amountToNextEmi = Math.min(deltaToTransfer, nextEmiRoom);
          const remainingAfterNextEmi = deltaToTransfer - amountToNextEmi;
          
          // Update Next EMI
          const newNextEmiAmount = currentNextEmiAmount + amountToNextEmi;
          setAdvanceEmiCollection(newNextEmiAmount);
          setAdvanceEmiDisplay(newNextEmiAmount > 0 ? newNextEmiAmount.toString() : '');
          setManuallyEditedFields(prev => new Set(prev).add('nextEmi'));
          setAutoAllocatedFields(prev => new Set(prev).add('nextEmi'));
          
          if (amountToNextEmi > 0) {
            isNextEmiFromTransferRef.current = true;
          }
          
          // Check prepayment capacity for remaining amount
          if (remainingAfterNextEmi > 0) {
            const currentPrepaymentAmount = prepaymentCollection || 0;
            const prepaymentRoom = Math.max(0, maxPrepaymentAmount - currentPrepaymentAmount);
            
            if (prepaymentRoom > 0) {
              const amountToPrepayment = Math.min(remainingAfterNextEmi, prepaymentRoom);
              const amountToForeclosure = remainingAfterNextEmi - amountToPrepayment;
              
              // Allocate to prepayment
              if (amountToPrepayment > 0) {
                const newPrepaymentAmount = currentPrepaymentAmount + amountToPrepayment;
                setPrepaymentCollection(newPrepaymentAmount);
                setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
                setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
                setAutoAllocatedFields(prev => new Set(prev).add('prepayment'));
              }
              
              // Allocate overflow to foreclosure
              if (amountToForeclosure > 0) {
                // When transferring from prepayment/charges, the amount is ALREADY the collection amount
                // We need to split it: 96% to foreclosure, 4% to charges
                // e.g., 1000 transferred → 960 to foreclosure + 40 to charges
                const netForeclosureToAdd = Math.ceil((amountToForeclosure * 100) / 104); // e.g., 960
                const foreclosureChargesToAdd = amountToForeclosure - netForeclosureToAdd; // e.g., 40
                
                const currentForeclosureAmount = foreclosureCollection || 0;
                const currentForeclosureCharges = foreclosureCharges || 0;
                
                const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
                const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
                
                setForeclosureCollection(newNetForeclosure);
                setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
                setForeclosureCharges(newForeclosureCharges);
                setNetForeclosureAmount(newNetForeclosure);
                setShowForeclosureCharges(true);
                
                // toast(`₹${formatCurrency(amountToForeclosure)} moved to Foreclosure as Prepayment limit (2 EMIs) reached`, {
                //   duration: 4000
                // });
              }
            } else {
              // Prepayment is full, all goes to foreclosure
              // When transferring from prepayment/charges, the amount is ALREADY the collection amount
              // We need to split it: 96% to foreclosure, 4% to charges
              const netForeclosureToAdd = Math.ceil((remainingAfterNextEmi * 100) / 104);
              const foreclosureChargesToAdd = remainingAfterNextEmi - netForeclosureToAdd;
              
              const currentForeclosureAmount = foreclosureCollection || 0;
              const currentForeclosureCharges = foreclosureCharges || 0;
              
              const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
              const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
              
              setForeclosureCollection(newNetForeclosure);
              setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
              setForeclosureCharges(newForeclosureCharges);
              setNetForeclosureAmount(newNetForeclosure);
              setShowForeclosureCharges(true);
              
              // toast(`₹${formatCurrency(remainingAfterNextEmi)} moved to Foreclosure as Prepayment limit (2 EMIs) reached`, {
              //   duration: 4000
              // });
            }
          }
        } else {
          // No Next EMI room, check prepayment capacity
          const currentPrepaymentAmount = prepaymentCollection || 0;
          const prepaymentRoom = Math.max(0, maxPrepaymentAmount - currentPrepaymentAmount);
          
          if (prepaymentRoom > 0) {
            const amountToPrepayment = Math.min(deltaToTransfer, prepaymentRoom);
            const amountToForeclosure = deltaToTransfer - amountToPrepayment;
            
            // Allocate to prepayment
            if (amountToPrepayment > 0) {
              const newPrepaymentAmount = currentPrepaymentAmount + amountToPrepayment;
              setPrepaymentCollection(newPrepaymentAmount);
              setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
              setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
              setAutoAllocatedFields(prev => new Set(prev).add('prepayment'));
            }
            
            // Allocate overflow to foreclosure
            if (amountToForeclosure > 0) {
              // When transferring, split: 96% to foreclosure, 4% to charges
              const netForeclosureToAdd = Math.ceil((amountToForeclosure * 100) / 104);
              const foreclosureChargesToAdd = amountToForeclosure - netForeclosureToAdd;
              
              const currentForeclosureAmount = foreclosureCollection || 0;
              const currentForeclosureCharges = foreclosureCharges || 0;
              
              const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
              const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
              
              setForeclosureCollection(newNetForeclosure);
              setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
              setForeclosureCharges(newForeclosureCharges);
              setNetForeclosureAmount(newNetForeclosure);
              setShowForeclosureCharges(true);
              
              // toast(`₹${formatCurrency(amountToForeclosure)} moved to Foreclosure as Prepayment limit (2 EMIs) reached`, {
              //   duration: 4000
              // });
            }
          } else {
            // Both Next EMI and Prepayment are full, all goes to foreclosure
            // When transferring, split: 96% to foreclosure, 4% to charges
            const netForeclosureToAdd = Math.ceil((deltaToTransfer * 100) / 104);
            const foreclosureChargesToAdd = deltaToTransfer - netForeclosureToAdd;
            
            const currentForeclosureAmount = foreclosureCollection || 0;
            const currentForeclosureCharges = foreclosureCharges || 0;
            
            const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
            const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
            
            setForeclosureCollection(newNetForeclosure);
            setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
            setForeclosureCharges(newForeclosureCharges);
            setNetForeclosureAmount(newNetForeclosure);
            setShowForeclosureCharges(true);
            
            // toast(`₹${formatCurrency(deltaToTransfer)} moved to Foreclosure as both Next EMI and Prepayment limits reached`, {
            //   duration: 4000
            // });
          }
        }
      }
    } else if (value < previousChargeAmount) {
      // User is REDUCING charges - transfer the delta to Next EMI → Prepayment → Foreclosure
      const deltaToTransfer = previousChargeAmount - value;
      
      // Check remaining Next EMI capacity
      const remainingNextEmiCapacity = remainingDemand.nextEmi;
      const currentNextEmiAmount = advanceEmiCollection || 0;
      const nextEmiRoom = Math.max(0, remainingNextEmiCapacity - currentNextEmiAmount);
      
      // Transfer to Next EMI first, then check prepayment capacity, overflow to Foreclosure
      if (nextEmiRoom > 0) {
        const amountToNextEmi = Math.min(deltaToTransfer, nextEmiRoom);
        const remainingAfterNextEmi = deltaToTransfer - amountToNextEmi;
        
        // Update Next EMI
        const newNextEmiAmount = currentNextEmiAmount + amountToNextEmi;
        setAdvanceEmiCollection(newNextEmiAmount);
        setAdvanceEmiDisplay(newNextEmiAmount > 0 ? newNextEmiAmount.toString() : '');
        setManuallyEditedFields(prev => new Set(prev).add('nextEmi'));
        setAutoAllocatedFields(prev => new Set(prev).add('nextEmi'));
        
        if (amountToNextEmi > 0) {
          isNextEmiFromTransferRef.current = true;
        }
        
        // Check prepayment capacity for remaining amount
        if (remainingAfterNextEmi > 0) {
          const currentPrepaymentAmount = prepaymentCollection || 0;
          const prepaymentRoom = Math.max(0, maxPrepaymentAmount - currentPrepaymentAmount);
          
          if (prepaymentRoom > 0) {
            const amountToPrepayment = Math.min(remainingAfterNextEmi, prepaymentRoom);
            const amountToForeclosure = remainingAfterNextEmi - amountToPrepayment;
            
            // Allocate to prepayment
            if (amountToPrepayment > 0) {
              const newPrepaymentAmount = currentPrepaymentAmount + amountToPrepayment;
              setPrepaymentCollection(newPrepaymentAmount);
              setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
              setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
              setAutoAllocatedFields(prev => new Set(prev).add('prepayment'));
            }
            
            // Allocate overflow to foreclosure
            if (amountToForeclosure > 0) {
              // When transferring, split: 96% to foreclosure, 4% to charges
              const netForeclosureToAdd = Math.ceil((amountToForeclosure * 100) / 104);
              const foreclosureChargesToAdd = amountToForeclosure - netForeclosureToAdd;
              
              const currentForeclosureAmount = foreclosureCollection || 0;
              const currentForeclosureCharges = foreclosureCharges || 0;
              
              const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
              const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
              
              setForeclosureCollection(newNetForeclosure);
              setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
              setForeclosureCharges(newForeclosureCharges);
              setNetForeclosureAmount(newNetForeclosure);
              setShowForeclosureCharges(true);
              
              // toast(`₹${formatCurrency(amountToForeclosure)} moved to Foreclosure as Prepayment limit (2 EMIs) reached`, {
              //   duration: 4000
              // });
            }
          } else {
            // Prepayment is full, all goes to foreclosure
            // When transferring, split: 96% to foreclosure, 4% to charges
            const netForeclosureToAdd = Math.ceil((remainingAfterNextEmi * 100) / 104);
            const foreclosureChargesToAdd = remainingAfterNextEmi - netForeclosureToAdd;
            
            const currentForeclosureAmount = foreclosureCollection || 0;
            const currentForeclosureCharges = foreclosureCharges || 0;
            
            const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
            const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
            
            setForeclosureCollection(newNetForeclosure);
            setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
            setForeclosureCharges(newForeclosureCharges);
            setNetForeclosureAmount(newNetForeclosure);
            setShowForeclosureCharges(true);
            
            // toast(`₹${formatCurrency(remainingAfterNextEmi)} moved to Foreclosure as Prepayment limit (2 EMIs) reached`, {
            //   duration: 4000
            // });
          }
        }
      } else {
        // No Next EMI room, check prepayment capacity
        const currentPrepaymentAmount = prepaymentCollection || 0;
        const prepaymentRoom = Math.max(0, maxPrepaymentAmount - currentPrepaymentAmount);
        
        if (prepaymentRoom > 0) {
          const amountToPrepayment = Math.min(deltaToTransfer, prepaymentRoom);
          const amountToForeclosure = deltaToTransfer - amountToPrepayment;
          
          // Allocate to prepayment
          if (amountToPrepayment > 0) {
            const newPrepaymentAmount = currentPrepaymentAmount + amountToPrepayment;
            setPrepaymentCollection(newPrepaymentAmount);
            setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
            setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
            setAutoAllocatedFields(prev => new Set(prev).add('prepayment'));
          }
          
          // Allocate overflow to foreclosure
          if (amountToForeclosure > 0) {
            // When transferring, split: 96% to foreclosure, 4% to charges
            const netForeclosureToAdd = Math.ceil((amountToForeclosure * 100) / 104);
            const foreclosureChargesToAdd = amountToForeclosure - netForeclosureToAdd;
            
            const currentForeclosureAmount = foreclosureCollection || 0;
            const currentForeclosureCharges = foreclosureCharges || 0;
            
            const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
            const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
            
            setForeclosureCollection(newNetForeclosure);
            setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
            setForeclosureCharges(newForeclosureCharges);
            setNetForeclosureAmount(newNetForeclosure);
            setShowForeclosureCharges(true);
            
            // toast(`₹${formatCurrency(amountToForeclosure)} moved to Foreclosure as Prepayment limit (2 EMIs) reached`, {
            //   duration: 4000
            // });
          }
        } else {
          // Both Next EMI and Prepayment are full, all goes to foreclosure
          // When transferring, split: 96% to foreclosure, 4% to charges
          const netForeclosureToAdd = Math.ceil((deltaToTransfer * 100) / 104);
          const foreclosureChargesToAdd = deltaToTransfer - netForeclosureToAdd;
          
          const currentForeclosureAmount = foreclosureCollection || 0;
          const currentForeclosureCharges = foreclosureCharges || 0;
          
          const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
          const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
          
          setForeclosureCollection(newNetForeclosure);
          setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
          setForeclosureCharges(newForeclosureCharges);
          setNetForeclosureAmount(newNetForeclosure);
          setShowForeclosureCharges(true);
          
          // toast(`₹${formatCurrency(deltaToTransfer)} moved to Foreclosure as both Next EMI and Prepayment limits reached`, {
          //   duration: 4000
          // });
        }
      }
    } else if (value > previousChargeAmount) {
      // User is INCREASING charges - take back from Foreclosure FIRST, then Prepayment, then Next EMI
      const deltaToTakeBack = value - previousChargeAmount;
      
      const currentForeclosureAmount = foreclosureCollection || 0;
      const currentPrepaymentAmount = prepaymentCollection || 0;
      const currentNextEmiAmount = advanceEmiCollection || 0;
      
      let remainingToTakeBack = deltaToTakeBack;
      
      // PRIORITY 1: Take from Foreclosure FIRST (includes both net foreclosure + foreclosure charges)
      // When taking back from foreclosure, we need to take BOTH the net amount AND its 4% charges
      const currentForeclosureCharges = foreclosureCharges || 0;
      const totalForeclosureWithCharges = currentForeclosureAmount + currentForeclosureCharges;
      
      if (totalForeclosureWithCharges > 0) {
        const amountFromForeclosureTotal = Math.min(remainingToTakeBack, totalForeclosureWithCharges);
        remainingToTakeBack = remainingToTakeBack - amountFromForeclosureTotal;
        
        // Calculate how much to reduce from net foreclosure (96%) and charges (4%)
        const netForeclosureToReduce = Math.ceil((amountFromForeclosureTotal * 100) / 104);
        const foreclosureChargesToReduce = amountFromForeclosureTotal - netForeclosureToReduce;
        
        // Reduce Foreclosure
        const newForeclosureAmount = Math.max(0, currentForeclosureAmount - netForeclosureToReduce);
        const newForeclosureCharges = Math.max(0, currentForeclosureCharges - foreclosureChargesToReduce);
        
        if (newForeclosureAmount > 0) {
          setForeclosureCollection(newForeclosureAmount);
          setForeclosureDisplay(newForeclosureAmount > 0 ? newForeclosureAmount.toFixed(2).replace(/\.00$/, '') : '');
          setForeclosureCharges(newForeclosureCharges);
          setNetForeclosureAmount(newForeclosureAmount);
          setShowForeclosureCharges(true);
        } else {
          // No foreclosure remaining, reset charges
          setForeclosureCollection(0);
          setForeclosureDisplay('');
          setForeclosureCharges(0);
          setNetForeclosureAmount(0);
          setShowForeclosureCharges(false);
        }
        
        // toast(`₹${formatCurrency(amountFromForeclosureTotal)} taken back from Foreclosure to Charges`, {
        //   duration: 3000
        // });
      }
      
      // PRIORITY 2: Take from Prepayment if still needed
      if (remainingToTakeBack > 0 && currentPrepaymentAmount > 0) {
        const amountFromPrepayment = Math.min(remainingToTakeBack, currentPrepaymentAmount);
        remainingToTakeBack = remainingToTakeBack - amountFromPrepayment;
        
        // Reduce Prepayment
        const newPrepaymentAmount = currentPrepaymentAmount - amountFromPrepayment;
        setPrepaymentCollection(newPrepaymentAmount);
        setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
      }
      
      // PRIORITY 3: Take from Next EMI if still needed
      if (remainingToTakeBack > 0 && currentNextEmiAmount > 0) {
        const amountFromNextEmi = Math.min(remainingToTakeBack, currentNextEmiAmount);
        
        const newNextEmiAmount = currentNextEmiAmount - amountFromNextEmi;
        setAdvanceEmiCollection(newNextEmiAmount);
        setAdvanceEmiDisplay(newNextEmiAmount > 0 ? newNextEmiAmount.toString() : '');
      }
    }
    
    // Reset flags after a short delay to allow state updates
    setTimeout(() => {
      setIsUpdatingFromManualFields(false);
      isManualUpdateRef.current = false;
      isChargeTransferRef.current = false;
    }, 50);
  };

  const handleChargeCollectionFocus = (e) => {
    if (!chargeCollection || chargeCollection === 0) {
      e.target.select(); // Select all text so typing replaces it
    }
    if (chargeCollection === 0) {
      setChargeCollectionDisplay(''); // Show empty field when value is 0
    } else {
      // For non-cash modes, show value with up to 2 decimal places
      if (collectionMode !== 'Cash Collection') {
        const formattedValue = Number(chargeCollection).toFixed(2).replace(/\.?0+$/, '');
        setChargeCollectionDisplay(formattedValue);
      } else {
        // For cash mode, round to integer to avoid floating point precision issues
        const roundedValue = Math.round(chargeCollection);
        setChargeCollectionDisplay(roundedValue.toString());
      }
    }
  };

  const handleChargeCollectionBlur = (e) => {
    const originalChargeAmount = chargeCollection;
    // Total allowed charges = Charges_os + Next EMI
    const maxCharge = remainingDemand.charges + remainingDemand.nextEmi;
    
    // Debug logging
    console.log('Charge blur - Current:', originalChargeAmount, 'Max Charge (remaining demand + EMI):', maxCharge, 
                'Breakdown:', {chargeDue: remainingDemand.charges, nextEmi: remainingDemand.nextEmi});
    
    // Validate and limit the amount on blur
    if (originalChargeAmount > maxCharge) {
      // If exceeds max charge, cap it and transfer the excess
      const excess = originalChargeAmount - maxCharge;
      setChargeCollection(maxCharge);
      setChargeCollectionDisplay(formatCurrency(maxCharge));
      showDebouncedToastError(`Charge collection limited to due amount of ₹${formatCurrency(maxCharge)}`);
      
      // Transfer the excess to Next EMI/Prepayment/Foreclosure
      const remainingNextEmiCapacity = remainingDemand.nextEmi;
      const currentNextEmiAmount = advanceEmiCollection || 0;
      const nextEmiRoom = Math.max(0, remainingNextEmiCapacity - currentNextEmiAmount);
      
      if (nextEmiRoom > 0) {
        const amountToNextEmi = Math.min(excess, nextEmiRoom);
        const remainingAfterNextEmi = excess - amountToNextEmi;
        
        const newNextEmiAmount = currentNextEmiAmount + amountToNextEmi;
        setAdvanceEmiCollection(newNextEmiAmount);
        setAdvanceEmiDisplay(newNextEmiAmount > 0 ? newNextEmiAmount.toString() : '');
        setManuallyEditedFields(prev => new Set(prev).add('nextEmi'));
        setAutoAllocatedFields(prev => new Set(prev).add('nextEmi'));
        
        if (amountToNextEmi > 0) {
          isNextEmiFromTransferRef.current = true;
        }
        
        // Check if remaining amount fits in prepayment or goes to foreclosure
        if (remainingAfterNextEmi > 0) {
          const currentPrepaymentAmount = prepaymentCollection || 0;
          const prepaymentRoom = Math.max(0, maxPrepaymentAmount - currentPrepaymentAmount);
          
          if (prepaymentRoom > 0) {
            const amountToPrepayment = Math.min(remainingAfterNextEmi, prepaymentRoom);
            const amountToForeclosure = remainingAfterNextEmi - amountToPrepayment;
            
            // Allocate to prepayment
            if (amountToPrepayment > 0) {
              const newPrepaymentAmount = currentPrepaymentAmount + amountToPrepayment;
              setPrepaymentCollection(newPrepaymentAmount);
              setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
              setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
              setAutoAllocatedFields(prev => new Set(prev).add('prepayment'));
            }
            
            // Allocate overflow to foreclosure
            if (amountToForeclosure > 0) {
              // When transferring, split: 96% to foreclosure, 4% to charges
              const netForeclosureToAdd = Math.ceil((amountToForeclosure * 100) / 104);
              const foreclosureChargesToAdd = amountToForeclosure - netForeclosureToAdd;
              
              const currentForeclosureAmount = foreclosureCollection || 0;
              const currentForeclosureCharges = foreclosureCharges || 0;
              
              const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
              const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
              
              setForeclosureCollection(newNetForeclosure);
              setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
              setForeclosureCharges(newForeclosureCharges);
              setNetForeclosureAmount(newNetForeclosure);
              setShowForeclosureCharges(true);
              
              // toast(`₹${formatCurrency(amountToForeclosure)} moved to Foreclosure as Prepayment limit (2 EMIs) reached`, {
              //   duration: 4000
              // });
            }
          } else {
            // Prepayment is full, all goes to foreclosure
            // When transferring, split: 96% to foreclosure, 4% to charges
            const netForeclosureToAdd = Math.ceil((remainingAfterNextEmi * 100) / 104);
            const foreclosureChargesToAdd = remainingAfterNextEmi - netForeclosureToAdd;
            
            const currentForeclosureAmount = foreclosureCollection || 0;
            const currentForeclosureCharges = foreclosureCharges || 0;
            
            const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
            const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
            
            setForeclosureCollection(newNetForeclosure);
            setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
            setForeclosureCharges(newForeclosureCharges);
            setNetForeclosureAmount(newNetForeclosure);
            setShowForeclosureCharges(true);
            
            // toast(`₹${formatCurrency(remainingAfterNextEmi)} moved to Foreclosure as Prepayment limit (2 EMIs) reached`, {
            //   duration: 4000
            // });
          }
        }
      } else {
        // No Next EMI room, check prepayment capacity
        const currentPrepaymentAmount = prepaymentCollection || 0;
        const prepaymentRoom = Math.max(0, maxPrepaymentAmount - currentPrepaymentAmount);
        
        if (prepaymentRoom > 0) {
          const amountToPrepayment = Math.min(excess, prepaymentRoom);
          const amountToForeclosure = excess - amountToPrepayment;
          
          // Allocate to prepayment
          if (amountToPrepayment > 0) {
            const newPrepaymentAmount = currentPrepaymentAmount + amountToPrepayment;
            setPrepaymentCollection(newPrepaymentAmount);
            setPrepaymentDisplay(newPrepaymentAmount > 0 ? newPrepaymentAmount.toString() : '');
            setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
            setAutoAllocatedFields(prev => new Set(prev).add('prepayment'));
          }
          
          // Allocate overflow to foreclosure
          if (amountToForeclosure > 0) {
            const currentForeclosureAmount = foreclosureCollection || 0;
            const currentForeclosureCharges = foreclosureCharges || 0;
            
            // Split the overflow amount: 96% to foreclosure, 4% to charges
            // Use parseFloat and toFixed(2) to preserve decimal values for small amounts
            const netForeclosureToAdd = Math.ceil((amountToForeclosure * 100) / 104);
            const foreclosureChargesToAdd = amountToForeclosure - netForeclosureToAdd;
            
            const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
            const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
            
            setForeclosureCollection(newNetForeclosure);
            setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
            setForeclosureCharges(newForeclosureCharges);
            setNetForeclosureAmount(newNetForeclosure);
            setShowForeclosureCharges(true);
            
            // toast(`₹${formatCurrency(amountToForeclosure)} moved to Foreclosure as Prepayment limit (2 EMIs) reached`, {
            //   duration: 4000
            // });
          }
        } else {
          // Both Next EMI and Prepayment are full, all goes to foreclosure
          const currentForeclosureAmount = foreclosureCollection || 0;
          const currentForeclosureCharges = foreclosureCharges || 0;
          
          // Split the excess amount: 96% to foreclosure, 4% to charges
          // Use parseFloat and toFixed(2) to preserve decimal values for small amounts
          const netForeclosureToAdd = Math.ceil((excess * 100) / 104);
          const foreclosureChargesToAdd = excess - netForeclosureToAdd;
          
          const newNetForeclosure = currentForeclosureAmount + netForeclosureToAdd;
          const newForeclosureCharges = currentForeclosureCharges + foreclosureChargesToAdd;
          
          setForeclosureCollection(newNetForeclosure);
          setForeclosureDisplay(newNetForeclosure > 0 ? newNetForeclosure.toString() : '');
          setForeclosureCharges(newForeclosureCharges);
          setNetForeclosureAmount(newNetForeclosure);
          setShowForeclosureCharges(true);
          
          // toast(`₹${formatCurrency(excess)} moved to Foreclosure as both Next EMI and Prepayment limits reached`, {
          //   duration: 4000
          // });
        }
      }
    } else if (originalChargeAmount > 0) {
      // Just format the display - no transfer logic on blur
      // Transfer already happened in onChange handler
      if (collectionMode !== 'Cash Collection') {
        const formattedValue = Number(originalChargeAmount).toFixed(2).replace(/\.?0+$/, '');
        setChargeCollectionDisplay(formattedValue);
      } else {
        const roundedValue = Math.round(originalChargeAmount);
        setChargeCollectionDisplay(formatCurrency(roundedValue));
      }
    } else {
      // If charge is cleared (set to 0), properly clear both state and display
      setChargeCollection(0);
      setChargeCollectionDisplay('');
      setManuallyEditedFields(prev => new Set(prev).add('charges'));
    }
  };

  const handleAdvanceEmiChange = (e) => {
    let inputValue = e.target.value;
    let value = 0;
    
    if (collectionMode === 'Cash Collection') {
      // For Cash mode: Remove all non-digits (including commas and decimals)
      const cleanValue = inputValue.replace(/,/g, ''); // Remove commas
      const integerPart = cleanValue.split('.')[0]; // Get only integer part before decimal
      inputValue = integerPart.replace(/[^0-9]/g, ''); // Keep only digits
      value = parseInt(inputValue) || 0;
    } else {
      // For non-cash modes: Allow decimals up to 2 places
      inputValue = inputValue.replace(/[^0-9.]/g, '');
      
      // Limit to 2 decimal places
      if (inputValue.includes('.')) {
        const parts = inputValue.split('.');
        if (parts[1] && parts[1].length > 2) {
          inputValue = parts[0] + '.' + parts[1].substring(0, 2);
        }
      }
      value = parseFloat(inputValue) || 0;
    }
    
    // Check remaining Next EMI capacity after previous payments
    const remainingNextEmiCapacity = remainingDemand.nextEmi;
    
    // If there's no remaining Next EMI capacity, prevent any input
    if (remainingNextEmiCapacity <= 0) {
      showDebouncedToastError('Next EMI has already been fully paid in previous payments');
      return;
    }
    
    // Prevent reduction of Next EMI amount (including clearing/deleting)
    // This handles Ctrl+A/Cmd+A delete, backspace, or any reduction
    const currentAmount = advanceEmiCollection || 0;
    if (value < currentAmount) {
      // Check if this field was auto-allocated from charge transfers
      if (autoAllocatedFields.has('nextEmi')) {
        showDebouncedToastError("You can't reduce or remove this amount as it was transferred from charges. Please edit the charge amount instead.");
        // Restore the current value
        e.target.value = advanceEmiDisplay;
        return;
      }
      
      // For manually entered amounts, still prevent reduction
      if (value > 0) {
        showDebouncedToastError("You can't reduce Next EMI amount. Please update the total collected or redistribute the amount to other fields.");
      } else {
        showDebouncedToastError("You can't clear Next EMI amount. Please update the total collected or redistribute the amount to other fields.");
      }
      // Restore the current value
      e.target.value = advanceEmiDisplay;
      return;
    }
    
    // Mark this field as manually edited
    setManuallyEditedFields(prev => new Set(prev).add('nextEmi'));
    
    // Automatically switch to advance mode when user enters advance EMI amount
    if (value > 0 && paymentToggle === 'foreclosure') {
      setPaymentToggle('advance');
      // Clear foreclosure field
      setForeclosureCollection(0);
    }
    
    // Validate that the entered amount doesn't exceed the remaining Next EMI capacity
    const maxEmiLimit = remainingNextEmiCapacity;
    if (value > maxEmiLimit) {
      showDebouncedToastError(`Next EMI cannot exceed the remaining capacity of ₹${formatCurrency(maxEmiLimit)}`);
      return;
    }
    
    // Handle overflow during typing: if value exceeds EMI limit, move excess to prepayment immediately
    if (value > maxEmiLimit && !manuallyEditedFields.has('prepayment')) {
      const overflow = value - maxEmiLimit;
      // Check if overflow would exceed total collection
      const currentAllocated = odCollection + currentCollection + chargeCollection + 
                              (advanceEmiCollection || 0) + (prepaymentCollection || 0) + (foreclosureCollection || 0);
      const potentialNewAllocated = currentAllocated - (advanceEmiCollection || 0) + maxEmiLimit + 
                                   ((prepaymentCollection || 0) + overflow);
      
      if (potentialNewAllocated > totalCollection) {
        // Calculate maximum allowable amount
        const maxAllowable = totalCollection - (currentAllocated - (advanceEmiCollection || 0) + (prepaymentCollection || 0));
        if (maxAllowable <= maxEmiLimit) {
          // Can only go up to EMI limit
          const limitedValue = Math.min(value, maxAllowable);
          setAdvanceEmiCollection(limitedValue);
          setAdvanceEmiDisplay(limitedValue.toString());
          toast.error(`Cannot exceed total collection amount of ₹${formatCurrency(totalCollection)}`);
          return;
        } else {
          // Allow partial overflow
          const partialOverflow = Math.min(overflow, maxAllowable - maxEmiLimit);
          setAdvanceEmiCollection(maxEmiLimit);
          setAdvanceEmiDisplay(maxEmiLimit.toString());
          setPrepaymentCollection((prepaymentCollection || 0) + partialOverflow);
          setPrepaymentDisplay(((prepaymentCollection || 0) + partialOverflow).toString());
          setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
          // toast(`₹${formatCurrency(partialOverflow)} moved to Prepayment as Next EMI cannot exceed ₹${formatCurrency(maxEmiLimit)}`, {
          //   icon: 'ℹ️',
          //   style: {
          //     background: '#d1ecf1',
          //     color: '#0c5460',
          //     border: '1px solid #bee5eb'
          //   }
          // });
          return;
        }
      }
      
      // Set Next EMI to limit and move overflow to prepayment
      setAdvanceEmiCollection(maxEmiLimit);
      setAdvanceEmiDisplay(maxEmiLimit.toString());
      setPrepaymentCollection((prepaymentCollection || 0) + overflow);
      setPrepaymentDisplay(((prepaymentCollection || 0) + overflow).toString());
      setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
      // `toast(`₹${formatCurrency(overflow)} moved to Prepayment as Next EMI cannot exceed ₹${formatCurrency(maxEmiLimit)}`, {
      //   icon: 'ℹ️',
      //   style: {
      //     background: '#d1ecf1',
      //     color: '#0c5460',
      //     border: '1px solid #bee5eb'
      //   }
      // });`
      return;
    }
    
    // Validate that the entered amount won't cause total allocation to exceed total collection
    const currentAllocated = odCollection + currentCollection + chargeCollection + 
                            (advanceEmiCollection || 0) + (prepaymentCollection || 0) + (foreclosureCollection || 0);
    const newAllocated = currentAllocated - (advanceEmiCollection || 0) + value;
    
    if (newAllocated > totalCollection) {
      showDebouncedToastError('You cannot manually add to Next EMI, try updating the total collected.');
      return;
    }    // Always allow typing, keep raw input - validation and formatting on blur
    setAdvanceEmiCollection(value);
    setAdvanceEmiDisplay(inputValue);
    
    // If user clears the field (empty input), immediately set to 0
    if (inputValue === '' || inputValue === '0') {
      setAdvanceEmiCollection(0);
    }
  };

  const handleAdvanceEmiFocus = (e) => {
    // Store the current value as previous for delta calculation
    previousNextEmiAmountRef.current = advanceEmiCollection || 0;
    
    if (!advanceEmiCollection || advanceEmiCollection === 0) {
      e.target.select(); // Select all text so typing replaces it
    }
    if (advanceEmiCollection === 0) {
      setAdvanceEmiDisplay(''); // Show empty field when value is 0
    } else {
      // For non-cash modes, show value with up to 2 decimal places
      if (collectionMode !== 'Cash Collection') {
        const formattedValue = Number(advanceEmiCollection).toFixed(2).replace(/\.?0+$/, '');
        setAdvanceEmiDisplay(formattedValue);
      } else {
        // For cash mode, round to integer to avoid floating point precision issues
        const roundedValue = Math.round(advanceEmiCollection);
        setAdvanceEmiDisplay(roundedValue.toString());
      }
    }
  };

  const handleAdvanceEmiBlur = (e) => {
    // If this blur is from a transfer operation, skip capping logic
    if (isNextEmiFromTransferRef.current) {
      isNextEmiFromTransferRef.current = false;
      // Just format the display
      if (advanceEmiCollection > 0) {
        // For non-cash modes, show value with up to 2 decimals but remove trailing zeros
        if (collectionMode !== 'Cash Collection') {
          const formattedValue = Number(advanceEmiCollection).toFixed(2).replace(/\.?0+$/, '');
          setAdvanceEmiDisplay(formattedValue);
        } else {
          // For cash mode, round to integer and format with commas
          const roundedValue = Math.round(advanceEmiCollection);
          setAdvanceEmiDisplay(formatCurrency(roundedValue));
        }
      } else {
        setAdvanceEmiDisplay('');
      }
      return;
    }
    
    // Store the previous Next EMI amount for comparison
    const previousNextEmiAmount = previousNextEmiAmountRef.current;
    const currentNextEmiAmount = advanceEmiCollection || 0;
    
    // Check remaining Next EMI capacity
    const remainingNextEmiCapacity = remainingDemand.nextEmi;
    
    // Use remaining capacity as the limit
    const maxEmiLimit = remainingNextEmiCapacity;
    
    // If there's no remaining capacity, clear any entered amount
    if (remainingNextEmiCapacity <= 0 && currentNextEmiAmount > 0) {
      setAdvanceEmiCollection(0);
      setAdvanceEmiDisplay('');
      toast.error('Next EMI has already been fully paid in previous payments');
      return;
    }
    
    // Check if this is manual entry that exceeds EMI limit and should overflow to prepayment
    // Note: Overflow is now handled in handleAdvanceEmiChange, so this is mainly for validation
    let finalNextEmiAmount = currentNextEmiAmount;
    let overflowToPrepayment = 0;
    
    // Only handle overflow if it wasn't already handled during change
    if (currentNextEmiAmount > maxEmiLimit) {
      overflowToPrepayment = currentNextEmiAmount - maxEmiLimit;
      finalNextEmiAmount = maxEmiLimit;
      
      // Validate that overflow to prepayment won't exceed total collection
      const currentAllocated = odCollection + currentCollection + chargeCollection + 
                              (advanceEmiCollection || 0) + (prepaymentCollection || 0) + (foreclosureCollection || 0);
      const newAllocatedAfterOverflow = currentAllocated - (advanceEmiCollection || 0) + finalNextEmiAmount + 
                                       ((prepaymentCollection || 0) + overflowToPrepayment);
      
      if (newAllocatedAfterOverflow > totalCollection) {
        // Calculate maximum allowable overflow
        const maxAllowableOverflow = totalCollection - (currentAllocated - (advanceEmiCollection || 0) + finalNextEmiAmount);
        if (maxAllowableOverflow <= 0) {
          // No overflow possible, limit to EMI amount
          finalNextEmiAmount = Math.min(currentNextEmiAmount, maxEmiLimit);
          overflowToPrepayment = 0;
          toast.error(`Cannot allocate more than total collection amount of ₹${formatCurrency(totalCollection)}`);
        } else {
          // Allow partial overflow
          overflowToPrepayment = Math.min(overflowToPrepayment, maxAllowableOverflow);
          finalNextEmiAmount = maxEmiLimit;
        }
      }
      
      // Limit Next EMI to max EMI amount
      setAdvanceEmiCollection(finalNextEmiAmount);
      // For non-cash modes, show value with up to 2 decimals but remove trailing zeros
      if (collectionMode !== 'Cash Collection') {
        const formattedValue = Number(finalNextEmiAmount).toFixed(2).replace(/\.?0+$/, '');
        setAdvanceEmiDisplay(formattedValue);
      } else {
        // For cash mode, round to integer and format with commas
        const roundedValue = Math.round(finalNextEmiAmount);
        setAdvanceEmiDisplay(formatCurrency(roundedValue));
      }
      
      // Add overflow to prepayment if not manually controlled and overflow is allowed
      if (!manuallyEditedFields.has('prepayment') && overflowToPrepayment > 0 && !hasShownOverflowMessage) {
        const newPrepaymentAmount = (prepaymentCollection || 0) + overflowToPrepayment;
        setPrepaymentCollection(newPrepaymentAmount);
        setPrepaymentDisplay(newPrepaymentAmount.toString());
        setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
        setHasShownOverflowMessage(true);
        toast(`₹${formatCurrency(overflowToPrepayment)} moved to Advance Payment as Next EMI cannot exceed ₹${formatCurrency(maxEmiLimit)}`, {
          icon: 'ℹ️',
          style: {
            background: '#d1ecf1',
            color: '#0c5460',
            border: '1px solid #bee5eb'
          }
        });
      } else if (overflowToPrepayment > 0 && manuallyEditedFields.has('prepayment')) {
        // Prepayment is manually controlled, show error
        toast.error(`Next EMI amount exceeds limit. Please adjust Advance Payment field first or reduce Next EMI amount.`);
        // Reset to previous valid amount
        setAdvanceEmiCollection(previousNextEmiAmount);
        // For non-cash modes, show value with up to 2 decimals but remove trailing zeros
        if (collectionMode !== 'Cash Collection') {
          const formattedValue = Number(previousNextEmiAmount).toFixed(2).replace(/\.?0+$/, '');
          setAdvanceEmiDisplay(previousNextEmiAmount > 0 ? formattedValue : '');
        } else {
          // For cash mode, round to integer and format with commas
          const roundedValue = Math.round(previousNextEmiAmount);
          setAdvanceEmiDisplay(previousNextEmiAmount > 0 ? formatCurrency(roundedValue) : '');
        }
        return;
      }
    } else if (currentNextEmiAmount > 0) {
      // For non-cash modes, show value with up to 2 decimals but remove trailing zeros
      if (collectionMode !== 'Cash Collection') {
        const formattedValue = Number(currentNextEmiAmount).toFixed(2).replace(/\.?0+$/, '');
        setAdvanceEmiDisplay(formattedValue);
      } else {
        // For cash mode, round to integer and format with commas
        const roundedValue = Math.round(currentNextEmiAmount);
        setAdvanceEmiDisplay(formatCurrency(roundedValue));
      }
    } else {
      setAdvanceEmiDisplay('');
      // If advance EMI is cleared (set to 0), keep it marked as manually edited to prevent auto-restoration
      setManuallyEditedFields(prev => new Set(prev).add('nextEmi'));
    }
  };

  const handlePrepaymentChange = (e) => {
    let inputValue = e.target.value;
    let value = 0;
    
    if (collectionMode === 'Cash Collection') {
      // For Cash mode: Remove all non-digits (including commas and decimals)
      const cleanValue = inputValue.replace(/,/g, ''); // Remove commas
      const integerPart = cleanValue.split('.')[0]; // Get only integer part before decimal
      inputValue = integerPart.replace(/[^0-9]/g, ''); // Keep only digits
      value = parseInt(inputValue) || 0;
    } else {
      // For non-cash modes: Allow decimals up to 2 places
      inputValue = inputValue.replace(/[^0-9.]/g, '');
      
      // Limit to 2 decimal places
      if (inputValue.includes('.')) {
        const parts = inputValue.split('.');
        if (parts[1] && parts[1].length > 2) {
          inputValue = parts[0] + '.' + parts[1].substring(0, 2);
        }
      }
      value = parseFloat(inputValue) || 0;
    }
    
    const currentAmount = prepaymentCollection || 0;
    
    // BLOCK ALL MANUAL INPUT - Prepayment is auto-calculated only
    // User cannot add, reduce, or clear prepayment manually
    if (value !== currentAmount) {
      if (value > currentAmount) {
        // User trying to INCREASE prepayment
        showDebouncedToastError("You cannot manually add to Prepayment. It will be automatically allocated from excess amounts.");
      } else if (value < currentAmount) {
        // User trying to DECREASE or CLEAR prepayment
        if (autoAllocatedFields.has('prepayment')) {
          showDebouncedToastError("You can't reduce or remove this amount as it was transferred from charges. Please edit the charge amount instead.");
        } else {
          showDebouncedToastError("You can't reduce Prepayment amount. Please update the total collected or redistribute the amount to other fields.");
        }
      }
      // RESTORE the original value - block all changes
      e.target.value = prepaymentDisplay || '';
      return;
    }
    
    // This code should never execute now, but keeping for safety
    // Mark this field as manually edited
    setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
    
    // Automatically switch to advance mode when user enters prepayment amount
    if (value > 0 && paymentToggle === 'foreclosure') {
      setPaymentToggle('advance');
      // Clear foreclosure field
      setForeclosureCollection(0);
    }
    
    // Always allow typing, keep raw input - validation and formatting on blur
    setPrepaymentCollection(value);
    setPrepaymentDisplay(inputValue);
    
    // If user clears the field (empty input), immediately set to 0
    if (inputValue === '' || inputValue === '0') {
      setPrepaymentCollection(0);
    }
  };

  const handlePrepaymentFocus = (e) => {
    // Store the current value as previous for delta calculation
    e.target.dataset.previousValue = prepaymentCollection || 0;
    
    if (!prepaymentCollection || prepaymentCollection === 0) {
      e.target.select(); // Select all text so typing replaces it
    }
    if (prepaymentCollection === 0) {
      setPrepaymentDisplay(''); // Show empty field when value is 0
    } else {
      // For non-cash modes, show value with up to 2 decimal places
      if (collectionMode !== 'Cash Collection') {
        const formattedValue = Number(prepaymentCollection).toFixed(2).replace(/\.?0+$/, '');
        setPrepaymentDisplay(formattedValue);
      } else {
        // For cash mode, round to integer to avoid floating point precision issues
        const roundedValue = Math.round(prepaymentCollection);
        setPrepaymentDisplay(roundedValue.toString());
      }
    }
  };

  const handlePrepaymentBlur = (e) => {
    // Store current and calculate previous amount for delta tracking
    const currentPrepaymentAmount = prepaymentCollection || 0;
    const previousPrepaymentAmount = parseFloat(e.target.dataset.previousValue || 0);
    
    // Format display value
    if (currentPrepaymentAmount > 0) {
      // For non-cash modes, show value with up to 2 decimals but remove trailing zeros
      if (collectionMode !== 'Cash Collection') {
        const formattedValue = Number(currentPrepaymentAmount).toFixed(2).replace(/\.?0+$/, '');
        setPrepaymentDisplay(formattedValue);
      } else {
        // For cash mode, round to integer and format with commas
        const roundedValue = Math.round(currentPrepaymentAmount);
        setPrepaymentDisplay(formatCurrency(roundedValue));
      }
    } else {
      setPrepaymentDisplay('');
      // If prepayment is cleared (set to 0), keep it marked as manually edited to prevent auto-restoration
      setManuallyEditedFields(prev => new Set(prev).add('prepayment'));
    }
  };

  const handleForeclosureChange = (e) => {
    let inputValue = e.target.value;
    let value = 0;
    
    if (collectionMode === 'Cash Collection') {
      // For Cash mode: Remove all non-digits (including commas and decimals)
      const cleanValue = inputValue.replace(/,/g, ''); // Remove commas
      const integerPart = cleanValue.split('.')[0]; // Get only integer part before decimal
      inputValue = integerPart.replace(/[^0-9]/g, ''); // Keep only digits
      value = parseInt(inputValue) || 0;
    } else {
      // For non-cash modes: Allow decimals up to 2 places
      inputValue = inputValue.replace(/[^0-9.]/g, '');
      
      // Limit to 2 decimal places
      if (inputValue.includes('.')) {
        const parts = inputValue.split('.');
        if (parts[1] && parts[1].length > 2) {
          inputValue = parts[0] + '.' + parts[1].substring(0, 2);
        }
      }
      value = parseFloat(inputValue) || 0;
    }
    
    // Automatically switch to foreclosure mode when user enters foreclosure amount
    if (value > 0 && paymentToggle === 'advance') {
      setPaymentToggle('foreclosure');
      // Clear advance payment fields
      setAdvanceEmiCollection(0);
      setPrepaymentCollection(0);
    }
    
    // Always allow typing, keep raw input - validation and formatting on blur
    setForeclosureCollection(value);
    setForeclosureDisplay(inputValue);
  };

  const handleForeclosureFocus = (e) => {
    if (!foreclosureCollection || foreclosureCollection === 0) {
      e.target.select(); // Select all text so typing replaces it
    }
    if (foreclosureCollection === 0) {
      setForeclosureDisplay(''); // Show empty field when value is 0
    } else {
      // For non-cash modes, show value with up to 2 decimal places
      if (collectionMode !== 'Cash Collection') {
        const formattedValue = Number(foreclosureCollection).toFixed(2).replace(/\.?0+$/, '');
        setForeclosureDisplay(formattedValue);
      } else {
        // For cash mode, round to integer to avoid floating point precision issues
        const roundedValue = Math.round(foreclosureCollection);
        setForeclosureDisplay(roundedValue.toFixed(2).replace(/\.?0+$/, ''));
      }
    }
  };

  const handleForeclosureBlur = (e) => {
    const remainingAmount = getRemainingAmount();
    const maxAllowed = remainingAmount + (foreclosureCollection || 0);
    
    // Validate and limit the amount on blur
    if (foreclosureCollection > maxAllowed) {
      setForeclosureCollection(maxAllowed);
      // For non-cash modes, show value with up to 2 decimals but remove trailing zeros
      if (collectionMode !== 'Cash Collection') {
        const formattedValue = Number(maxAllowed).toFixed(2).replace(/\.?0+$/, '');
        setForeclosureDisplay(formattedValue);
      } else {
        // For cash mode, round to integer and format with commas
        const roundedValue = Math.round(maxAllowed);
        setForeclosureDisplay(formatCurrency(roundedValue));
      }
    } else if (foreclosureCollection > 0) {
      // For non-cash modes, show value with up to 2 decimals but remove trailing zeros
      if (collectionMode !== 'Cash Collection') {
        const formattedValue = Number(foreclosureCollection).toFixed(2).replace(/\.?0+$/, '');
        setForeclosureDisplay(formattedValue);
      } else {
        // For cash mode, round to integer and format with commas
        const roundedValue = Math.round(foreclosureCollection);
        setForeclosureDisplay(formatCurrency(roundedValue));
      }
    } else {
      setForeclosureDisplay('');
    }
  };

  const handlePaymentToggle = (mode) => {
    setPaymentToggle(mode);
    // Reset values when switching modes
    if (mode === 'advance') {
      setForeclosureCollection(0);
    } else {
      setAdvanceEmiCollection(0);
      setPrepaymentCollection(0);
    }
  };

  // Helper function to clear manual field tracking when user wants auto-allocation
  const clearManualFieldTracking = (fieldName) => {
    setManuallyEditedFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });
  };

  const getRemainingAmount = () => {
    // Calculate the total that should be allocated based on remaining demands
    const demandBasedAllocation = remainingDemand.od + remainingDemand.current + remainingDemand.charges;
    
    // Calculate what has been actually collected in the core demand fields
    const coreCollected = odCollection + currentCollection + chargeCollection;
    
    // Calculate excess amount beyond core demands
    const excessAmount = totalCollection - demandBasedAllocation;
    
    if (excessAmount <= 0) {
      return 0; // No excess, so no remaining
    }
    
    // Calculate what has been allocated to advance fields
    const advanceAllocated = (advanceEmiCollection || 0) + (prepaymentCollection || 0) + (foreclosureCollection || 0);
    
    // The remaining is the excess minus what's been allocated to advance fields
    const remaining = excessAmount - advanceAllocated;
    
    // Return 0 if remaining amount is negative
    return Math.max(0, remaining);
  };

  const isAdditionalPaymentAvailable = () => {
    const allocation = calculateAllocation(totalCollection);
    return allocation.remaining > 0;
  };

  const handleRemarksChange = (e) => {
    setRemarks(e.target.value);
  };

  // Speech recognition handlers
  const startListening = () => {
    if (!speechSupported) {
      toast.error('Speech recognition is not supported in this browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US'; // You can change this to 'hi-IN' for Hindi

    recognition.onstart = () => {
      setIsListening(true);
      toast.success('Listening... Speak now');
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setRemarks(prevRemarks => prevRemarks + (prevRemarks ? ' ' : '') + transcript);
      toast.success('Speech captured successfully');
    };

    recognition.onerror = (event) => {
      // Speech recognition error occurred
      toast.error('Speech recognition failed. Please try again.');
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };

  const stopListening = () => {
    setIsListening(false);
  };

  // Helper function to parse currency input (removes formatting)
  const parseCurrencyInput = (value) => {
    if (!value) return 0;
    // Remove all non-numeric characters except decimal point
    const numericValue = value.toString().replace(/[^0-9.]/g, '');
    return parseFloat(numericValue) || 0;
  };

  // Helper function to format currency for display while typing
  const formatForDisplay = (value) => {
    if (!value || value === 0) return '';
    return formatCurrency(value);
  };

  const handleMobileNumberChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 10) {
      setMobileNumber(value);
    }
  };

  // Handle Payment Link Success - User confirms payment received
  const handlePaymentLinkSuccess = async () => {
    console.log('✅ Payment Link Success confirmed by user');
    
    // STEP 1: Close the Payment Link Waiting Modal immediately
    setShowPaymentWaitingModal(false);
    clearInterval(countdownIntervalRef.current);
    setWaitingCountdown(300);
    
    // STEP 2: Show loading toast - this will stay until we get API response and check difference
    const loadingToastId = toast.loading('⏳ Verifying payment with backend... Please wait 30 seconds...', {
      duration: Infinity, // Keep showing until we manually dismiss it
      position: 'top-center'
    });
    
    try {
      // STEP 3: Wait 30 seconds FIRST for backend to process the payment
      // Backend needs time to process the UPI payment before we can verify it
      console.log('⏳ STEP 3: Waiting 30 seconds for backend to process payment...');
      await new Promise(resolve => setTimeout(resolve, 30000)); // Wait 30 seconds for backend
      console.log('✅ STEP 3 COMPLETE: 30-second wait finished, backend should have processed payment');
      
      // STEP 4: NOW call onDataRefresh API to get the updated data from backend
      console.log('🔄 STEP 4: Calling onDataRefresh API to get latest data from backend...');
      
      // CRITICAL: We MUST have onDataRefresh to verify payment
      if (!onDataRefresh || typeof onDataRefresh !== 'function') {
        console.error('❌ CRITICAL ERROR: onDataRefresh function is not available!');
        toast.dismiss(loadingToastId);
        toast.error('Unable to verify payment: System error. Please contact support.', { duration: 7000 });
        setPaymentLinkData(null);
        return; // STOP here - cannot verify without API call
      }
      
      // Call the API and WAIT for it to complete - it should return the fresh data
      console.log('⏳ Waiting for API to fetch fresh household data...');
      const freshData = await onDataRefresh(); // ⏳ WAIT for the API call to complete and get fresh data
      console.log('✅ STEP 4 COMPLETE: onDataRefresh API call completed');
      console.log('📦 Fresh data received from API:', freshData);
      console.log('📦 Current items prop for comparison:', items);
      
      // STEP 5: Extract updated demand values from the API response directly
      // Use freshData if available (returned from API), otherwise fall back to items prop
      console.log('🔍 STEP 5: Extracting updated demand from API response...');
      
      const dataSource = (freshData && typeof freshData === 'object') ? freshData : items;
      console.log('📊 Data source:', { type: (freshData && typeof freshData === 'object') ? 'FRESH_DATA' : 'ITEMS_PROP', Balance: dataSource && dataSource.Balance, tot_overdue: dataSource && dataSource.tot_overdue });
      
      const updatedOverdueDemand = parseFloat(dataSource && dataSource.tot_overdue || 0);
      const updatedCurrentDemand = parseFloat((dataSource && dataSource.Balance || 0) - (dataSource && dataSource.tot_overdue || 0));
      const updatedChargeDue = Math.max(0, parseFloat(dataSource && dataSource.Charges_os || 0));
      const updatedTotalDemand = Math.max(0, parseFloat(dataSource && dataSource.Balance || 0)) + Math.max(0, parseFloat(dataSource && dataSource.Charges_os || 0));
      
      const demandAfterPayment = {
        od: Math.abs(updatedOverdueDemand),
        current: updatedCurrentDemand,
        charges: updatedChargeDue,
        total: updatedTotalDemand
      };
      
      console.log('📊 Demand BEFORE payment (stored in state):', remainingDemand);
      console.log('📊 Demand AFTER payment (from API response):', demandAfterPayment);
      console.log('✅ STEP 5 COMPLETE: Demand values extracted');
      
      // STEP 6: NOW check the difference - did demand actually reduce?
      console.log('🔍 STEP 6: Comparing demand before vs after payment...');
      
      const demandReduced = demandAfterPayment.total < remainingDemand.total;
      const demandReduction = remainingDemand.total - demandAfterPayment.total;
      
      console.log(`📊 Demand comparison result: Before=${remainingDemand.total}, After=${demandAfterPayment.total}, Difference=${demandReduction}, Reduced=${demandReduced}`);
      console.log('✅ STEP 6 COMPLETE: Demand comparison finished');
      
      // STEP 7: Dismiss loading toast now that we have checked the difference
      console.log('🔄 STEP 7: Dismissing loading toast...');
      toast.dismiss(loadingToastId);
      console.log('✅ STEP 7 COMPLETE: Loading toast dismissed');
      
      // STEP 8: Show appropriate message based on verification result
      console.log('🔍 STEP 8: Showing verification result to user...');
      
      if (!demandReduced || demandReduction <= 0) {
        // ❌ PAYMENT NOT VERIFIED - Demand didn't reduce (no difference found)
        console.error('⚠️ VERIFICATION FAILED: Demand did not reduce. Payment may not have been completed.');
        console.error('⚠️ This error is shown AFTER API response and difference check');
        
        // Clear payment link data
        setPaymentLinkData(null);
        
        // Show error to user - THIS HAPPENS AFTER API CALL AND DIFFERENCE CHECK
        toast.error(
          'Payment verification failed! Customer may not have completed the payment. Please verify manually before confirming.',
          { duration: 7000 }
        );
        
        console.log('❌ STEP 8 COMPLETE: Error message shown to user');
        return; // DON'T proceed with payment history or success modal
      }
      
      // ✅ PAYMENT VERIFIED - Demand reduced successfully (difference found)
      console.log(`✅ VERIFICATION SUCCESS: Payment verified! Demand reduced by ₹${demandReduction}`);
      
      // Add payment to history
      if (paymentLinkData) {
        const paymentData = {
          mode: 'Payment Link',
          amount: totalCollection,
          allocation: {
            od: odCollection,
            current: currentCollection,
            charges: chargeCollection,
            nextEmi: advanceEmiCollection,
            prepayment: prepaymentCollection,
            total: totalCollection
          },
          transactionId: paymentLinkData.short_url
        };
        
        console.log('📝 Adding verified Payment Link to history:', paymentData);
        addPaymentToHistory(paymentData);
        
        // Set success message and transaction ID
        setSuccessMessage(paymentLinkData.message || 'Payment verified and confirmed successfully!');
        setTransactionId(paymentLinkData.short_url);
        
        // Show success modal
        setShowSuccessModal(true);
      }
      
      setPaymentLinkData(null);
      toast.success(`Payment verified! Demand reduced by ₹${demandReduction}`, { duration: 5000 });
      console.log('✅ STEP 8 COMPLETE: Success modal shown to user');
      
    } catch (error) {
      console.error('❌ ERROR in payment verification flow:', error);
      toast.dismiss(loadingToastId); // Dismiss loading toast on error
      toast.error(`Failed to verify payment: ${error.message}. Please verify manually.`, { duration: 5000 });
      
      // Clear payment link data
      setPaymentLinkData(null);
    }
  };

  // Handle Payment Link Fail/Cancel - User confirms payment failed or cancelled
  const handlePaymentLinkFail = () => {
    console.log('❌ Payment Link Failed/Cancelled by user');
    
    // Close waiting modal
    setShowPaymentWaitingModal(false);
    clearInterval(countdownIntervalRef.current);
    setWaitingCountdown(300); // Reset countdown
    
    // Clear payment link data
    setPaymentLinkData(null);
    
    // Do NOT update payment history
    // Do NOT update remaining demand
    // Just reset the form for next attempt
    
    toast.error('Payment cancelled. No changes made to demand.');
    
    // Reset form fields
    setTotalCollection(0);
    setTotalCollectionDisplay('');
    setOdCollection(0);
    setCurrentCollection(0);
    setChargeCollection(0);
    setChargeCollectionDisplay('');
    setAdvanceEmiCollection(0);
    setAdvanceEmiDisplay('');
    setPrepaymentCollection(0);
    setPrepaymentDisplay('');
    setRemarks('');
    
    console.log('🔄 Form reset after payment link cancellation');
  };

  const isValidMobile = (mobile) => {
    return /^[0-9]{10}$/.test(mobile);
  };

  // Function to validate if submit button should be enabled
  const isSubmitEnabled = () => {
    // Basic validation: total collection must be greater than 0
    // Payment mode validation will be shown in toast when user clicks the button
    if (!totalCollection || totalCollection <= 0) {
      return false;
    }

    // Mode-specific validations - only check if a mode is already selected
    if (collectionMode === 'Payment Link') {
      if (!mobileNumber || !isValidMobile(mobileNumber)) {
        return false;
      }
    }

    if (collectionMode === 'Cheque') {
      if (!chequeFile || !issuerName.trim() || !chequeDate || !chequeNumber.trim()) {
        return false;
      }
    }

    // Check if total allocated amount matches collection amount
    // Always include foreclosure and foreclosure charges in total allocation calculation
    const totalAllocated = odCollection + currentCollection + chargeCollection +
                          (advanceEmiCollection || 0) + (prepaymentCollection || 0) + 
                          (foreclosureCollection || 0) + (foreclosureCharges || 0);

    // Skip allocation check during charge transfers to prevent timing issues
    if (!isChargeTransferRef.current && Math.abs(totalAllocated - totalCollection) > 0.01) {
      return false;
    }
    return true;
  };  const handleChequeFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'application/pdf' || file.type === 'image/png')) {
      // Clean up previous preview URL if it exists
      if (chequePreview && chequePreview.startsWith('data:')) {
        // Data URLs are automatically garbage collected, but we can clear the state
        setChequePreview(null);
      }
      
      setChequeFile(file);
      setOcrError(''); // Clear any previous OCR errors
      
      // Create preview for images only
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setChequePreview(e.target.result);
        };
        reader.onerror = () => {
          // FileReader error occurred
          setOcrError('Failed to read file');
        };
        reader.readAsDataURL(file);
        
        // Process OCR for image files
        processChequeOCR(file);
      } else {
        setChequePreview(null); // For PDF files
        // Note: OCR might not work for PDF files depending on your backend
        // You can choose to process or skip OCR for PDFs
        processChequeOCR(file);
      }
    } else {
      toast.error('Please upload only JPG, PNG, or PDF files');
      e.target.value = '';
    }
  };

  const handleIssuerNameChange = (e) => {
    setIssuerName(e.target.value);
  };

  const handleChequeDateChange = (e) => {
    setChequeDate(e.target.value);
  };

  const handleChequeNumberChange = (e) => {
    setChequeNumber(e.target.value);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    setChequeFile(null);
    setChequePreview(null);
    document.getElementById('chequeFile').value = '';
    setShowDeleteConfirm(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  // OCR Processing Function
  const processChequeOCR = async (file) => {
    setIsProcessingOCR(true);
    setOcrError('');

    try {
      const api_token = localStorage.getItem("api_token");
      const formdata = new FormData();
      formdata.append("file", file);
      let generate_Id = generateId(10);
      let base_url = process.env.REACT_APP_URL;
      
      const response = await customFetch(base_url + "/get/cheque/details", {
        method: "POST",
        headers: {
          "api-token": api_token,
          component_id: generate_Id1,
          apiId: generate_Id,
          api_group_id: "",
          component: "CollectionModal",
          user_id: localStorage.getItem("in_userid"),
        },
        body: formdata,
      });

      if (response.headers.get("api_code") > 0) {
        throw new Error(response.headers.get("api_error_message") || "OCR processing failed");
      }

      const data = await response.json();
      
      if (data && data.data && data.data[0]) {
        // Auto-fill the form fields with OCR data
        setIssuerName(data.data[0].name_of_issuer || '');
        setChequeDate(data.data[0].chq_date
          ? moment(data.data[0].chq_date, "DD-MM-YYYY").format("YYYY-MM-DD")
          : '');
        setChequeNumber(data.data[0].chq_no || '');
        
        // Auto-fill amount if not already set
        if (!totalCollection && data.data[0].chq_amount) {
          setTotalCollection(parseFloat(data.data[0].chq_amount) || 0);
        }
      } else {
        setOcrError("No cheque details found in the image. Please enter details manually.");
      }
    } catch (error) {
      setOcrError("Failed to process cheque image. Please enter details manually.");
    } finally {
      setIsProcessingOCR(false);
    }
  };

  const toggleAdvanceEmiTooltip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowAdvanceEmiTooltip(!showAdvanceEmiTooltip);
  };

  const toggleFcmTooltip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowFcmTooltip(!showFcmTooltip);
  };

  const toggleFcmTooltipForeclosure = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowFcmTooltipForeclosure(!showFcmTooltipForeclosure);
  };

  const togglePrepaymentTooltip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowForeclosureTooltip(false);
    setShowPrepaymentTooltip(!showPrepaymentTooltip);
  };

  const toggleForeclosureTooltip = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPrepaymentTooltip(false);
    setShowForeclosureTooltip(!showForeclosureTooltip);
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(items.HH_ID || '');
    toast.success('Household ID copied to clipboard!');
  };

  const handleCall = () => {
    window.location.href = `tel:${items.Household_mobile_no || ''}`;
  };

  const handleClose = () => {
    // Reset all collection input fields to prevent data persistence between modal sessions
    setTotalCollection(0);
    setTotalCollectionDisplay('');
    setOdCollection(0);
    setCurrentCollection(0);
    setChargeCollection(0);
    setChargeCollectionDisplay('');
    setAdvanceEmiCollection(0);
    setAdvanceEmiDisplay('');
    setPrepaymentCollection(0);
    setPrepaymentDisplay('');
    setForeclosureCollection(0);
    setForeclosureDisplay('');
    setRemarks('');
    setManuallyEditedFields(new Set());
    setAutoAllocatedFields(new Set());
    isChargeTransferRef.current = false;
    isNextEmiFromTransferRef.current = false;
    setHasShownOverflowMessage(false); // Reset overflow message flag
    
    // Reset foreclosure charges
    setForeclosureCharges(0);
    setNetForeclosureAmount(0);
    setShowForeclosureCharges(false);
    
    // Reset original total collection tracking
    setOriginalTotalCollection(0);
    setOriginalAllocation({
      od: 0,
      current: 0,
      charges: 0,
      nextEmi: 0,
      prepayment: 0
    });
    setHasUserEnteredTotal(false);

    // Close the modal - you can pass an onClose prop from parent
    if (typeof onClose === 'function') {
      onClose();
    } else {
      // onClose prop is not a function or not provided
    }
  };

  const handleGoToDashboard = () => {
    // Navigate to dashboard
    history.push({
      pathname: "/dashboard",
      state: { api_token: api_token },
    });
  };

  // Direct submit function for new UI - shows confirmation modal first
  const handleDirectSubmit = async (e) => {
    e.preventDefault();
    
    // Validate payment mode is selected
    if (!collectionMode || collectionMode.trim() === '') {
      toast.error('Please select the payment mode.');
      return;
    }
    
    if (!totalCollection || totalCollection <= 0) {
      toast.error('Please enter a valid collection amount');
      return;
    }

    // Validate using the same smart logic as input handlers
    // if (totalCollection <= totalDemand) {
    //   // Regular payment within demand - allowed
    // } else if (foreclosureAmount > 0 && totalCollection <= foreclosureAmount) {
    //   // Advance payment within loan outstanding - allowed
    // } else {
    //   // Payment exceeds all limits - show error and return
    //   if (foreclosureAmount > 0 && foreclosureAmount >= totalDemand) {
    //     toast.error(`Collection amount cannot exceed loan outstanding amount of ₹${formatCurrency(foreclosureAmount)}`);
    //   } else if (totalDemand > 0) {
    //     toast.error(`Collection amount cannot exceed total demand amount of ₹${formatCurrency(totalDemand)}`);
    //   } else {
    //     toast.error('Collection amount exceeds allowed limits');
    //   }
    //   return;
    // }

    // Validate mobile number for Payment Link mode
    if (collectionMode === 'Payment Link') {
      if (!mobileNumber || !isValidMobile(mobileNumber)) {
        toast.error('Please enter a valid 10-digit mobile number');
        return;
      }
    }

    // Validate cheque details for Cheque mode
    if (collectionMode === 'Cheque') {
      if (!chequeFile) {
        toast.error('Please upload cheque image or PDF');
        return;
      }
      if (!issuerName.trim()) {
        toast.error('Please enter issuer name');
        return;
      }
      if (!chequeDate) {
        toast.error('Please select cheque date');
        return;
      }
      if (!chequeNumber.trim()) {
        toast.error('Please enter cheque number');
        return;
      }
    }

    // Check if total allocated amount matches collection amount
    // Always include foreclosure and foreclosure charges in total allocation calculation
    const totalAllocated = odCollection + currentCollection + chargeCollection + 
                          (advanceEmiCollection || 0) + (prepaymentCollection || 0) + 
                          (foreclosureCollection || 0) + (foreclosureCharges || 0);

    console.log('Payment allocation debug:', {
      totalCollection,
      totalAllocated,
      odCollection,
      currentCollection,
      chargeCollection,
      advanceEmiCollection,
      prepaymentCollection,
      foreclosureCollection,
      foreclosureCharges,
      paymentToggle
    }); // Debug log

    if (Math.abs(totalAllocated - totalCollection) > 0.01) {
      toast.error('Total allocated amount does not match collection amount');
      return;
    }

    // Show confirmation modal before proceeding
    setConfirmationMode(collectionMode);
    setShowConfirmationModal(true);
  };

  // Function to handle confirmed submission after user clicks OK
  const handleConfirmedSubmit = async () => {
    // Close confirmation modal
    setShowConfirmationModal(false);

    // Call appropriate API based on collection mode
    let result;
    try {
      if (confirmationMode === 'Cash Collection') {
        result = await handleCashCollection();
      } else if (confirmationMode === 'Payment Link') {
        result = await handlePaymentLink();
      } else if (confirmationMode === 'Cheque') {
        result = await handleChequeCollection();
      }

      if (result) {
        console.log('💳 Payment result received:', result);
        console.log('💳 Collection mode:', collectionMode);
        
        // Handle different types of success responses
        if (result.txn_ref_no) {
          // Cash collection success
          console.log('💰 Cash collection - txn_ref_no:', result.txn_ref_no);
          setTransactionId(result.txn_ref_no);
          
          // Add payment to history for multi-mode support
          const paymentData = {
            mode: 'Cash Collection',
            amount: totalCollection,
            allocation: {
              od: odCollection || 0,
              current: currentCollection || 0,
              charges: chargeCollection || 0,
              nextEmi: advanceEmiCollection || 0,
              prepayment: prepaymentCollection || 0,
              total: totalCollection
            },
            transactionId: result.txn_ref_no
          };
          addPaymentToHistory(paymentData);
          
          let successMsg = result.message || 'Cash collection processed successfully';
          setSuccessMessage(successMsg);
          setShowSuccessModal(true);
        } else if (result.document_id) {
          // Cheque collection success
          console.log('📝 Cheque collection - document_id:', result.document_id);
          setTransactionId(result.document_id);
          
          // Add payment to history for multi-mode support
          const paymentData = {
            mode: 'Cheque',
            amount: totalCollection,
            allocation: {
              od: odCollection || 0,
              current: currentCollection || 0,
              charges: chargeCollection || 0,
              nextEmi: advanceEmiCollection || 0,
              prepayment: prepaymentCollection || 0,
              total: totalCollection
            },
            transactionId: result.document_id
          };
          console.log('📝 Adding Cheque payment to history:', paymentData);
          addPaymentToHistory(paymentData);
          
          let successMsg = result.message || 'Cheque submitted successfully';
          setSuccessMessage(successMsg);
          setShowSuccessModal(true);
        } else if (result.short_url) {
          // Payment link success - DO NOT show success modal yet
          // Waiting modal is already shown by handlePaymentLink()
          // User needs to confirm payment status first
          console.log('📱 Payment link generated - waiting for user confirmation');
          // Don't add to payment history yet
          // Don't show success modal yet
          // The waiting modal is already displayed
        } else if (result.success !== false) {
          // Generic success
          setTransactionId('SUCCESS');
          
          // Add payment to history for multi-mode support
          const paymentData = {
            mode: collectionMode,
            amount: totalCollection,
            allocation: {
              od: odCollection,
              current: currentCollection,
              charges: chargeCollection,
              nextEmi: advanceEmiCollection,
              prepayment: prepaymentCollection,
              total: totalCollection
            },
            transactionId: 'SUCCESS'
          };
          addPaymentToHistory(paymentData);
          
          let successMsg = 'Collection processed successfully';
          setSuccessMessage(successMsg);
          setShowSuccessModal(true);
        } else {
          toast.error(result.error || 'Failed to process collection');
        }
      } else {
        toast.error('No response received from server');
      }
    } catch (error) {
      console.error('Collection processing error:', error); // Debug log
      // Collection processing error occurred
      if (error.message) {
        toast.error(`An error occurred while processing the collection: ${error.message}`);
      } else {
        toast.error('An error occurred while processing the collection');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to handle cancel on confirmation modal
  const handleConfirmationCancel = () => {
    setShowConfirmationModal(false);
    setConfirmationMode('');
  };

  // Helper function to deactivate payment link
  const deactivatePaymentLink = async () => {
    try {
      const uid = localStorage.getItem("in_userid");
      let base_url = process.env.REACT_APP_URL;
      if (items.serverflag === "RF") {
        base_url = process.env.REACT_APP_R_URL;
      }

      const deactivatePayload = {
        household_id: items.household_id
      };

      let generate_Id = generateId(10);
      await customFetch(
        base_url + "/deactivate/payment/link",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "api-token": apiToken,
            component_id: generate_Id1,
            apiId: generate_Id,
            api_group_id: "",
            component: "CollectionModal",
            user_id: uid,
          },
          body: JSON.stringify(deactivatePayload)
        }
      );
      console.log('✅ Payment link deactivated for household:', items.household_id);
    } catch (error) {
      console.error('❌ Error deactivating payment link:', error);
      // Continue with collection even if deactivate fails
    }
  };

  // API Functions
  const handleCashCollection = async () => {
    setIsSubmitting(true);
    const uid = localStorage.getItem("in_userid");
    if (!uid) {
      setIsSubmitting(false);
      return { success: false, error: "Session expired. Please login again." };
    }

    // Call deactivate payment link API before processing cash collection
    await deactivatePaymentLink();

    // For Cash Collection, round all amounts to integers
    const roundedTotalCollection = collectionMode === 'Cash Collection' ? Math.round(totalCollection) : totalCollection;
    const roundedOdCollection = collectionMode === 'Cash Collection' ? Math.round(odCollection || 0) : (odCollection || 0);
    const roundedCurrentCollection = collectionMode === 'Cash Collection' ? Math.round(currentCollection || 0) : (currentCollection || 0);
    const roundedChargeCollection = collectionMode === 'Cash Collection' ? Math.round(chargeCollection || 0) : (chargeCollection || 0);
    const roundedAdvanceEmiCollection = collectionMode === 'Cash Collection' ? Math.round(advanceEmiCollection || 0) : (advanceEmiCollection || 0);
    const roundedPrepaymentCollection = collectionMode === 'Cash Collection' ? Math.round(prepaymentCollection || 0) : (prepaymentCollection || 0);
    const roundedNetForeclosureAmount = collectionMode === 'Cash Collection' ? Math.round(netForeclosureAmount || 0) : (netForeclosureAmount || 0);
    const roundedForeclosureCharges = collectionMode === 'Cash Collection' ? Math.round(foreclosureCharges || 0) : (foreclosureCharges || 0);

    const formdata = new FormData();
    formdata.append("in_household_id", items.household_id);
    formdata.append("in_amount_collected", roundedTotalCollection);
    formdata.append("in_charges_collected", items.Charges_os || 0);
    formdata.append("in_collection_mode_flag", "C (Cash)");
    formdata.append("in_reference_number", "");
    formdata.append("in_remark", remarks || "Cash collection via CollectionModal");
    formdata.append("in_userid", uid);
    formdata.append("in_collection_type", "P");
    formdata.append("csf", items.csf || "");
    formdata.append("product_id", process.env.REACT_APP_PRODUCTID || "");
    formdata.append("payment_type", "collection");
    
    // Add collection allocation values (all rounded for Cash mode)
    formdata.append("odCollection", roundedOdCollection);
    formdata.append("currentCollection", roundedCurrentCollection);
    formdata.append("chargeCollection", roundedChargeCollection);
    formdata.append("advanceEmiCollection", roundedAdvanceEmiCollection);
    formdata.append("prepaymentCollection", roundedPrepaymentCollection);
    formdata.append("prepaymentCount", maxPrepaymentEmis || 2);
    formdata.append("foreclosureCollection", roundedNetForeclosureAmount);
    formdata.append("foreclosureCharges", roundedForeclosureCharges); // NEW: 4% of foreclosure
    formdata.append("netForeclosureAmount", roundedNetForeclosureAmount); // NEW: 96% applied to POS

    let generate_Id = generateId(10);
    const requestOptions = {
      method: "POST",
      headers: {
        "api-token": apiToken,
        component_id: generate_Id1,
        apiId: generate_Id,
        api_group_id: "",
        component: "CollectionModal",
        user_id: uid,
      },
      body: formdata,
    };

    try {
      let base_url = process.env.REACT_APP_URL;
      if (items.serverflag === "RF") {
        base_url = process.env.REACT_APP_R_URL;
      }

      const response = await customFetch(
        base_url + "/set/collection/household/customer",
        requestOptions
      );

      if (response.headers.get("api_code") > 0) {
        toast.error(response.headers.get("api_error_message"));
        return;
      }

      const data = await response.json();
      // Cash collection response received

      if (data.api_code === 4 || data.api_code === 3 || data.api_code === 5) {
        localStorage.clear();
        window.location.href = "/";
        return;
      }

      if (data.txn_ref_no) {
        // Cash collection successful, returning raw response
        // NOTE: Do NOT add to payment history here - ConfirmCashCollection will handle it
        // to avoid duplicate entries
        
        // Return the raw API response to match what ConfirmCashCollection expects
        return data;
      } else {
        // No transaction reference in response
        return { 
          success: false, 
          error: "Collection completed but no transaction reference received" 
        };
      }
    } catch (error) {
      // Cash collection error occurred
      return { 
        success: false, 
        error: "Failed to process cash collection. Please try again." 
      };
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChequeCollection = async () => {
    setIsSubmitting(true);
    const uid = localStorage.getItem("in_userid");
    if (!uid) {
      setIsSubmitting(false);
      return { success: false, error: "Session expired. Please login again." };
    }

    try {
      let base_url = process.env.REACT_APP_URL;
      if (items.serverflag === "RF") {
        base_url = process.env.REACT_APP_R_URL;
      }

      // First upload the cheque file
      const uploadFormdata = new FormData();
      uploadFormdata.append("file", chequeFile);
      uploadFormdata.append("blob_url", "cheque_collection_by_cso/" + chequeFile.name);

      let generate_Id = generateId(10);

      const uploadResponse = await customFetch(
        base_url + "/save/files/bucket",
        {
          method: "POST",
          body: uploadFormdata,
          headers: {
            "api-token": apiToken,
            component_id: generate_Id1,
            apiId: generate_Id,
            api_group_id: generate_Id1,
            component: "CollectionModal",
            user_id: uid,
          },
        }
      );

      // Check for HTTP errors first
      if (uploadResponse.headers.get("api_code") > 0) {
        const errorMessage = uploadResponse.headers.get("api_error_message") || "File upload failed";
        // File upload API error occurred
        setIsSubmitting(false);
        return { success: false, error: errorMessage };
      }

      const uploadData = await uploadResponse.json();
      // File upload response received

      // Handle the actual API response structure - it's an array with blob_url
      if (!uploadData || !Array.isArray(uploadData) || uploadData.length === 0 || !uploadData[0].blob_url) {
        setIsSubmitting(false);
        return { success: false, error: "Failed to upload cheque file" };
      }

      // Then process the cheque collection
      const collectionFormdata = new FormData();
      collectionFormdata.append("household_id", items.household_id);
      collectionFormdata.append("amount_collected", totalCollection);
      collectionFormdata.append("charges_collected", items.Charges_os || 0);
      collectionFormdata.append("collection_mode_flag", "Q (Cheque)");
      collectionFormdata.append("reference_number", chequeNumber);
      collectionFormdata.append("remark", remarks || "Cheque collection via CollectionModal");
      collectionFormdata.append("filename", uploadData[0].blob_url);
      collectionFormdata.append("issuer_name", issuerName);
      collectionFormdata.append("cheque_date", chequeDate);
      collectionFormdata.append("cheque_number", chequeNumber);
      collectionFormdata.append("in_userid", uid);
      collectionFormdata.append("collection_type", "P");
      collectionFormdata.append("csf", items.csf || "");
      collectionFormdata.append("product_id", process.env.REACT_APP_PRODUCTID || "");
      collectionFormdata.append("payment_type", "collection");

      // Add collection allocation values
      collectionFormdata.append("odCollection", odCollection || 0);
      collectionFormdata.append("currentCollection", currentCollection || 0);
      collectionFormdata.append("chargeCollection", chargeCollection || 0);
      collectionFormdata.append("advanceEmiCollection", advanceEmiCollection || 0);
      collectionFormdata.append("prepaymentCollection", prepaymentCollection || 0);
      collectionFormdata.append("prepaymentCount", maxPrepaymentEmis || 2);
      collectionFormdata.append("foreclosureCollection", netForeclosureAmount || 0);
      collectionFormdata.append("foreclosureCharges", foreclosureCharges || 0); // NEW: 4% of foreclosure
      collectionFormdata.append("netForeclosureAmount", netForeclosureAmount || 0); // NEW: 96% applied to POS

      const collectionResponse = await customFetch(
        base_url + "/collection/cheque/set",
        {
          method: "POST",
          headers: {
            "api-token": apiToken,
            component_id: generate_Id1,
            apiId: generateId(10),
            api_group_id: generate_Id1,
            component: "CollectionModal",
            user_id: uid,
          },
          body: collectionFormdata,
        }
      );

      // Check for HTTP errors first
      if (collectionResponse.headers.get("api_code") > 0) {
        const errorMessage = collectionResponse.headers.get("api_error_message") || "Cheque collection failed";
        // Cheque collection API error occurred
        setIsSubmitting(false);
        return { success: false, error: errorMessage };
      }

      const collectionData = await collectionResponse.json();
      // Cheque collection response received

      if (collectionData.api_code === 4 || collectionData.api_code === 3 || collectionData.api_code === 5) {
        localStorage.clear();
        window.location.href = "/";
        return;
      }

      if (collectionData.document_id) {
        // Cheque collection successful, returning raw response
        // Return the raw API response to match what ConfirmCashCollection expects
        return collectionData;
      } else {
        // No document_id in cheque response
        return { 
          success: false, 
          error: "Cheque collection completed but no transaction reference received" 
        };
      }
    } catch (error) {
      // Cheque collection error occurred
      return { 
        success: false, 
        error: "Failed to process cheque collection. Please try again." 
      };
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentLink = async () => {
    setIsSubmitting(true);
    const uid = localStorage.getItem("in_userid");
    if (!uid) {
      setIsSubmitting(false);
      return { success: false, error: "Session expired. Please login again." };
    }

    const formdata = new FormData();
    formdata.append("household_id", items.household_id);
    formdata.append("mobile_no", mobileNumber);
    formdata.append("amount", totalCollection);
    formdata.append("created_by_id", uid);
    formdata.append("created_mode", "A");
    formdata.append("csf", items.csf || "");
    formdata.append("remark", remarks || "Payment Link collection via CollectionModal");
    formdata.append("product_id", process.env.REACT_APP_PRODUCTID);
    formdata.append("payment_type", "collection");

    // Add collection allocation values
    formdata.append("odCollection", odCollection || 0);
    formdata.append("currentCollection", currentCollection || 0);
    formdata.append("chargeCollection", chargeCollection || 0);
    formdata.append("advanceEmiCollection", advanceEmiCollection || 0);
    formdata.append("prepaymentCollection", prepaymentCollection || 0);
    formdata.append("prepaymentCount", maxPrepaymentEmis || 2);
    formdata.append("foreclosureCollection", netForeclosureAmount || 0);
    formdata.append("foreclosureCharges", foreclosureCharges || 0); // NEW: 4% of foreclosure
    formdata.append("netForeclosureAmount", netForeclosureAmount || 0); // NEW: 96% applied to POS

    let generate_Id = generateId(10);

    try {
      let base_url = process.env.REACT_APP_URL;
      if (items.serverflag === "RF") {
        base_url = process.env.REACT_APP_R_URL;
      }

      const response = await customFetch(
        base_url + "/create/upi/payment/link/v2",
        {
          method: "POST",
          headers: {
            "api-token": apiToken,
            component_id: generate_Id1,
            apiId: generate_Id,
            api_group_id: generate_Id1,
            component: "CollectionModal",
            user_id: uid,
          },
          body: formdata,
        }
      );

      const data = await response.json();
      // Payment link response received

      if (data.api_code === 4 || data.api_code === 3 || data.api_code === 5) {
        localStorage.clear();
        window.location.href = "/";
        return;
      }

      if (data.short_url) {
        // Payment link generated successfully
        // SHOW SUCCESS MODAL IMMEDIATELY - No waiting modal
        const linkResult = { 
          success: true, 
          short_url: data.short_url,
          message: `Payment link sent successfully to ${mobileNumber}!`
        };
        
        // Add payment to history immediately
        const paymentData = {
          mode: 'Payment Link',
          amount: totalCollection,
          allocation: {
            od: odCollection,
            current: currentCollection,
            charges: chargeCollection,
            nextEmi: advanceEmiCollection,
            prepayment: prepaymentCollection,
            total: totalCollection
          },
          transactionId: data.short_url
        };
        
        console.log('📝 Adding Payment Link to history immediately:', paymentData);
        addPaymentToHistory(paymentData);
        
        // Set success message and show success modal
        setSuccessMessage(linkResult.message);
        setTransactionId(data.short_url);
        setShowSuccessModal(true); // Show success modal immediately
        setIsSubmitting(false);
        
        // COMMENTED OUT - Waiting modal no longer used
        // setPaymentLinkData(linkResult);
        // setShowPaymentWaitingModal(true);
        // setWaitingCountdown(300); // Reset to 5 minutes
        
        return linkResult;
      } else {
        return { 
          success: false, 
          error: "Failed to generate payment link. Please try again." 
        };
      }
    } catch (error) {
      // Payment link error occurred
      return { 
        success: false, 
        error: "Failed to send payment link. Please try again." 
      };
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="min-vh-100 bg-light d-flex align-items-center justify-content-center p-0 p-md-4 modal-scrollable" style={{overflowY: 'auto'}}>
        <div className="card payment-card w-100 modal-scrollable" style={{maxWidth: '800px', maxHeight: '100vh', overflowY: 'auto'}}>
          <div className="card-body p-2 p-md-4">
            {/* Header with Profile */}
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div className="d-flex align-items-center gap-3">
                <div className="profile-circle bg-light border rounded-circle d-flex align-items-center justify-content-center" style={{width: '50px', height: '50px'}}>
                  <span className="text-muted">👤</span>
                </div>
                <div style={{ marginTop: window.innerWidth >= 768 ? '16px' : '0' }}>
                  <h1 className="h5 mb-0 fw-bold">&nbsp;{items && items.Lead_Name ? items.Lead_Name : 'N/A'}</h1>&nbsp;
                  <h6 className="mb-0 fw-bold d-md-none">{items && items.HH_ID ? items.HH_ID : 'N/A'}</h6>
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-center gap-3">
                <span className="icon d-flex align-items-center justify-content-center" onClick={handleCopyId} style={{cursor: 'pointer', width: 'auto', height: '40px'}}>📋 &nbsp;<span className="d-none d-md-inline">{items && items.HH_ID ? items.HH_ID : 'N/A'}</span></span>&nbsp;&nbsp;
                <span className="icon d-flex align-items-center justify-content-center" onClick={handleCall} style={{cursor: 'pointer', width: 'auto', height: '40px'}}>📞 &nbsp;<span className="d-none d-md-inline">{items && items.Household_mobile_no ? items.Household_mobile_no : 'N/A'}</span></span>
              </div>
              <button className="btn-close" onClick={handleClose} style={{background: 'none', border: 'none', fontSize: '1.5rem', color: '#000'}} aria-label="Close">×</button>
            </div>

            <hr className="mb-1 mb-md-4" />
            {/* Select Mode */}
            <div className="mb-1 mb-md-4">
              <div className="d-flex gap-3 gap-md-4 flex-wrap justify-content-around">
                {/* Cash Collection */}
                <PEDiv 
                  element_id="CollectionModal_CashCollectionCard"
                  className={`payment-mode-card ${collectionMode === 'Cash Collection' ? 'selected' : ''}`}
                  onClick={() => handleModeChange({target: {value: 'Cash Collection'}})}
                  style={{
                    cursor: 'pointer',
                    padding: '12px 16px',
                    
                    textAlign: 'center',
                    minWidth: '80px'
                  }}
                >
                  <div style={{marginBottom: '4px'}}>
                    <img
                      src={require("../assets/images/rupee-icon.png")}
                      alt="Cash"
                      style={{width: '60px', height: '60px'}}
                    />
                  </div>
                  <div style={{fontSize: '0.8rem', fontWeight: '500', color: collectionMode === 'Cash Collection' ? '#007bff' : '#6c757d'}}>
                    Cash
                  </div>
                </PEDiv>

                {/* Payment Link (UPI/Bharat Pay) */}
                <PEDiv 
                  element_id="CollectionModal_PaymentLinkCard"
                  className={`payment-mode-card ${collectionMode === 'Payment Link' ? 'selected' : ''}`}
                  onClick={() => handleModeChange({target: {value: 'Payment Link'}})}
                  style={{
                    cursor: 'pointer',
                    padding: '12px 16px',
                    
                    textAlign: 'center',
                    minWidth: '80px'
                  }}
                >
                  <div style={{marginBottom: '4px'}}>
                    <img
                      src={require("../assets/images/mobile-payment.png")}
                      alt="UPI"
                      style={{width: '60px', height: '60px'}}
                    />
                  </div>
                  <div style={{fontSize: '0.8rem', fontWeight: '500', color: collectionMode === 'Payment Link' ? '#007bff' : '#6c757d'}}>
                    UPI
                  </div>
                </PEDiv>

                {/* Cheque */}
                <PEDiv 
                  element_id="CollectionModal_ChequeCard" 
                  className={`payment-mode-card ${collectionMode === 'Cheque' ? 'selected' : ''}`}
                  onClick={() => handleModeChange({target: {value: 'Cheque'}})}
                  style={{
                    cursor: 'pointer',
                    padding: '12px 16px',
                    
                    textAlign: 'center',
                    minWidth: '80px'
                  }}
                >
                  <div style={{marginBottom: '4px'}}>
                    <img
                      src={require("../assets/images/cheque.png")}
                      alt="Cheque"
                      style={{width: '60px', height: '60px'}}
                    />
                  </div>
                  <div style={{fontSize: '0.8rem', fontWeight: '500', color: collectionMode === 'Cheque' ? '#007bff' : '#6c757d'}}>
                    Cheque/Demand Draft
                  </div>
                </PEDiv>
              </div>
            </div>

            {/* Mobile Number Section - Only for Payment Link */}
            {collectionMode === 'Payment Link' && (
              <div className="mb-1 mb-md-4">
                <label className="form-label fw-semibold h6" htmlFor="mobileNumber">Mobile Number</label>
                <input
                  type="tel"
                  id="mobileNumber"
                  name="mobileNumber"
                  className="form-control form-control-lg"
                  value={mobileNumber}
                  onChange={handleMobileNumberChange}
                  placeholder="Enter 10-digit mobile number"
                  maxLength="10"
                  pattern="[0-9]{10}"
                />
                {mobileNumber && !isValidMobile(mobileNumber) && (
                  <small className="text-danger">Please enter a valid 10-digit mobile number</small>
                )}
              </div>
            )}

            {/* Cheque Upload Section - Only for Cheque */}
            {collectionMode === 'Cheque' && (
              <div className="mb-3 mb-md-4">
                <label className="form-label fw-semibold h6">Upload Cheque</label>
                <div 
                  className="upload-box border rounded p-4 text-center" 
                  style={{
                    border: '2px dashed #ccc',
                    backgroundColor: '#fafafa',
                    minHeight: '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  {chequePreview ? (
                    <div className="w-100">
                      <img 
                        src={chequePreview} 
                        alt="Cheque Preview" 
                        style={{maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', width: '100%'}}
                      />
                      <div className="d-flex justify-content-center gap-2 mt-2">
                        <button
                          type="button"
                          className="btn btn-sm d-flex align-items-center gap-1 mr-2"
                          style={{backgroundColor: '#87CEEB', color: 'white', border: 'none'}}
                          onClick={() => {
                            if (chequePreview) {
                              const newWindow = window.open();
                              newWindow.document.write(`
                                <html>
                                  <head><title>Cheque Preview</title></head>
                                  <body style="margin:0; display:flex; justify-content:center; align-items:center; background:#f0f0f0;">
                                    <img src="${chequePreview}" style="max-width:100%; max-height:100vh; object-fit:contain;" />
                                  </body>
                                </html>
                              `);
                              newWindow.document.close();
                            }
                          }}
                        >
                          <span>👁️</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm d-flex align-items-center gap-1"
                          style={{backgroundColor: '#dc3545', color: 'white', border: 'none'}}
                          onClick={handleDeleteClick}
                        >
                          <span>🗑️</span>
                        </button>
                      </div>
                    </div>
                  ) : chequeFile && chequeFile.type === 'application/pdf' ? (
                    <div className="text-center">
                      <div style={{fontSize: '2rem', color: '#dc3545', marginBottom: '10px'}}>📄</div>
                      <p className="mb-1 font-weight-bold">{chequeFile.name}</p>
                      <small className="text-muted">PDF file uploaded</small>
                      <div className="d-flex justify-content-center gap-2 mt-2">
                        <button
                          type="button"
                          className="btn btn-sm d-flex align-items-center gap-1"
                          style={{backgroundColor: '#87CEEB', color: 'white', border: 'none'}}
                          onClick={() => {
                            const fileURL = URL.createObjectURL(chequeFile);
                            window.open(fileURL, '_blank');
                            // Clean up the object URL to prevent memory leaks
                            setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
                          }}
                        >
                          <span>👁️</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm d-flex align-items-center gap-1"
                          style={{backgroundColor: '#dc3545', color: 'white', border: 'none'}}
                          onClick={handleDeleteClick}
                        >
                          <span>🗑️</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div style={{fontSize: '2rem', color: '#6c757d', marginBottom: '10px'}}>📤</div>
                      <p className="mb-1 text-muted">Upload JPG or PDF</p>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm mt-2"
                        onClick={() => document.getElementById('chequeFile').click()}
                      >
                        Choose File
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    id="chequeFile"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleChequeFileUpload}
                    style={{display: 'none'}}
                  />
                </div>
                
                {/* OCR Processing Indicator */}
                {isProcessingOCR && (
                  <div className="text-center mt-2">
                    <small className="text-info">
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Processing cheque image...
                    </small>
                  </div>
                )}
                
                {/* OCR Error Display */}
                {ocrError && (
                  <div className="alert alert-warning mt-2 p-2">
                    <small>{ocrError}</small>
                  </div>
                )}
                
                {/* Cheque Details Fields */}
                {chequeFile && (
                  <div className="mt-3">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="issuerName">Issuer Name</label>
                          <input
                            type="text"
                            id="issuerName"
                            name="issuerName"
                            className="form-control"
                            value={issuerName}
                            onChange={handleIssuerNameChange}
                            placeholder="Enter issuer name"
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="chequeDate">Cheque Date</label>
                          <input
                            type="date"
                            id="chequeDate"
                            name="chequeDate"
                            className="form-control"
                            value={chequeDate}
                            onChange={handleChequeDateChange}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label className="form-label" htmlFor="chequeNumber">Cheque Number</label>
                          <input
                            type="text"
                            id="chequeNumber"
                            name="chequeNumber"
                            className="form-control"
                            value={chequeNumber}
                            onChange={handleChequeNumberChange}
                            placeholder="Enter cheque number"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Collection Summary Table - Desktop View */}
            <div className="d-none d-md-block">
              <div>
                <table className="table table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th className="text-center fw-bold" style={{fontSize: '0.9rem'}}></th>
                      <th className="text-center fw-bold" style={{fontSize: '0.9rem'}}>Demand</th>
                      <th className="text-center fw-bold" style={{fontSize: '0.9rem'}}>Collection</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Total Collection Row */}
                    <tr style={{backgroundColor: '#e8f5e8'}}>
                      <td className="text-center fw-bold" style={{fontSize: '0.9rem', verticalAlign: 'middle', color: '#000'}}>Total</td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <input 
                          type="text" 
                          className="form-control form-control-sm text-center fw-bold" 
                          disabled 
                          value={formatCurrency(remainingDemand.total)} 
                          style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.85rem', height: '38px', color: '#000'}}
                        />
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <input 
                          type="text"
                          id="totalCollectionTable"
                          name="totalCollectionTable"
                          className="form-control form-control-sm text-center fw-bold" 
                          value={totalCollectionDisplay || ''}
                          onChange={handleTotalCollectionChange}
                          onFocus={handleTotalCollectionFocus}
                          onBlur={handleTotalCollectionBlur}
                          placeholder="Enter amount"
                          style={{fontSize: '0.85rem', color: '#000', backgroundColor: '#f8fff8'}}
                        />
                      </td>
                    </tr>
                    
                    {/* Overdue Row */}
                    <tr className="overdue-row" style={{backgroundColor: '#fff2f2'}}>
                      <td className="text-center fw-semibold" style={{fontSize: '0.85rem', verticalAlign: 'middle'}}>Overdue</td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <input 
                          type="text" 
                          className="form-control form-control-sm text-center" 
                          disabled 
                          value={formatCurrency(remainingDemand.od)} 
                          style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.8rem'}}
                        />
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <input 
                          type="text"
                          disabled
                          className="form-control form-control-sm text-center" 
                          value={formatCurrency(odCollection)}
                          style={{fontSize: '0.8rem'}}
                        />
                      </td>
                    </tr>
                    
                    {/* Current Demand Row */}
                    <tr className="current-emi-row" style={{backgroundColor: '#f2f8ff'}}>
                      <td className="text-center fw-semibold" style={{fontSize: '0.85rem', verticalAlign: 'middle'}}>Current Demand</td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <input 
                          type="text" 
                          className="form-control form-control-sm text-center" 
                          disabled 
                          value={formatCurrency(remainingDemand.current)} 
                          style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.8rem'}}
                        />
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <input 
                          type="text"
                          disabled
                          className="form-control form-control-sm text-center" 
                          value={formatCurrency(currentCollection)}
                          style={{fontSize: '0.8rem'}}
                        />
                      </td>
                    </tr>
                    
                    {/* Charges Row */}
                    <tr className="charges-row" style={{backgroundColor: '#fff8f2'}}>
                      <td className="text-center fw-semibold" style={{fontSize: '0.85rem', verticalAlign: 'middle'}}>Charges</td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <input 
                          type="text" 
                          className="form-control form-control-sm text-center" 
                          disabled 
                          value={formatCurrency(remainingDemand.charges)} 
                          style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.8rem'}}
                        />
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <input 
                          type="text"
                          className="form-control form-control-sm text-center" 
                          value={chargeCollectionDisplay || ''}
                          onChange={handleChargeCollectionChange}
                          onFocus={handleChargeCollectionFocus}
                          onBlur={handleChargeCollectionBlur}
                          placeholder="0"
                          style={{fontSize: '0.8rem'}}
                        />
                      </td>
                    </tr>

                    {/* Next EMI Row */}
                    <tr className="next-emi-row" style={{backgroundColor: '#f2f8ff'}}>
                      <td className="text-center fw-semibold" style={{ fontSize: '0.85rem', verticalAlign: 'middle'}}>Next EMI</td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <input 
                          type="text" 
                          className="form-control form-control-sm text-center" 
                          disabled 
                          value={(() => {
                            // Show remaining Next EMI capacity after previous payments
                            const remainingCapacity = remainingDemand.nextEmi;
                            return remainingCapacity > 0 ? formatCurrency(remainingCapacity) : '0';
                          })()}
                          style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.8rem'}}
                        />
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <input 
                          type="text"
                          className="form-control form-control-sm text-center" 
                          placeholder="0"
                          value={advanceEmiDisplay || ''}
                          onChange={handleAdvanceEmiChange}
                          onFocus={handleAdvanceEmiFocus}
                          onBlur={handleAdvanceEmiBlur}
                          disabled
                          style={{fontSize: '0.8rem'}}
                        />
                      </td>
                    </tr>
                    
                    {/* Prepayment Row */}
                    <tr className="prepayment-row" style={{backgroundColor: '#f2fff8'}}>
                      <td className="text-center fw-semibold" style={{fontSize: '0.85rem', verticalAlign: 'middle'}}>
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <span>Prepayment / Advance Collection</span>&nbsp;
                          <div className="prepayment-info-icon-wrapper" style={{position: 'relative'}}>
                            <span 
                              onClick={togglePrepaymentTooltip}
                              style={{
                                cursor: 'pointer',
                                fontSize: '0.7rem',
                                color: '#007bff',
                                fontWeight: 'bold'
                              }}
                            >
                              ⓘ
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <input 
                          type="text" 
                          className="form-control form-control-sm text-center" 
                          disabled 
                          value={formatCurrency(maxPrepaymentAmount)}
                          style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.8rem'}}
                        />
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <input 
                          type="text"
                          className="form-control form-control-sm text-center" 
                          placeholder="0"
                          value={prepaymentDisplay || ''}
                          onChange={handlePrepaymentChange}
                          onFocus={handlePrepaymentFocus}
                          onBlur={handlePrepaymentBlur}
                          style={{fontSize: '0.8rem'}}
                          disabled
                        />
                      </td>
                    </tr>
                    
                    {/* Foreclosure Row */}
                    <tr className="foreclosure-row" style={{backgroundColor: '#fff2f8'}}>
                      <td className="text-center fw-semibold" style={{fontSize: '0.85rem', verticalAlign: 'middle'}}>
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <span>Foreclosure / Partial Foreclosure</span>&nbsp;
                          <span 
                            className="foreclosure-info-icon-wrapper d-inline-flex align-items-center justify-content-center"
                            onClick={toggleForeclosureTooltip}
                            style={{
                              fontSize: '0.7rem', 
                              color: '#007bff', 
                              cursor: 'pointer',
                              width: '14px',
                              height: '14px'
                            }}
                          >
                            ⓘ
                          </span>
                        </div>
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <span className="form-control form-control-sm text-center d-flex align-items-center justify-content-center" 
                              style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.8rem', height: '38px'}}>
                          -
                        </span>
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle'}}>
                        <input 
                          type="text"
                          disabled
                          className="form-control form-control-sm text-center" 
                          placeholder="0"
                          value={foreclosureDisplay || ''}
                          onChange={handleForeclosureChange}
                          onFocus={handleForeclosureFocus}
                          onBlur={handleForeclosureBlur}
                          style={{fontSize: '0.8rem'}}
                        />
                      </td>
                    </tr>
                    
                    {/* Foreclosure Charges Row - NEW (4% of foreclosure amount) */}
                    {showForeclosureCharges && foreclosureCharges > 0 && (
                      <tr className="foreclosure-charges-row" style={{backgroundColor: '#fff9f2'}}>
                        <td className="text-center fw-semibold" style={{fontSize: '0.85rem', verticalAlign: 'middle'}}>
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            <span>Foreclosure Charges</span>
                            <span 
                              style={{
                                fontSize: '0.65rem',
                                color: '#666',
                                fontStyle: 'italic'
                              }}
                            >
                              (4%)
                            </span>
                          </div>
                        </td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <span className="form-control form-control-sm text-center d-flex align-items-center justify-content-center" 
                                style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.8rem', height: '38px'}}>
                            -
                          </span>
                        </td>
                        <td className="text-center" style={{verticalAlign: 'middle'}}>
                          <input 
                            type="text"
                            disabled
                            className="form-control form-control-sm text-center" 
                            value={foreclosureCharges > 0 ? foreclosureCharges.toFixed(2).replace(/\.00$/, '') : ''}
                            style={{fontSize: '0.8rem', backgroundColor: '#f5f5f5'}}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Prepayment Tooltip Message - Desktop */}
              {showPrepaymentTooltip && (
                <div style={{fontSize: '0.8rem', color: '#333'}}>
                  <span>Up to {maxPrepaymentEmis} EMI allowed</span>
                </div>
              )}
              
              {/* Foreclosure Tooltip Message - Desktop */}
              {showForeclosureTooltip && (
                <div style={{fontSize: '0.8rem', color: '#333'}}>
                  <span>For Foreclosure contact TO at branch.</span>
                </div>
              )}
            </div>

            {/* Collection Summary Table - Mobile View */}
            <div className="d-md-none">
              <div>
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th className="text-center fw-bold" style={{fontSize: '0.8rem', width: '40%'}}></th>
                      <th className="text-center fw-bold" style={{fontSize: '0.8rem', width: '30%'}}>Demand</th>
                      <th className="text-center fw-bold" style={{fontSize: '0.8rem', width: '30%'}}>Collection</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Total Collection Row */}
                    <tr style={{backgroundColor: '#e8f5e8'}}>
                      <td className="text-center fw-bold" style={{fontSize: '0.8rem', verticalAlign: 'middle', padding: '8px 4px', color: '#000'}}>Total</td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <span className="form-control form-control-sm text-center d-flex align-items-center justify-content-center fw-bold" 
                              style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.7rem', height: '30px', padding: '4px', color: '#000'}}>
                          {formatCurrency(remainingDemand.total)}
                        </span>
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <input 
                          type="text"
                          id="totalCollectionTableMobile"
                          name="totalCollectionTableMobile"
                          className="form-control form-control-sm text-center fw-bold" 
                          value={totalCollectionDisplay || ''}
                          onChange={handleTotalCollectionChange}
                          onFocus={handleTotalCollectionFocus}
                          onBlur={handleTotalCollectionBlur}
                          placeholder="Amount"
                          style={{fontSize: '0.75rem', padding: '4px', color: '#000', backgroundColor: '#f8fff8'}}
                        />
                      </td>
                    </tr>
                    
                    {/* Overdue Row */}
                    <tr className="overdue-row" style={{backgroundColor: '#fff2f2'}}>
                      <td className="text-center fw-semibold" style={{fontSize: '0.75rem', verticalAlign: 'middle', padding: '8px 4px'}}>Overdue</td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <input 
                          type="text" 
                          className="form-control form-control-sm text-center" 
                          disabled 
                          value={formatCurrency(remainingDemand.od)} 
                          style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.7rem', padding: '4px'}}
                        />
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <input 
                          type="text"
                          disabled
                          className="form-control form-control-sm text-center" 
                          value={formatCurrency(odCollection)}
                          style={{fontSize: '0.7rem', padding: '4px'}}
                        />
                      </td>
                    </tr>
                    
                    {/* Current Demand Row */}
                    <tr className="current-emi-row" style={{backgroundColor: '#f2f8ff'}}>
                      <td className="text-center fw-semibold" style={{fontSize: '0.75rem', verticalAlign: 'middle', padding: '8px 4px'}}>Current Demand</td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <input 
                          type="text" 
                          className="form-control form-control-sm text-center" 
                          disabled 
                          value={formatCurrency(remainingDemand.current)} 
                          style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.7rem', padding: '4px'}}
                        />
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <input 
                          type="text"
                          disabled
                          className="form-control form-control-sm text-center" 
                          value={formatCurrency(currentCollection)}
                          style={{fontSize: '0.7rem', padding: '4px'}}
                        />
                      </td>
                    </tr>
                    
                    {/* Charges Row */}
                    <tr className="charges-row" style={{backgroundColor: '#fff8f2'}}>
                      <td className="text-center fw-semibold" style={{fontSize: '0.75rem', verticalAlign: 'middle', padding: '8px 4px'}}>Charges</td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <input 
                          type="text" 
                          className="form-control form-control-sm text-center" 
                          disabled 
                          value={formatCurrency(remainingDemand.charges)} 
                          style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.7rem', padding: '4px'}}
                        />
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <input 
                          type="text"
                          className="form-control form-control-sm text-center" 
                          value={chargeCollectionDisplay || ''}
                          onChange={handleChargeCollectionChange}
                          onFocus={handleChargeCollectionFocus}
                          onBlur={handleChargeCollectionBlur}
                          placeholder="0"
                          style={{fontSize: '0.7rem', padding: '4px'}}
                        />
                      </td>
                    </tr>

                    {/* Next EMI row */}
                    <tr className="next-emi-row" style={{backgroundColor: '#f2f8ff'}}>
                      <td className="text-center fw-semibold" style={{fontSize: '0.75rem', verticalAlign: 'middle', padding: '8px 4px'}}>Next EMI</td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <input 
                          type="text" 
                          className="form-control form-control-sm text-center" 
                          disabled 
                          value={(() => {
                            // Show remaining Next EMI capacity after previous payments
                            const remainingCapacity = remainingDemand.nextEmi;
                            console.log('🔍 Next EMI Display:', {
                              remainingCapacity,
                              remainingDemand,
                              willShow: remainingCapacity > 0 ? formatCurrency(remainingCapacity) : '0'
                            });
                            return remainingCapacity > 0 ? formatCurrency(remainingCapacity) : '0';
                          })()}
                          style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.7rem', padding: '4px'}}
                        />
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <input 
                          type="text"
                          className="form-control form-control-sm text-center" 
                          placeholder="0"
                          value={advanceEmiDisplay || ''}
                          onChange={handleAdvanceEmiChange}
                          onFocus={handleAdvanceEmiFocus}
                          onBlur={handleAdvanceEmiBlur}
                          disabled
                          style={{fontSize: '0.7rem', padding: '4px'}}
                        />
                      </td>
                    </tr>
                    
                    {/* Prepayment Row */}
                    <tr className="prepayment-row" style={{backgroundColor: '#f2fff8'}}>
                      <td className="text-center fw-semibold" style={{fontSize: '0.75rem', verticalAlign: 'middle', padding: '8px 4px'}}>
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <span>Prepayment / Advance Collection</span>&nbsp;
                          <div className="prepayment-info-icon-wrapper" style={{position: 'relative'}}>
                            <span 
                              onClick={togglePrepaymentTooltip}
                              style={{
                                cursor: 'pointer',
                                fontSize: '0.6rem',
                                color: '#007bff',
                                fontWeight: 'bold'
                              }}
                            >
                              ⓘ
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <input 
                          type="text" 
                          className="form-control form-control-sm text-center" 
                          disabled 
                          value={formatCurrency(maxPrepaymentAmount)}
                          style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.7rem', padding: '4px'}}
                        />
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <input 
                          type="text"
                          className="form-control form-control-sm text-center" 
                          placeholder="0"
                          value={prepaymentDisplay || ''}
                          onChange={handlePrepaymentChange}
                          onFocus={handlePrepaymentFocus}
                          onBlur={handlePrepaymentBlur}
                          style={{fontSize: '0.7rem', padding: '4px'}}
                          disabled
                        />
                      </td>
                    </tr>
                    
                    {/* Foreclosure Row */}
                    <tr className="foreclosure-row" style={{backgroundColor: '#fff2f8'}}>
                      <td className="text-center fw-semibold" style={{fontSize: '0.75rem', verticalAlign: 'middle', padding: '8px 4px'}}>
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <span>Foreclosure / Partial Foreclosure</span>&nbsp;
                          <span 
                            className="foreclosure-info-icon-wrapper d-inline-flex align-items-center justify-content-center"
                            onClick={toggleForeclosureTooltip}
                            style={{
                              fontSize: '0.6rem', 
                              color: '#007bff', 
                              cursor: 'pointer',
                              width: '12px',
                              height: '12px'
                            }}
                          >
                            ⓘ
                          </span>
                        </div>
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <span className="form-control form-control-sm text-center d-flex align-items-center justify-content-center" 
                              style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.7rem', height: '30px', padding: '4px'}}>
                          -
                        </span>
                      </td>
                      <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                        <input 
                          type="text"
                          className="form-control form-control-sm text-center" 
                          disabled
                          placeholder="0"
                          value={foreclosureDisplay || ''}
                          onChange={handleForeclosureChange}
                          onFocus={handleForeclosureFocus}
                          onBlur={handleForeclosureBlur}
                          style={{fontSize: '0.7rem', padding: '4px'}}
                        />
                      </td>
                    </tr>
                    
                    {/* Foreclosure Charges Row - NEW (4% of foreclosure amount) - Mobile */}
                    {showForeclosureCharges && foreclosureCharges > 0 && (
                      <tr className="foreclosure-charges-row" style={{backgroundColor: '#fff9f2'}}>
                        <td className="text-center fw-semibold" style={{fontSize: '0.75rem', verticalAlign: 'middle', padding: '8px 4px'}}>
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            <span>Foreclosure Charges</span>
                            <span 
                              style={{
                                fontSize: '0.6rem',
                                color: '#666',
                                fontStyle: 'italic'
                              }}
                            >
                              (4%)
                            </span>
                          </div>
                        </td>
                        <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                          <span className="form-control form-control-sm text-center d-flex align-items-center justify-content-center" 
                                style={{backgroundColor: 'transparent', border: 'none', fontSize: '0.7rem', height: '30px', padding: '4px'}}>
                            -
                          </span>
                        </td>
                        <td className="text-center" style={{verticalAlign: 'middle', padding: '8px 4px'}}>
                          <input 
                            type="text"
                            disabled
                            className="form-control form-control-sm text-center" 
                            value={foreclosureCharges > 0 ? foreclosureCharges.toFixed(2).replace(/\.00$/, '') : ''}
                            style={{fontSize: '0.7rem', padding: '4px', backgroundColor: '#f5f5f5'}}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Prepayment Tooltip Message - Mobile */}
              {showPrepaymentTooltip && (
                <div style={{fontSize: '0.6rem', color: '#333'}}>
                  <span>Up to {maxPrepaymentEmis} EMI allowed</span>
                </div>
              )}
              
              {/* Foreclosure Tooltip Message - Mobile */}
              {showForeclosureTooltip && (
                <div style={{fontSize: '0.6rem', color: '#333'}}>
                  <span>For Foreclosure contact TO at branch.</span>
                </div>
              )}
            </div>

            {/* Remarks */}
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="form-label fw-semibold h6 mb-0" htmlFor="remarks">Remarks (Optional)</label>
                {speechSupported && (
                  <button
                    type="button"
                    className={`btn btn-sm d-flex align-items-center gap-1 ${isListening ? 'btn-danger' : 'btn-outline-primary'}`}
                    onClick={isListening ? stopListening : startListening}
                    disabled={isListening}
                    style={{fontSize: '0.8rem', padding: '4px 8px'}}
                  >
                    <span>{isListening ? '🔴' : '🎤'}</span>
                    <span className="d-none d-md-inline">{isListening ? 'Listening...' : ''}</span>
                  </button>
                )}
              </div>
              <div className="position-relative">
                <textarea
                  id="remarks"
                  name="remarks"
                  className="form-control"
                  rows="3"
                  placeholder="Add any final remarks... (Click voice button to speak)"
                  value={remarks}
                  onChange={handleRemarksChange}
                ></textarea>
                {isListening && (
                  <div className="position-absolute top-0 end-0 m-2">
                    <span className="badge bg-danger">
                      <span className="spinner-border spinner-border-sm me-1" style={{width: '0.8rem', height: '0.8rem'}}></span>
                      Listening
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="row">
              <div className="col-6">
                <button 
                  className="btn btn-cancel w-100 py-2 fw-medium"
                  onClick={handleClose}
                >
                  Cancel
                </button>
              </div>
              <div className="col-6">
                <PEDiv 
                  element_id="CollectionModal_CollectBtn"
                  style={{display: 'flex'}}
                >
                  <button 
                    className="btn btn-confirm-collection text-white w-100 py-2 fw-medium"
                    onClick={handleDirectSubmit}
                    disabled={isSubmitting || !isSubmitEnabled()}
                  >
                    {isSubmitting ? (
                      <span>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Processing...
                      </span>
                    ) : (
                      <>
                        {collectionMode === 'Cash Collection'
                          ? 'Collect Cash'
                          : collectionMode === 'Cheque'
                            ? 'Submit Cheque'
                            : collectionMode === 'Payment Link'
                              ? 'Send Link'
                              : 'Select Payment Mode'}
                      </>
                    )}
                  </button>
                </PEDiv>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal - Direct API result without confirmation step */}
      {showSuccessModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 10000}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content confirmation-modal">
              <div className="modal-body p-4 text-center">
                {/* Success Amount - Only for Cash Collection mode */}
                {collectionMode === "Cash Collection" && (
                  <div className="mb-3">
                    <h5 className="fw-semibold text-dark mb-1 d-none d-md-block">
                      Received From : {items && items.Lead_Name || 'N/A'}
                    </h5>
                    <h3 className="fw-semibold text-dark mb-1 d-md-none">
                      Received From : {items && items.Lead_Name || 'N/A'}
                    </h3>
                    <h2 className="display-4 mb-2" style={{fontWeight: 'bold', color: '#000'}}>{formatCurrency(totalCollection)}</h2>
                    <h5 className="fw-semibold text-dark mb-1">
                      Collected successfully
                    </h5>
                  </div>
                )}
                
                {/* Cheque Success Message - Only for Cheque mode */}
                {collectionMode === 'Cheque' && (
                  <div className="mb-4">
                    <h5 className="fw-semibold text-dark mb-3">
                      Cheque for <span style={{fontSize: '1.2em', fontWeight: 'bold'}}>{formatCurrency(totalCollection)}</span> submitted Successfully
                    </h5>
                    <p className="mb-3 text-dark">
                      From: <span className="font-weight-bold">{items && items.Lead_Name || 'N/A'}</span> | <span className="font-weight-bold">{items && items.HH_ID || 'N/A'}</span>
                    </p>
                    
                    {/* Cheque Details in Success Modal */}
                    <div className="card mb-4" style={{backgroundColor: '#f8f9fa', border: '1px solid #e9ecef'}}>
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <span style={{fontSize: '1.2rem'}}>💳</span>&nbsp;
                          <h6 className="mb-0 font-weight-bold">Cheque Details:</h6>
                        </div>
                        <div className="d-flex flex-row flex-wrap gap-3 cheque-details">
                          <div className="mb-2">
                            <p className="small mb-1 text-muted">Issuer Name:</p>
                            <p className="mb-0 font-weight-bold">{issuerName || 'N/A'}</p>
                          </div>
                          <div className="mb-2">
                            <p className="small mb-1 text-muted">Cheque Date:</p>
                            <p className="mb-0 font-weight-bold">{chequeDate || 'N/A'}</p>
                          </div>
                          <div className="mb-0">
                            <p className="small mb-1 text-muted">Cheque No:</p>
                            <p className="mb-0 font-weight-bold">{chequeNumber || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Link Details - Only for Payment Link mode */}
                {collectionMode === 'Payment Link' && (
                  <div className="mb-4 p-3 rounded" style={{backgroundColor: '#f8f9fa', border: '1px solid #e9ecef'}}>
                    <p className="mb-2 text-dark">
                      Payment Link <a href={transactionId} target="_blank" rel="noopener noreferrer" style={{color: '#007bff', textDecoration: 'none'}}>{transactionId}</a> for <span style={{fontSize: '1.1em', fontWeight: 'bold'}}>{formatCurrency(totalCollection)}</span> is Sent Successfully on <span style={{color: '#28a745', fontWeight: 'bold'}}>{mobileNumber}</span>!
                    </p>
                    <p className="mb-0 text-dark small" style={{lineHeight: '1.4'}}>
                      अगर यह लिंक {items && items.Lead_Name || 'Customer'} जी को SMS से प्राप्त नही हुआ है.<br />
                      तभी यह लिंक सिर्फ उनको ही प्रदान करने का कष्ट करे। कृपया यह लिंक दूसरे हाउसहोल्ड को ना भेजें.
                    </p>
                  </div>
                )}

                {/* Allocation Summary */}
                <div className="allocation-summary-modal bg-light p-3 rounded mb-4">
                  <h6 className="font-weight-bold mb-3 d-none d-md-block" style={{textAlign: 'left'}}>Allocation Summary:</h6>
                  <h4 className="font-weight-bold mb-3 d-md-none" style={{textAlign: 'left'}}>Allocation Summary:</h4>
                  
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-dark">Overdue:</span>
                    <span className="font-weight-bold">{formatCurrency(odCollection)}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-dark">Current Demand:</span>
                    <span className="font-weight-bold">{formatCurrency(currentCollection)}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-dark">Charges:</span>
                    <span className="font-weight-bold">{formatCurrency(chargeCollection)}</span>
                  </div>
                  {advanceEmiCollection > 0 && (
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-dark">Next EMI:</span>
                      <span className="font-weight-bold">{formatCurrency(advanceEmiCollection)}</span>
                    </div>
                  )}
                  {prepaymentCollection > 0 && (
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-dark">Prepayment:</span>
                      <span className="font-weight-bold">{formatCurrency(prepaymentCollection)}</span>
                    </div>
                  )}
                  {foreclosureCollection > 0 && (
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-dark">Foreclosure:</span>
                      <span className="font-weight-bold">{formatCurrency(foreclosureCollection)}</span>
                    </div>
                  )}
                  {foreclosureCharges > 0 && (
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-dark">Foreclosure Charges (4%):</span>
                      <span className="font-weight-bold">
                        {foreclosureCharges > 0 ? Number(foreclosureCharges).toFixed(2).replace(/\.?0+$/, '') : '0'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Foreclosure Alert Message */}
                {foreclosureCollection > 0 && (
                  <div className="alert alert-danger mb-3" role="alert" style={{
                    backgroundColor: '#f8d7da',
                    borderColor: '#f5c6cb',
                    color: '#721c24',
                    padding: '12px 20px',
                    borderRadius: '5px',
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    textAlign : 'left'
                  }}>
                    ⚠️ Connect with FCM if this payment is for loan foreclosure.
                  </div>
                )}

                {/* Transaction Details */}
                {(collectionMode === 'Cash Collection' || collectionMode === 'Cheque') && (
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mb-3">
                    <span className="font-weight-bold mb-1 mb-sm-0">
                      Txn ID: {transactionId || 'PROCESSING'}
                    </span>
                    <span className="text-muted">{new Date().toLocaleDateString('en-GB')}</span>
                  </div>
                )}

                {/* Hindi Text */}
                {(collectionMode === 'Cash Collection' || collectionMode === 'Cheque') && (
                  <p className="hindi-text text-danger small mb-4">
                    {successMessage || 'कैश एप्प पोस्ट की गई है और इसे आपके कैश बैलेंस में शामिल कर दिया गया है'}
                  </p>
                )}

                {/* Action Buttons */}
                <div className="row g-2" style={{justifyContent:'space-around'}}>
                  <div className="col-4">
                    <button 
                      className="btn btn-outline-info w-100 py-2 fw-medium"
                      onClick={handleGoToDashboard}
                    >
                      Home
                    </button>
                  </div>
                  {/* Next Payment Button - COMMENTED OUT */}
                  {/* <div className="col-4">
                    <button 
                      className="btn btn-info text-white w-100 py-2 fw-medium"
                      onClick={() => {
                        console.log('🔘 Next Payment button clicked!');
                        console.log('🔘 Collection mode:', collectionMode);
                        console.log('🔘 Payment history length:', paymentHistory.length);
                        try {
                          resetForNextPayment();
                          setShowSuccessModal(false);
                          
                          // Show appropriate toast based on last payment mode
                          const lastPayment = paymentHistory.length > 0 ? paymentHistory[paymentHistory.length - 1] : null;
                          if (lastPayment && lastPayment.mode !== 'Cheque') {
                            toast.success('Data refreshed! Ready for next payment.');
                          }
                        } catch (error) {
                          console.error('❌ Error in Next Payment:', error);
                          toast.error('Error preparing for next payment');
                        }
                      }}
                    >
                      Next Payment
                    </button>
                  </div> */}
                  <div className="col-4">
                    <button 
                      className="btn btn-info text-white w-100 py-2 fw-medium"
                      onClick={() => {
                        // Refresh data before closing
                        window.location.reload();
                        handleClose();
                        setShowSuccessModal(false);
                      }}
                    >
                      Collection Screen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div 
          className="modal fade show d-block" 
          style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001}}
          onClick={handleDeleteCancel}
        >
          <div 
            className="modal-dialog modal-dialog-centered modal-sm" 
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header border-0 pb-0" style={{justifyContent: 'end'}}>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleDeleteCancel}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: '#000',
                    opacity: 0.5
                  }}
                >
                  ×
                </button>
              </div>
              <div className="modal-body text-center pt-0">
                <h5 className="mb-3" style={{fontWeight: 'bold'}}>Are you sure?</h5>
                <p className="mb-4 text-muted">You want to delete the cheque photo.</p>
                <div className="d-flex gap-3 justify-content-center">
                  <button 
                    type="button" 
                    className="btn px-4 py-2"
                    style={{
                      backgroundColor: '#f8f9fa', 
                      color: '#6c757d',
                      border: '1px solid #dee2e6',
                      marginRight: '20px'
                    }}
                    onClick={handleDeleteCancel}
                  >
                    No
                  </button>
                  <button 
                    type="button" 
                    className="btn px-4 py-2"
                    style={{
                      backgroundColor: '#f8d7da', 
                      color: '#721c24',
                      border: '1px solid #f5c6cb'
                    }}
                    onClick={handleDeleteConfirm}
                  >
                    Yes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Link Waiting Modal - COMMENTED OUT - Success modal now shows immediately after link generation */}
      {/* {showPaymentWaitingModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 10002}}>
          <div className="modal-dialog modal-dialog-centered mx-2 mx-md-auto">
            <div className="modal-content" style={{borderRadius: '15px', border: '2px solid #007bff'}}>
              <div className="modal-body p-3 p-md-5 text-center">
                <div className="mb-3 mb-md-4">
                  <div 
                    className="spinner-border text-primary" 
                    role="status" 
                    style={{
                      width: window.innerWidth < 768 ? '3rem' : '4rem', 
                      height: window.innerWidth < 768 ? '3rem' : '4rem', 
                      borderWidth: '0.4rem'
                    }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>

                <h4 className="mb-3 fw-bold text-dark" style={{fontSize: window.innerWidth < 768 ? '1.25rem' : '1.5rem'}}>
                  Payment Link Sent!
                </h4>
                
                {paymentLinkData && (
                  <div className="mb-3 mb-md-4 p-2 p-md-3 rounded" style={{backgroundColor: '#f0f8ff', border: '1px solid #d0e8ff'}}>
                    <p className="mb-2 text-dark" style={{fontSize: window.innerWidth < 768 ? '0.875rem' : '1rem'}}>
                      Link sent to: <strong>{mobileNumber}</strong>
                    </p>
                    <p className="mb-2 text-dark" style={{fontSize: window.innerWidth < 768 ? '0.875rem' : '1rem'}}>
                      Amount: <strong>{formatCurrency(totalCollection)}</strong>
                    </p>
                    <p className="mb-0 small text-muted" style={{fontSize: window.innerWidth < 768 ? '0.75rem' : '0.875rem', wordBreak: 'break-all'}}>
                      <a href={paymentLinkData.short_url} target="_blank" rel="noopener noreferrer" style={{color: '#007bff', textDecoration: 'none'}}>
                        {paymentLinkData.short_url}
                      </a>
                    </p>
                  </div>
                )}

                <div className="alert alert-info mb-3 mb-md-4 p-2 p-md-3" role="alert">
                  <p className="mb-2 fw-semibold" style={{fontSize: window.innerWidth < 768 ? '0.875rem' : '1rem'}}>
                    📱 Please confirm payment status:
                  </p>
                  <ul className="text-start mb-0" style={{fontSize: window.innerWidth < 768 ? '0.75rem' : '0.875rem', paddingLeft: window.innerWidth < 768 ? '1.25rem' : '1.5rem'}}>
                    <li>Click <strong>"Success"</strong> if customer has completed payment</li>
                    <li>Click <strong>"Fail/Cancel"</strong> if payment was not completed</li>
                  </ul>
                </div>

                <div className="row g-2 g-md-3">
                  <div className="col-6">
                    <button 
                      className="btn btn-success w-100 fw-bold"
                      onClick={handlePaymentLinkSuccess}
                      style={{
                        fontSize: window.innerWidth < 768 ? '0.9rem' : '1.1rem', 
                        borderRadius: '10px',
                        padding: window.innerWidth < 768 ? '0.75rem 0.5rem' : '1rem 0.75rem'
                      }}
                    >
                      ✓ Success
                    </button>
                  </div>
                  <div className="col-6">
                    <button 
                      className="btn btn-danger w-100 fw-bold"
                      onClick={handlePaymentLinkFail}
                      style={{
                        fontSize: window.innerWidth < 768 ? '0.9rem' : '1.1rem', 
                        borderRadius: '10px',
                        padding: window.innerWidth < 768 ? '0.75rem 0.5rem' : '1rem 0.75rem'
                      }}
                    >
                      ✗ Fail/Cancel
                    </button>
                  </div>
                </div>

                {waitingCountdown < 60 && waitingCountdown > 0 && (
                  <div className="mt-2 mt-md-3 alert alert-warning py-2">
                    <small className="fw-semibold" style={{fontSize: window.innerWidth < 768 ? '0.75rem' : '0.875rem'}}>
                      ⚠️ Less than 1 minute remaining! <br/>
                      <strong>Modal will auto-close at 00:00 if no action is taken.</strong>
                    </small>
                  </div>
                )}

                {waitingCountdown === 0 && (
                  <div className="mt-2 mt-md-3 alert alert-danger py-2">
                    <small className="fw-bold" style={{fontSize: window.innerWidth < 768 ? '0.75rem' : '0.875rem'}}>
                      ⏰ Time expired! Auto-cancelling payment... <br/>
                      <span className="spinner-border spinner-border-sm ms-2"></span>
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10002}}>
          <div className="modal-dialog modal-dialog-centered mx-2 mx-md-auto">
            <div className="modal-content" style={{borderRadius: '15px', border: '2px solid #007bff'}}>
              <div className="modal-header border-0 pb-0 justify-content-center position-relative">
                {/* <h5 className="modal-title fw-bold text-dark">Confirm Collection</h5>*/}
                <h5 className="text-dark">
                  Do you want to proceed with <strong>
                    {confirmationMode === 'Cash Collection' ? 'Cash Collection' : 
                     confirmationMode === 'Payment Link' ? 'UPI/Payment Link' : 
                     'Cheque/Demand Draft'}
                  </strong>?
                </h5>
                <button 
                  type="button" 
                  className="btn-close position-absolute" 
                  onClick={handleConfirmationCancel}
                  aria-label="Close"
                  style={{
                    right: '0.5rem',
                    top: '0rem',
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: '#000',
                    opacity: 0.5,
                    cursor: 'pointer',
                    zIndex: 1
                  }}
                >
                  ×
                </button>
              </div>
              <div className="modal-body text-center ">
                {/* Incentive Warning Message */}
                {getIncentiveWarningStatus() && (
                  <LocalisedAlert message={getLocalisedMessages(items.HH_ID)}  variant='warning' />
                  // <div className="alert alert-warning mb-3 py-2 px-3 text-start" style={{fontSize: '0.85rem', borderLeft: '4px solid #ffc107', backgroundColor: '#fff9e6', color: '#856404'}}>
                  //   <strong>⚠️ Incentive Warning:</strong> Money is being allocated to <strong>Charges</strong> instead of fully settling an <strong>EMI installment</strong>. Without these charges, you could have cleared a full installment, which may affect your <strong>incentive calculation</strong>.
                  // </div>
                )}


                {/* Allocation Summary in Confirmation Modal */}
                <div className="allocation-summary bg-light p-3 rounded mt-2 mb-4 text-start" style={{fontSize: '0.9rem', border: '1px solid #e9ecef'}}>
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">Total Amount:</span>
                    <span className="fw-bold " style={{fontSize: '1.1rem'}}>{formatCurrency(totalCollection)}</span>
                  </div>
                  <hr className="my-2" />
                  
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">Overdue:</span>
                    <span className="fw-bold">{formatCurrency(odCollection)}</span>
                  </div>
                  
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted">Current Demand:</span>
                    <span className="fw-bold">{formatCurrency(currentCollection)}</span>
                  </div>
                  
                  {(() => {
                    const isImpacted = getIncentiveWarningStatus();
                    
                    return (
                      <>
                        <div className="d-flex justify-content-between mb-1">
                          <span className={`text-muted ${isImpacted ? "text-danger fw-bold" : ""}`}>Charges:</span>
                          <span className={`fw-bold ${isImpacted ? "text-danger" : ""}`}>{formatCurrency(chargeCollection)}</span>
                        </div>
                        
                        <div className="d-flex justify-content-between mb-1">
                          <span className={`text-muted ${isImpacted && (advanceEmiCollection < remainingDemand.nextEmi || remainingDemand.nextEmi === 0) ? "text-danger fw-bold" : ""}`}>Next EMI:</span>
                          <span className={`fw-bold ${isImpacted && (advanceEmiCollection < remainingDemand.nextEmi || remainingDemand.nextEmi === 0) ? "text-danger" : ""}`}>{formatCurrency(advanceEmiCollection)}</span>
                        </div>
                        
                        {prepaymentCollection > 0 && (
                          <div className="d-flex justify-content-between mb-1">
                            <span className={`text-muted ${isImpacted && prepaymentCollection % standardEmi > 0.1 ? "text-danger fw-bold" : ""}`}>Prepayment:</span>
                            <span className={`fw-bold ${isImpacted && prepaymentCollection % standardEmi > 0.1 ? "text-danger" : ""}`}>{formatCurrency(prepaymentCollection)}</span>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {(foreclosureCollection > 0 || foreclosureCharges > 0) && (
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted">Foreclosure:</span>
                      <span className="fw-bold">{formatCurrency(foreclosureCollection + foreclosureCharges)}</span>
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-center" style={{gap: '20px'}}>
                  <button 
                    type="button" 
                    className="btn btn-secondary px-5 py-2"
                    onClick={handleConfirmationCancel}
                    style={{borderRadius: '8px'}}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className={`btn px-5 py-2 ${isOkDisabled ? 'btn-light text-muted' : 'btn-primary'}`}
                    onClick={handleConfirmedSubmit}
                    style={{borderRadius: '8px'}}
                    disabled={isOkDisabled}
                  >
                    {isOkDisabled ? `OK (${timerCount}s)` : 'OK'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            style: {
              background: '#4caf50',
            },
          },
          error: {
            style: {
              background: '#f44336',
            },
          },
        }}
      />
    </>
  );
}

const messages = {
    "MP": {
        "lang": "हिन्दी",
        "format": "Amount EMI पूरी तरह जमा होने की बजाय charges में adjust हो रहा है, जिससे अगले महीने की EMI अधूरी रहेगी। सुनिश्चित करें कि EMI बकाया charges के साथ ही जमा हो। अगर customer charges देने के लिए तैयार नहीं है, तो EMI पूरी तरह जमा करने के लिए charges zero  करें."
    },
    "RJ": {
        "lang": "हिन्दी",
        "format": "Amount EMI पूरी तरह जमा होने की बजाय charges में adjust हो रहा है, जिससे अगले महीने की EMI अधूरी रहेगी। सुनिश्चित करें कि EMI बकाया charges के साथ ही जमा हो। अगर customer charges देने के लिए तैयार नहीं है, तो EMI पूरी तरह जमा करने के लिए charges zero  करें."
    },
    "GJ": {
        "lang": "ગુજરાતી",
        "format": "રકમ ચાર્જિસ તરફ ફાળવાઈ રહી છે, જેના કારણે EMI સંપૂર્ણ ચૂકવાતી નથી અને આગામી મહિનાની EMI આંશિક બાકી રહે છે. ખાતરી કરો કે EMI સાથે લાગુ પડતા તમામ ચાર્જિસ પણ વસૂલવામાં આવે. જો ગ્રાહક ચાર્જિસ ચૂકવવા તૈયાર ન હોય, તો EMI સંપૂર્ણ સમાપ્ત કરવા માટે ચાર્જિસને શૂન્ય તરીકે નોંધો."
    },
    "KA": {
        "lang": "ಕನ್ನಡ",
        "format": "ಮೊತ್ತವು charges ಕಡೆಗೆ Allocate ಆಗುತ್ತಿದೆ, EMI ಅನ್ನು ಸಂಪೂರ್ಣವಾಗಿ settle ಮಾಡುವುದಕ್ಕೆ ಬದಲಾಗಿ, ಇದರಿಂದ ಮುಂದಿನ ತಿಂಗಳ EMI ಭಾಗಶಃ ಬಾಕಿ ಉಳಿಯುತ್ತದೆ. EMI ಜೊತೆ ಅನ್ವಯಿಸುವ charges ಕೂಡ ಸಂಗ್ರಹವಾಗುವಂತೆ ಖಚಿತಪಡಿಸಿಕೊಳ್ಳಿ. ಗ್ರಾಹಕರು charges ಪಾವತಿಸಲು ಸಿದ್ಧರಿಲ್ಲದಿದ್ದರೆ, EMI ಸಂಪೂರ್ಣವಾಗಿ settle ಆಗಲು charges ಅನ್ನು zero ಎಂದು mark ಮಾಡಿ"
    },
    "OD": {
        "lang": "ଓଡ଼ିଆ",
        "format": "ରାଶି ସମ୍ପୂର୍ଣ୍ଣ EMI ପୁରଣ କରିବା ପରିବର୍ତ୍ତେ ଚାର୍ଜ୍‌ସ (charges) ପାଇଁ ବ୍ୟବହୃତ ହେଉଛି, ଯାହା ଫଳରେ ପରବର୍ତ୍ତୀ ମାସର EMI ଅଂଶିକ ଅବଶେଷ ରହିଯାଏ। ନିଶ୍ଚିତ କରନ୍ତୁ ଯେ EMI ସହିତ ଲାଗୁଥିବା ଚାର୍ଜ୍‌ସ ମଧ୍ୟ ସଂଗ୍ରହ କରାଯାଉଛି। ଯଦି ଗ୍ରାହକ ଚାର୍ଜ୍‌ସ ଦେବାକୁ ଇଚ୍ଛୁକ ନୁହନ୍ତି, ତେବେ EMI ପୂରା ଭାବେ ସମାପ୍ତ କରିବା ପାଇଁ ଚାର୍ଜ୍‌ସ‌କୁ ଶୂନ୍ୟ (zero) କରନ୍ତୁ।"
    },
    "DEFAULT": {
        "lang": "English",
        "format": "Amount is getting allocated towards charges instead of fully settling the EMI, which leave the next month EMI partially unpaid. Ensure EMI is collected along with applicable charges. If the customer is not willing to pay the charges, mark the charges as zero to fully settle the EMI."
    }
}

const getLocalisedMessages = (household_number) => {
  // Extract the first two characters (State Code)
  const stateCode = household_number.substring(0, 2);

  // Retrieve the state-specific data or fall back to DEFAULT if the code is missing
  const stateData = messages[stateCode] || messages["DEFAULT"];
  const defaultData = messages["DEFAULT"];

  // Construct the result object
  const result = {
      [defaultData.lang]: defaultData.format,
      [stateData.lang]: stateData.format
  };

  return result;
};
